import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, useCallback } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import cytoscapePopper from "cytoscape-popper";
import { createPopper } from "@popperjs/core";
import { ZoomIn, ZoomOut, ArrowsFullscreen, Download, Diagram2, Square, Pencil, Trash, Link45deg, X, LayoutSidebarReverse, Eye, EyeSlash, Search, Lock, Archive, Buildings } from "react-bootstrap-icons";
import LoadingSpinner from "../../../pages/dashboard/LoadingSpinner";
import { loadLatestVersion, loadVersion } from '../../../utils/canvasApi';
import { hydrateCanvasFromSnapshot } from '../../../utils/canvasSerializer';
import { RULE_TYPES, ruleColor, RULE_TYPE_KEYS } from '../../../utils/ruleConfig';
import { useCanvasAutosave } from '../../../hooks/useCanvasAutosave';
import Graph from 'graphology';
import louvain from 'graphology-communities-louvain';

// Register extensions once at module level
cytoscape.use(cytoscapePopper(createPopper));

// ── constants ─────────────────────────────────────────────────────────────────

// Sentinel community ID for nodes that could not be placed into any qualifying
// community by the merge algorithm (no edges AND no graph path to a large group).
// Louvain always produces non-negative integers, so -1 is safe to use here.
const UNCATEGORIZED_COMM = -1;

const SD_BREAKPOINTS = { yellow: 20, orange: 35, red: 50 };
const COLLAPSED_W = 220;
const COLLAPSED_H = 52;

const GRADIENT_STOPS = [
  { t: 0.0, rgb: [40, 167, 69] },
  { t: 0.33, rgb: [255, 193, 7] },
  { t: 0.66, rgb: [253, 126, 20] },
  { t: 1.0, rgb: [220, 53, 69] },
];

const LAYOUTS = [
  { name: "package-groups", label: "Package Groups" },
  { name: "community-groups", label: "Community Groups" },
  { name: "cose", label: "Force-directed" },
  { name: "breadthfirst", label: "Hierarchical" },
  { name: "circle", label: "Circle" },
  { name: "grid", label: "Grid" },
  { name: "concentric", label: "Concentric" },
];

const LAYER_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899",
];


const COMMUNITY_COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
  "#aec7e8", "#ffbb78", "#98df8a", "#ff9896", "#c5b0d5",
  "#c49c94", "#f7b6d2", "#c7c7c7", "#dbdb8d", "#9edae5",
  "#393b79", "#637939", "#8c6d31", "#843c39", "#7b4173",
  "#3182bd", "#e6550d", "#31a354", "#756bb1", "#636363"
];

// 8-handle descriptors: position relative to bounding box
// handle half-size = 7 (14px / 2)
const H = 7;
const RESIZE_HANDLES = [
  { dir: "nw", cursor: "nw-resize", getPos: b => ({ left: b.x1 - H, top: b.y1 - H }) },
  { dir: "n", cursor: "n-resize", getPos: b => ({ left: (b.x1 + b.x2) / 2 - H, top: b.y1 - H }) },
  { dir: "ne", cursor: "ne-resize", getPos: b => ({ left: b.x2 - H, top: b.y1 - H }) },
  { dir: "e", cursor: "e-resize", getPos: b => ({ left: b.x2 - H, top: (b.y1 + b.y2) / 2 - H }) },
  { dir: "se", cursor: "se-resize", getPos: b => ({ left: b.x2 - H, top: b.y2 - H }) },
  { dir: "s", cursor: "s-resize", getPos: b => ({ left: (b.x1 + b.x2) / 2 - H, top: b.y2 - H }) },
  { dir: "sw", cursor: "sw-resize", getPos: b => ({ left: b.x1 - H, top: b.y2 - H }) },
  { dir: "w", cursor: "w-resize", getPos: b => ({ left: b.x1 - H, top: (b.y1 + b.y2) / 2 - H }) },
];

const HANDLE_CURSORS = { n: "n-resize", ne: "ne-resize", e: "e-resize", se: "se-resize", s: "s-resize", sw: "sw-resize", w: "w-resize", nw: "nw-resize" };

// ── colour helpers ────────────────────────────────────────────────────────────

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const lerp = (a, b, t) => a + (b - a) * t;

function rgbToHex([r, g, b]) {
  const h = x => Math.round(x).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
function sdToGradientT(sd) {
  const v = Math.max(0, Number(sd) || 0);
  const { yellow, orange, red } = SD_BREAKPOINTS;
  if (v <= yellow) return lerp(0.0, 0.33, clamp(v / (yellow || 1), 0, 1));
  if (v <= orange) return lerp(0.33, 0.66, clamp((v - yellow) / ((orange - yellow) || 1), 0, 1));
  return lerp(0.66, 1.0, clamp((v - orange) / ((red - orange) || 1), 0, 1));
}
function gradientColor(t) {
  const tt = clamp(t, 0, 1);
  for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
    const a = GRADIENT_STOPS[i], b = GRADIENT_STOPS[i + 1];
    if (tt >= a.t && tt <= b.t) {
      const lt = (tt - a.t) / (b.t - a.t || 1);
      return rgbToHex([lerp(a.rgb[0], b.rgb[0], lt), lerp(a.rgb[1], b.rgb[1], lt), lerp(a.rgb[2], b.rgb[2], lt)]);
    }
  }
  return rgbToHex(GRADIENT_STOPS[GRADIENT_STOPS.length - 1].rgb);
}
function normalizeRect(s, e) {
  return { x: Math.min(s.x, e.x), y: Math.min(s.y, e.y), width: Math.abs(e.x - s.x), height: Math.abs(e.y - s.y) };
}

// ── community-groups layout ───────────────────────────────────────────────────

function runCommunityLayout(cy, communityMap) {
  const groups = {};
  cy.nodes("[!isLayer]").forEach(n => {
    if (n.parent().length) return; // skip nodes already assigned to a layer
    const comm = communityMap[n.id()];
    if (comm === undefined) return;
    if (!groups[comm]) groups[comm] = [];
    groups[comm].push(n);
  });

  const keys = Object.keys(groups).map(Number).sort((a, b) => b - a);
  const gridCols = Math.ceil(Math.sqrt(keys.length));
  const NODE_D = 19;
  const NS = 90;
  const CLUSTER_PAD = 180;

  const meta = keys.map(comm => {
    const n = groups[comm].length;
    const perRow = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / perRow);
    return { perRow, rows, w: (perRow - 1) * NS + NODE_D, h: (rows - 1) * NS + NODE_D };
  });

  const numRows = Math.ceil(keys.length / gridCols);
  const colWidths = Array.from({ length: gridCols }, (_, c) =>
    Math.max(...keys.map((_, i) => i % gridCols === c ? meta[i].w : 0)) + CLUSTER_PAD
  );
  const rowHeights = Array.from({ length: numRows }, (_, r) =>
    Math.max(...keys.map((_, i) => Math.floor(i / gridCols) === r ? meta[i].h : 0)) + CLUSTER_PAD
  );

  const colX = colWidths.reduce((acc, w, i) => [...acc, (acc[i - 1] ?? 0) + (i > 0 ? colWidths[i - 1] : 0)], []);
  const rowY = rowHeights.reduce((acc, h, i) => [...acc, (acc[i - 1] ?? 0) + (i > 0 ? rowHeights[i - 1] : 0)], []);

  cy.batch(() => {
    keys.forEach((comm, gi) => {
      const col = gi % gridCols;
      const row = Math.floor(gi / gridCols);
      const { perRow, w, h } = meta[gi];
      // Center the cluster within its grid cell so small and large clusters
      // have equal whitespace on each side regardless of size difference.
      const offsetX = (colWidths[col] - w) / 2;
      const offsetY = (rowHeights[row] - h) / 2;
      const gx = colX[col] + offsetX;
      const gy = rowY[row] + offsetY;
      groups[comm].forEach((node, ni) => {
        node.position({
          x: gx + (ni % perRow) * NS,
          y: gy + Math.floor(ni / perRow) * NS,
        });
      });
    });
  });

  cy.animate({ fit: { eles: cy.elements(), padding: 60 } }, { duration: 400 });
}

// ── package-group layout ──────────────────────────────────────────────────────
// Groups free (unparented) nodes by the second-to-last package segment,
// then arranges groups in a grid so the graph is visually clustered.

// Find the longest shared prefix that ends on a separator (. or /)
function sharedPackagePrefix(ids) {
  if (ids.length === 0) return "";
  let prefix = ids[0];
  for (let i = 1; i < ids.length; i++) {
    while (prefix && !ids[i].startsWith(prefix)) prefix = prefix.slice(0, -1);
    if (!prefix) return "";
  }
  // Trim to the last separator so we don't cut mid-token
  const last = Math.max(prefix.lastIndexOf("."), prefix.lastIndexOf("/"));
  return last >= 0 ? prefix.slice(0, last + 1) : "";
}

// Convert a glob pattern (user input) to a RegExp.
// Escapes regex specials except *, which becomes .*
// Test is run against the full node ID so "service.*" matches any ID containing "service."
function globToRegex(pattern) {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(escaped, "i");
}

function runPackageGroupLayout(cy) {
  const freeNodes = cy.nodes("[!isLayer]").filter(n => !n.parent().length);
  if (freeNodes.length === 0) return;

  const allIds = freeNodes.map(n => n.id());
  const prefix = sharedPackagePrefix(allIds);

  const groups = {};
  freeNodes.forEach(n => {
    const stripped = n.id().slice(prefix.length);
    const sep = Math.min(
      ...[stripped.indexOf("."), stripped.indexOf("/")].filter(i => i >= 0),
      stripped.length
    );
    const pkg = stripped.slice(0, sep) || "other";
    if (!groups[pkg]) groups[pkg] = [];
    groups[pkg].push(n);
  });

  const keys = Object.keys(groups).sort();
  const gridCols = Math.ceil(Math.sqrt(keys.length));
  const NODE_D = 19;  // must match stylesheet node width/height
  const NS = 82;  // center-to-center: covers node (19px) + label (72px max-width) + 10px breathing room
  const CLUSTER_PAD = 150; // minimum whitespace between any two clusters

  // Per-cluster grid dimensions and bounding-box size
  const meta = keys.map(pkg => {
    const n = groups[pkg].length;
    const perRow = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / perRow);
    return { perRow, rows, w: (perRow - 1) * NS + NODE_D, h: (rows - 1) * NS + NODE_D };
  });

  // Column widths and row heights sized to their tallest/widest cluster
  const numRows = Math.ceil(keys.length / gridCols);
  const colWidths = Array.from({ length: gridCols }, (_, c) =>
    Math.max(...keys.map((_, i) => i % gridCols === c ? meta[i].w : 0)) + CLUSTER_PAD
  );
  const rowHeights = Array.from({ length: numRows }, (_, r) =>
    Math.max(...keys.map((_, i) => Math.floor(i / gridCols) === r ? meta[i].h : 0)) + CLUSTER_PAD
  );

  // Cumulative offsets so each cluster starts after the previous one
  const colX = colWidths.reduce((acc, w, i) => [...acc, (acc[i - 1] ?? 0) + (i > 0 ? colWidths[i - 1] : 0)], []);
  const rowY = rowHeights.reduce((acc, h, i) => [...acc, (acc[i - 1] ?? 0) + (i > 0 ? rowHeights[i - 1] : 0)], []);

  cy.batch(() => {
    keys.forEach((pkg, gi) => {
      const col = gi % gridCols;
      const row = Math.floor(gi / gridCols);
      const { perRow, w, h } = meta[gi];
      // Center the cluster within its grid cell so small and large clusters
      // have equal whitespace on each side regardless of size difference.
      const offsetX = (colWidths[col] - w) / 2;
      const offsetY = (rowHeights[row] - h) / 2;
      const gx = colX[col] + offsetX;
      const gy = rowY[row] + offsetY;
      groups[pkg].forEach((node, ni) => {
        node.position({
          x: gx + (ni % perRow) * NS,
          y: gy + Math.floor(ni / perRow) * NS,
        });
      });
    });
  });

  cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 350 });
}

// ── toolbar primitives ────────────────────────────────────────────────────────

const ToolBtn = ({ title, onClick, active, disabled, children }) => (
  <button title={title} onClick={disabled ? undefined : onClick} disabled={disabled}
    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, border: "none", borderRadius: 6, background: active ? "#f0fdfa" : "transparent", color: active ? "#0d9488" : disabled ? "#cbd5e1" : "#475569", cursor: disabled ? "not-allowed" : "pointer", transition: "background 0.15s, color 0.15s", flexShrink: 0, opacity: disabled ? 0.5 : 1 }}
    onMouseEnter={e => { if (!active && !disabled) { e.currentTarget.style.background = "#f0fdfa"; e.currentTarget.style.color = "#0d9488"; } }}
    onMouseLeave={e => { e.currentTarget.style.background = active ? "#f0fdfa" : "transparent"; e.currentTarget.style.color = active ? "#0d9488" : disabled ? "#cbd5e1" : "#475569"; }}
  >{children}</button>
);
const ToolDivider = () => <div style={{ height: 1, background: "#e2e8f0", margin: "2px 0" }} />;

// ── community merge helper ────────────────────────────────────────────────────
// Takes the raw Louvain map and merges groups smaller than minNodes into their
// most-connected neighbour (by total edge weight). Returns a new map — never
// mutates the original so the Community Groups panel is unaffected.
function mergeCommunities(rawMap, G, minNodes) {
  if (!G || minNodes <= 1) return rawMap;

  const map = { ...rawMap };

  const buildGroups = () => {
    const g = {};
    for (const [nodeId, comm] of Object.entries(map)) {
      if (!g[comm]) g[comm] = [];
      g[comm].push(nodeId);
    }
    return g;
  };

  let changed = true;
  while (changed) {
    changed = false;
    const groups = buildGroups();
    const small = Object.entries(groups)
      .filter(([, nodes]) => nodes.length < minNodes)
      .sort((a, b) => a[1].length - b[1].length); // smallest first

    if (small.length === 0) break;

    for (const [commId, nodes] of small) {
      const score = {};
      for (const nodeId of nodes) {
        if (!G.hasNode(nodeId)) continue;
        G.forEachEdge(nodeId, (edge, attrs, source, target) => {
          const neighbour = source === nodeId ? target : source;
          const nComm = map[neighbour];
          if (nComm === undefined || nComm === Number(commId)) return;
          score[nComm] = (score[nComm] || 0) + (attrs.weight ?? 1);
        });
      }

      const best = Object.entries(score).sort((a, b) => b[1] - a[1])[0];

      if (!best) {
        // No direct edges to any other community — BFS outward through the graph
        // to find the nearest reachable qualifying community (by hop count).
        const currentGroups = buildGroups();
        const visited = new Set(nodes);
        const queue = [...nodes];
        let found = null;

        while (queue.length) {
          const curr = queue.shift();
          if (!G.hasNode(curr)) continue;
          const neighbours = [];
          G.forEachNeighbor(curr, n => neighbours.push(n));
          for (const neighbour of neighbours) {
            if (visited.has(neighbour)) continue;
            visited.add(neighbour);
            const nComm = map[neighbour];
            if (nComm === undefined || nComm === Number(commId)) {
              queue.push(neighbour);
              continue;
            }
            // Only merge into a community that already meets the threshold
            if ((currentGroups[nComm]?.length ?? 0) >= minNodes) {
              found = nComm;
              break;
            }
            queue.push(neighbour);
          }
          if (found !== null) break;
        }

        if (found === null) continue; // genuinely unreachable via edges — handled by catch-all below

        nodes.forEach(nodeId => { map[nodeId] = found; });
        changed = true;
        break;
      }

      const target = Number(best[0]);
      nodes.forEach(nodeId => { map[nodeId] = target; });
      changed = true;
      break; // restart with fresh groups after each merge
    }
  }

  // ── Mark remaining below-threshold nodes as UNCATEGORIZED_COMM (-1) ─────────
  // These are nodes whose small community had no edge path to any large community
  // (e.g. truly isolated classes with no imports at all). Rather than silently
  // dumping them into an unrelated large group, we flag them so the caller can
  // surface them in a dedicated "Uncategorized" suggested layer where the user
  // can review and reassign them manually.
  const finalGroups = buildGroups();
  for (const [, ns] of Object.entries(finalGroups)) {
    if (ns.length < minNodes) {
      ns.forEach(nodeId => { map[nodeId] = UNCATEGORIZED_COMM; });
    }
  }

  return map;
}

// ── component ─────────────────────────────────────────────────────────────────

export default function ArchitectureLayerGraph({
  graph, title, onNetworkReady,
  activeNodeIds = null, centerNodeId = null,
  resetViewSignal = 0, headerActions,
  projectId = null,
  definitionId = null,
  versionNumber = null,
  versionStatus = null,
  onVersionLoaded = null,
  onStageChange = null,
  onPublish = null,
  wizardStep = undefined,
}) {
  const cyRef = useRef(null);
  const containerRef = useRef(null);
  const nameInputRef = useRef(null);
  const collapsedNodePositions = useRef({}); // { layerId: { nodeId: {x,y} } }
  const bannerMetaRef = useRef({ consumed: 0, segStart: 0 }); // tracks elapsed ms for hover-pause
  const popupInputRef = useRef(null);
  const nodeParentMapRef = useRef({});
  const grabbedNodeIdRef = useRef(null); // tracks which node the user directly grabbed
  // refs so Cytoscape closure-captured handlers can read live connect state
  const connectModeRef = useRef(false);
  const connectSourceRef = useRef(null);
  const layerEdgesRef = useRef([]);   // live snapshot for closure use
  const hasHydratedRef = useRef(false); // prevents double-hydration in React StrictMode
  const pendingCanvasRestoreRef = useRef(null);
  const isHydratingRef = useRef(false); // blocks autosave during the async hydration window
  const scheduleSaveRef = useRef(null);  // ref mirror so cy drag handlers call the latest scheduleSave
  const communityMapRef = useRef({});   // { nodeId: communityNumber } — raw Louvain result, never mutated
  const communityGraphRef = useRef(null); // graphology Graph instance — kept for merge step in suggestLayersFromCommunities
  const mergedCommunityMapRef = useRef({});   // merged map (respects panelMinNodes) — updated on each threshold change
  const activeLayoutRef = useRef('community-groups'); // kept in sync with activeLayout state for non-reactive callbacks
  const workflowStageRef = useRef('suggest');          // live mirror of workflowStage for use inside Cytoscape handlers
  const skipSuggestLockRef = useRef(false);              // hydrating a draft that already has layers: ignore the lock the initial community compute would trigger
  const selectFromCanvasRef = useRef(null);               // node ID to auto-select after a canvas tap changes selectedComm
  const selectedCommRef = useRef(null);               // live mirror of selectedComm for use inside Cytoscape handlers

  const [isLoading, setIsLoading] = useState(true);
  const [communityMapVersion, setCommunityMapVersion] = useState(0); // bumped after communities are computed to re-trigger elements useMemo
  const tooltipRef = useRef(null); // { instance: PopperInstance, el: HTMLElement }
  const [zoomLevel, setZoomLevel] = useState(200);
  const [activeLayout, setActiveLayout] = useState("community-groups");
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);

  const [layerNodes, setLayerNodes] = useState([]);

  // suggested layers — provisional compounds not yet confirmed by the user
  const [suggestedLayerIds, setSuggestedLayerIds] = useState(new Set());
  const suggestedLayerIdsRef = useRef(new Set()); // mutable mirror for use inside Cytoscape callbacks

  const [panelMinNodes, setPanelMinNodes] = useState(10);
  const [minSizeInput, setMinSizeInput] = useState('10'); // raw string for the stepper text input
  const [appliedMinNodes, setAppliedMinNodes] = useState(10); // only updates on Generate Clusters click
  const [showNodeEdges, setShowNodeEdges] = useState(false); // global toggle for class-node edges (off by default)
  const [layerSplitSuggestions, setLayerSplitSuggestions] = useState([]); // layers whose nodes span >1 community at current threshold
  const [conflictsCollapsed, setConflictsCollapsed] = useState(false); // collapse layer conflicts section
  const [hoveredComm, setHoveredComm] = useState(null);  // hover highlight in group list
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(true); // toolbar toggle
  const [dismissedBanners, setDismissedBanners] = useState(new Set()); // stages whose guidance toast has been dismissed
  const [bannerHovered, setBannerHovered] = useState(false);    // pauses auto-dismiss timer while user reads
  // Gate the suggest→arrange transition behind an explicit user action
  const [suggestStageLocked, setSuggestStageLocked] = useState(false);
  // Set to true when the user explicitly clicks Continue — bypasses the layerNodes.length === 0 guard
  const [arrangeUnlocked, setArrangeUnlocked] = useState(false);

  // draw
  const [drawMode, setDrawMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawRect, setDrawRect] = useState(null);
  const [pendingRect, setPendingRect] = useState(null);
  const [pendingName, setPendingName] = useState("");

  // selected layer + resize handles
  const [selectedLayer, setSelectedLayer] = useState(null); // { layerId, color }
  const [handleBB, setHandleBB] = useState(null); // rendered BB in px
  const [resizingLayer, setResizingLayer] = useState(null); // { layerId, handle, startGraphBB, startChildData, startMouse }

  // rename popup
  const [popupEditing, setPopupEditing] = useState(false);
  const [popupName, setPopupName] = useState("");

  // add-nodes-by-pattern
  const [addNodesMode, setAddNodesMode] = useState(false);
  const [addNodesPattern, setAddNodesPattern] = useState("");
  const [addNodesMatchCount, setAddNodesMatchCount] = useState(0);
  const addNodesInputRef = useRef(null);

  // remove-nodes-by-pattern
  const [removeNodesMode, setRemoveNodesMode] = useState(false);
  const [removeNodesPattern, setRemoveNodesPattern] = useState("");
  const [removeNodesMatchCount, setRemoveNodesMatchCount] = useState(0);
  const removeNodesInputRef = useRef(null);

  // node-inside-layer popup
  const [nodePopup, setNodePopup] = useState(null); // { nodeId, label, pos }

  // connect-layers mode
  const [connectMode, setConnectMode] = useState(false);
  const [connectSource, setConnectSource] = useState(null); // { layerId, color, cx, cy } rendered px
  const [connectSourceBB, setConnectSourceBB] = useState(null); // rendered BB of source layer
  const [connectMouse, setConnectMouse] = useState(null); // { x, y } container-relative px
  const [layerEdges, setLayerEdges] = useState([]); // { id, source, target, color, rule }
  const [selectedLayerEdge, setSelectedLayerEdge] = useState(null); // { edgeId, color, rule, pos }
  const [pendingConnection, setPendingConnection] = useState(null); // { sourceLayerId, targetLayerId, sourceName, targetName, sourceColor, targetColor }
  const [connectAlert, setConnectAlert] = useState(null); // { x, y, msg? } — "already connected" toast
  const [createLayerPrompt, setCreateLayerPrompt] = useState(null); // { commNum, map, inputValue } — name-before-create dialog
  // rule-form state for the connection modal
  const [ruleFormName, setRuleFormName] = useState('');
  const [ruleFormEnforcement, setRuleFormEnforcement] = useState('denied');
  const [connectedLayerBBs, setConnectedLayerBBs] = useState({}); // layerId → renderedBB

  // community groups inspection
  const [selectedComm, setSelectedComm] = useState(null);
  const [selectedNodeInPanel, setSelectedNodeInPanel] = useState(null);
  const [highlightEdgesActive, setHighlightEdgesActive] = useState(false);
  const [showIntraGroupEdges, setShowIntraGroupEdges] = useState(false);
  const [showCrossGroupEdges, setShowCrossGroupEdges] = useState(false);
  const [nodeConnections, setNodeConnections] = useState(null); // { outgoing, incoming }
  const [crossGroupData, setCrossGroupData] = useState(null); // Map<extGrpNum, conn[]>
  const [crossGroupExpandedGrp, setCrossGroupExpandedGrp] = useState(null);
  const [commHull, setCommHull] = useState(null); // { x, y, w, h, color, label } in container px

  // navigate panel
  const [showNavigatePanel, setShowNavigatePanel] = useState(false);
  const [navigateTab, setNavigateTab] = useState('nodes');
  const [nodeQuery, setNodeQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');

  // layer collapse
  const [collapsedLayers, setCollapsedLayers] = useState(new Set());

  // signals that Cytoscape has mounted; hydration useEffect waits for this
  const [cyReady, setCyReady] = useState(false);

  // Keep mutable ref in sync so Cytoscape callbacks can read current suggested IDs
  useEffect(() => { suggestedLayerIdsRef.current = suggestedLayerIds; }, [suggestedLayerIds]);

  const { saveStatus, scheduleSave } = useCanvasAutosave({
    projectId, definitionId, versionNumber, versionStatus,
    cyRef, layerNodes, layerEdges, collapsedLayers,
    nodeParentMapRef: nodeParentMapRef.current,
    suggestedLayerIds,
    collapsedNodePositions: collapsedNodePositions.current,
  });


  // ── Layers Explorer panel (published / read-only view) ────────────────────
  const [layerPanelSelectedLayer, setLayerPanelSelectedLayer] = useState(null); // layerId | null
  const [layerPanelSelectedNode, setLayerPanelSelectedNode] = useState(null); // nodeId  | null
  const [layerPanelHighlightEdges, setLayerPanelHighlightEdges] = useState(false);
  const [layerPanelIntraEdges, setLayerPanelIntraEdges] = useState(false);
  const [layerPanelCrossEdges, setLayerPanelCrossEdges] = useState(false);
  // ── Layers Explorer: node connections + cross-layer data ─────────────────
  const [layerNodeConnections, setLayerNodeConnections] = useState(null); // { outgoing, incoming }
  const [crossLayerData, setCrossLayerData] = useState(null); // Map<layerId, {layerName,layerColor,conns[]}>
  const [crossLayerExpandedGrp, setCrossLayerExpandedGrp] = useState(null);

  const rawNodes = useMemo(() => (Array.isArray(graph?.nodes) ? graph.nodes : []), [graph]);
  const rawEdges = useMemo(() => (Array.isArray(graph?.edges) ? graph.edges : []), [graph]);
  const hasGraph = rawNodes.length > 0;

  // Derives the user's current position in the architecture-definition workflow.
  // Used to drive the stage breadcrumb and contextual guidance banners.
  const workflowStage = useMemo(() => {
    // Published/archived versions are always in the read-only arrange view — never show the Suggest stage.
    if (wizardStep || versionStatus !== 'draft') return 'arrange';
    // arrangeUnlocked: user explicitly clicked Continue — advance regardless of layerNodes count,
    // but only if clusters haven't been re-generated since (suggestStageLocked resets this intent).
    if (arrangeUnlocked && !suggestStageLocked) return 'arrange';
    if (suggestedLayerIds.size > 0 || suggestStageLocked || layerNodes.length === 0) return 'suggest';
    return 'arrange';
  }, [wizardStep, versionStatus, arrangeUnlocked, layerNodes.length, suggestedLayerIds.size, suggestStageLocked]);

  // Notify parent whenever the workflow stage transitions
  useEffect(() => { onStageChange?.(workflowStage); }, [workflowStage, onStageChange]);

  // Auto-show suggestions panel in Suggest stage; hide it in the merged Arrange & Define stage
  useEffect(() => {
    if (workflowStage === 'suggest') setShowSuggestionsPanel(true);
    else if (workflowStage === 'arrange') setShowSuggestionsPanel(false);
  }, [workflowStage]);

  // Auto-exit connect mode when leaving the arrange stage (e.g. user removes all layers)
  useEffect(() => {
    if (workflowStage !== 'arrange') {
      setConnectMode(false);
      setConnectSource(null);
      setConnectMouse(null);
      setConnectSourceBB(null);
      setPendingConnection(null);
    }
  }, [workflowStage]);

  // Clear Layers Explorer panel state when leaving arrange stage in draft mode
  useEffect(() => {
    if (workflowStage !== 'arrange' && versionStatus === 'draft') {
      setLayerPanelSelectedLayer(null);
      setLayerPanelSelectedNode(null);
      setLayerPanelHighlightEdges(false);
      setLayerPanelIntraEdges(false);
      setLayerPanelCrossEdges(false);
      setLayerNodeConnections(null);
      setCrossLayerData(null);
    }
  }, [workflowStage, versionStatus]);

  // Lock the suggest stage whenever clusters are (re-)generated so the user must
  // explicitly click "Continue to Arrange Layers" to advance — regardless of whether
  // they used "Suggest All" or the individual "+ Layer" button on each group.
  useEffect(() => {
    if (communityMapVersion > 0) {
      // The initial community compute during hydration bumps this too. For a draft that
      // already has layers (e.g. a fork of a published version) we don't want that to
      // force the Suggest stage back on — consume the one-shot skip flag and stay put.
      if (skipSuggestLockRef.current) {
        skipSuggestLockRef.current = false;
      } else {
        setSuggestStageLocked(true);
        setArrangeUnlocked(false); // re-generated clusters send the user back to suggest
      }
    }
  }, [communityMapVersion]);

  // Reset suggest lock when the user switches definition or version (fresh context)
  useEffect(() => {
    setSuggestStageLocked(false);
    setArrangeUnlocked(false);
  }, [definitionId, versionNumber]);

  // When a version transitions to non-draft (e.g. just published), immediately exit
  // ALL edit/interaction modes so the UI becomes fully read-only without a page reload.
  useEffect(() => {
    if (versionStatus !== 'draft') {
      setDrawMode(false);
      setConnectMode(false);
      setConnectSource(null);
      setConnectMouse(null);
      setSelectedLayerEdge(null);
      setPendingConnection(null);
      setPopupEditing(false);       // cancel any in-progress rename
      setAddNodesMode(false);       // cancel add-nodes-by-pattern
      setRemoveNodesMode(false);    // cancel remove-nodes-by-pattern
      setNodePopup(null);           // dismiss "Move out of layer" popup
      setCreateLayerPrompt(null);   // dismiss name-before-create dialog
    }
  }, [versionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prevent layer compound nodes from being dragged in published/archived mode.
  // Runs once Cytoscape is ready and again if versionStatus changes (e.g. just published).
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !cyReady) return;
    cy.nodes("[?isLayer]")[versionStatus !== 'draft' ? 'ungrabify' : 'grabify']();
  }, [cyReady, versionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset elapsed counter and hover state whenever the active stage changes
  useEffect(() => {
    bannerMetaRef.current = { consumed: 0, segStart: Date.now() };
    setBannerHovered(false);
  }, [workflowStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss with hover-pause: accumulate elapsed time while hovered; schedule remaining on un-hover
  useEffect(() => {
    if (bannerHovered) {
      bannerMetaRef.current.consumed += Date.now() - bannerMetaRef.current.segStart;
      return;
    }
    bannerMetaRef.current.segStart = Date.now();
    const remaining = Math.max(300, 7000 - bannerMetaRef.current.consumed);
    const t = setTimeout(() => {
      setDismissedBanners(prev => prev.has(workflowStage) ? prev : new Set([...prev, workflowStage]));
    }, remaining);
    return () => clearTimeout(t);
  }, [bannerHovered, workflowStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merged community map + sorted group list recomputed whenever communities or threshold change.
  const [mergedMapForPanel, mergedGroupEntries] = useMemo(() => {
    const mergedMap = mergeCommunities(communityMapRef.current, communityGraphRef.current, appliedMinNodes);
    const groups = {};
    for (const n of rawNodes) {
      const id = n.id ?? n.key ?? n.name ?? "";
      const comm = mergedMap[id];
      if (comm === undefined) continue;
      if (!groups[comm]) groups[comm] = [];
      groups[comm].push(n);
    }
    const entries = Object.entries(groups)
      .filter(([key, ns]) => Number(key) !== UNCATEGORIZED_COMM && ns.length >= appliedMinNodes)
      .sort((a, b) => b[1].length - a[1].length);
    return [mergedMap, entries];
  }, [rawNodes, communityMapVersion, appliedMinNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync mergedCommunityMapRef on initial community load so the default view
  // (appliedMinNodes=10) is colored correctly before the user clicks Generate.
  useEffect(() => {
    if (communityMapVersion > 0) mergedCommunityMapRef.current = mergedMapForPanel;
  }, [communityMapVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count nodes that couldn't be merged into any qualifying community.
  // Shown as a warning badge in the suggestions panel so the user knows
  // some classes will land in the "Uncategorized" layer (or sit as gray
  // dots on the canvas if they haven't clicked "Suggest All" yet).
  // Recomputes live as panelMinNodes slider changes so the badge gives instant feedback.
  // mergeCommunities is called here with panelMinNodes (not appliedMinNodes) so the user
  // can see how many orphan nodes will result BEFORE clicking Generate Clusters.
  const uncategorizedCount = useMemo(() => {
    if (communityMapVersion === 0 || !communityGraphRef.current) return 0;
    const liveMap = mergeCommunities(communityMapRef.current, communityGraphRef.current, panelMinNodes);
    return Object.values(liveMap).filter(v => v === UNCATEGORIZED_COMM).length;
  }, [panelMinNodes, communityMapVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigate panel filtered results ──────────────────────────────────────────
  const filteredNavNodes = useMemo(() => {
    const q = nodeQuery.trim().toLowerCase();
    if (!q) return [];
    return rawNodes
      .filter(n => {
        const label = (n.label || n.data?.label || n.id || '').toLowerCase();
        return label.includes(q);
      })
      .slice(0, 15);
  }, [rawNodes, nodeQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredNavGroups = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    return mergedGroupEntries
      .filter(([commNum]) => !q || `group ${commNum}`.includes(q))
      .map(([commNum, nodes]) => ({ commNum: Number(commNum), count: nodes.length }))
      .slice(0, 20);
  }, [groupQuery, mergedGroupEntries]);

  const filteredNavLayers = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    return layerNodes
      .filter(l => !q || l.name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [groupQuery, layerNodes]);

  // Tracks which community numbers have already been individually promoted to a layer.
  // Uses the layerId naming convention: suggestion-comm-{N}-{timestamp}
  const convertedCommNums = useMemo(() => {
    const s = new Set();
    layerNodes.forEach(l => {
      const m = l.id.match(/^suggestion-comm-(\d+)-\d+$/);
      if (m) s.add(Number(m[1]));
    });
    return s;
  }, [layerNodes]);

  // Subset of convertedCommNums that are still pending confirmation (still in suggestedLayerIds).
  const suggestedCommNums = useMemo(() => {
    const s = new Set();
    layerNodes.forEach(l => {
      if (!suggestedLayerIds.has(l.id)) return;
      const m = l.id.match(/^suggestion-comm-(\d+)-\d+$/);
      if (m) s.add(Number(m[1]));
    });
    return s;
  }, [layerNodes, suggestedLayerIds]);

  useEffect(() => { if (pendingRect) setTimeout(() => nameInputRef.current?.focus(), 50); }, [pendingRect]);
  useEffect(() => { if (popupEditing) setTimeout(() => popupInputRef.current?.focus(), 30); }, [popupEditing]);
  useEffect(() => { if (addNodesMode) setTimeout(() => addNodesInputRef.current?.focus(), 30); }, [addNodesMode]);
  useEffect(() => { if (removeNodesMode) setTimeout(() => removeNodesInputRef.current?.focus(), 30); }, [removeNodesMode]);

  useEffect(() => {
    if (!addNodesPattern.trim() || !cyRef.current) { setAddNodesMatchCount(0); return; }
    try {
      const regex = globToRegex(addNodesPattern.trim());
      let count = 0;
      cyRef.current.nodes("[!isLayer]").forEach(n => {
        if (!n.parent().length && regex.test(n.id())) count++;
      });
      setAddNodesMatchCount(count);
    } catch { setAddNodesMatchCount(0); }
  }, [addNodesPattern]);

  useEffect(() => {
    if (!removeNodesPattern.trim() || !cyRef.current || !selectedLayer) { setRemoveNodesMatchCount(0); return; }
    try {
      const regex = globToRegex(removeNodesPattern.trim());
      let count = 0;
      cyRef.current.$(`#${selectedLayer.layerId}`).children().forEach(n => {
        if (regex.test(n.id())) count++;
      });
      setRemoveNodesMatchCount(count);
    } catch { setRemoveNodesMatchCount(0); }
  }, [removeNodesPattern, selectedLayer]);

  // highlight / dim nodes when a community groups is selected
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    // When a specific node is highlighted via the Navigate panel, the edge-highlight
    // effect owns node opacity — don't let the community dim override it.
    if (highlightEdgesActive && selectedNodeInPanel) return;
    cy.batch(() => {
      cy.nodes("[!isLayer]").forEach(n => {
        if (activeLayout !== "community-groups" || selectedComm === null) {
          n.style("opacity", 1);
        } else {
          // Use the merged map so nodes absorbed from small communities are
          // correctly attributed to the group the panel is showing.
          const comm = mergedMapForPanel[n.id()];
          n.style("opacity", comm === selectedComm ? 1 : 0.07);
        }
      });
    });
  }, [selectedComm, activeLayout, mergedMapForPanel, highlightEdgesActive, selectedNodeInPanel]);

  // reset community selection when switching away from community-groups layout
  useEffect(() => {
    if (activeLayout !== "community-groups") setSelectedComm(null);
  }, [activeLayout]);

  // reset node/edge selection when the active community changes (including close)
  // If selectFromCanvasRef is set, a canvas tap triggered the change — carry the pending node through instead of resetting.
  useEffect(() => {
    const pending = selectFromCanvasRef.current;
    selectFromCanvasRef.current = null;
    setSelectedNodeInPanel(pending ?? null);
    setHighlightEdgesActive(!!pending);
    setShowIntraGroupEdges(!!pending);
    setShowCrossGroupEdges(false);
    setNodeConnections(null);
  }, [selectedComm]);

  // auto-fit viewport to selected community so its hull always stays on screen
  useEffect(() => {
    if (selectedComm === null || !cyRef.current || !cyReady) return;
    const cy = cyRef.current;
    const commEles = cy.nodes('[!isLayer]').filter(n => mergedMapForPanel[n.id()] === selectedComm);
    if (commEles.length) cy.animate({ fit: { eles: commEles, padding: 80 } }, { duration: 350, easing: 'ease-in-out' });
  }, [selectedComm, cyReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // compute directed connections for the selected node
  useEffect(() => {
    const cy = cyRef.current;
    if (!selectedNodeInPanel || !cy) { setNodeConnections(null); return; }

    const outMap = new Map(); // id → item  (dedup multi-edges)
    const inMap = new Map();

    cy.edges('[!isLayerEdge]').forEach(e => {
      const srcId = e.source().id();
      const tgtId = e.target().id();

      const buildItem = (nodeId) => {
        const n = cy.$(`#${CSS.escape(nodeId)}`);
        const fullLabel = n.data('fullLabel') || nodeId;
        const shortName = fullLabel.includes('.') ? fullLabel.split('.').pop() : fullLabel;
        const groupNum = mergedMapForPanel[nodeId];
        return { id: nodeId, shortName, groupNum, isInGroup: groupNum === selectedComm };
      };

      if (srcId === selectedNodeInPanel && !outMap.has(tgtId)) outMap.set(tgtId, buildItem(tgtId));
      if (tgtId === selectedNodeInPanel && !inMap.has(srcId)) inMap.set(srcId, buildItem(srcId));
    });

    setNodeConnections({
      outgoing: [...outMap.values()],
      incoming: [...inMap.values()],
    });
  }, [selectedNodeInPanel, selectedComm, mergedMapForPanel]);

  // reset edge-filter toggles when the global eye is turned back on
  useEffect(() => {
    if (showNodeEdges) { setShowIntraGroupEdges(false); setShowCrossGroupEdges(false); }
  }, [showNodeEdges]);

  // compute cross-group connection data whenever cross-group filter is active
  useEffect(() => {
    if (!showCrossGroupEdges || selectedComm === null || !cyRef.current) {
      setCrossGroupData(null);
      setCrossGroupExpandedGrp(null);
      return;
    }
    const cy = cyRef.current;
    const commNodes = selectedComm === UNCATEGORIZED_COMM
      ? rawNodes.filter(n => mergedMapForPanel[n.id ?? n.key ?? n.name ?? ""] === UNCATEGORIZED_COMM)
      : (mergedGroupEntries.find(([c]) => Number(c) === selectedComm)?.[1] ?? []);
    const commNodeIds = new Set(commNodes.map(n => n.id ?? n.key ?? n.name ?? ''));
    const getShort = (id) => {
      const n = cy.$(`#${CSS.escape(id)}`);
      const full = n.data('fullLabel') || id;
      return full.includes('.') ? full.split('.').pop() : full;
    };
    const groupMap = new Map(); // extGrpNum → conn[]
    cy.edges('[!isLayerEdge]').forEach(e => {
      const srcId = e.source().id(), tgtId = e.target().id();
      const srcIn = commNodeIds.has(srcId), tgtIn = commNodeIds.has(tgtId);
      if (srcIn === tgtIn) return;
      const internalId = srcIn ? srcId : tgtId;
      const externalId = srcIn ? tgtId : srcId;
      const direction = srcIn ? 'out' : 'in';
      const extGrp = mergedMapForPanel[externalId];
      if (extGrp === undefined) return;
      if (!groupMap.has(extGrp)) groupMap.set(extGrp, []);
      const list = groupMap.get(extGrp);
      const key = `${internalId}__${externalId}`;
      if (!list.find(c => `${c.internalId}__${c.externalId}` === key)) {
        list.push({ internalId, internalName: getShort(internalId), externalId, externalName: getShort(externalId), direction });
      }
    });
    setCrossGroupData(groupMap.size > 0 ? groupMap : null);
    setCrossGroupExpandedGrp(null);
  }, [showCrossGroupEdges, selectedComm, mergedGroupEntries, mergedMapForPanel, rawNodes]);

  // auto-scroll selected node card into view so the toggle button is always visible
  useEffect(() => {
    if (!selectedNodeInPanel) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-panel-node="${selectedNodeInPanel}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 60);
    return () => clearTimeout(t);
  }, [selectedNodeInPanel]);

  // prominent node highlight in graph when a node is selected from the panel
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      // restore all non-layer nodes to base size/border first
      cy.nodes("[!isLayer]").style({ width: 19, height: 19, "border-width": 1, "border-color": "#00000022", "overlay-opacity": 0, "z-index": 1 });
      if (selectedNodeInPanel) {
        const commNum = communityMapRef.current[selectedNodeInPanel];
        const glowColor = (commNum !== undefined && commNum !== UNCATEGORIZED_COMM)
          ? COMMUNITY_COLORS[commNum % COMMUNITY_COLORS.length]
          : "#94a3b8";
        const n = cy.$(`#${CSS.escape(selectedNodeInPanel)}`);
        if (n.length) {
          n.style({
            width: 34, height: 34,
            "border-width": 4, "border-color": glowColor,
            "overlay-color": glowColor, "overlay-opacity": 0.30, "overlay-padding": 10,
            "z-index": 999,
            opacity: 1,
          });
        }
      }
    });
  }, [selectedNodeInPanel]);

  // highlight / dim connected edges + reveal neighbour nodes when edge toggle is on
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      if (!highlightEdgesActive || !selectedNodeInPanel) {
        cy.edges("[!isLayerEdge]").style({ opacity: 0.25, width: 0.7, "line-color": "#94a3b8", "target-arrow-color": "#94a3b8" });
        // restore node opacity to community-dim baseline using the merged map so
        // nodes absorbed from small communities stay visible in their group.
        cy.nodes("[!isLayer]").forEach(n => {
          const comm = mergedMapForPanel[n.id()];
          n.style("opacity", selectedComm === null || comm === selectedComm ? 1 : 0.07);
        });
      } else {
        const commNum = mergedMapForPanel[selectedNodeInPanel];
        const edgeColor = (commNum !== undefined && commNum !== UNCATEGORIZED_COMM)
          ? COMMUNITY_COLORS[commNum % COMMUNITY_COLORS.length]
          : "#94a3b8";

        // collect neighbour IDs across connected edges
        const visibleNodeIds = new Set([selectedNodeInPanel]);
        cy.edges("[!isLayerEdge]").forEach(e => {
          if (e.source().id() === selectedNodeInPanel) visibleNodeIds.add(e.target().id());
          else if (e.target().id() === selectedNodeInPanel) visibleNodeIds.add(e.source().id());
        });

        // style edges — force connected edges visible even if intra-group filter hid them
        cy.edges("[!isLayerEdge]").forEach(e => {
          const connected = e.source().id() === selectedNodeInPanel || e.target().id() === selectedNodeInPanel;
          e.style(connected
            ? { display: 'element', opacity: 0.85, width: 1.5, "line-color": edgeColor, "target-arrow-color": edgeColor }
            : { opacity: 0.12, width: 0.7, "line-color": "#cbd5e1", "target-arrow-color": "#cbd5e1" }
          );
        });

        // make selected node + all its neighbours fully visible, including nodes
        // outside the current group that are reachable via cross-group edges
        cy.nodes("[!isLayer]").forEach(n => {
          n.style("opacity", visibleNodeIds.has(n.id()) ? 1 : 0.07);
        });
      }
    });
  }, [highlightEdgesActive, selectedNodeInPanel, selectedComm, mergedMapForPanel]);

  // Filter edges based on the two toggles (within-group / cross-group).
  // Neither on → restore all edges. Either or both on → show only matching edges.
  // A selected node's edges always stay visible when edge-highlight is active.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      const neitherActive = !showIntraGroupEdges && !showCrossGroupEdges;
      if (neitherActive || showNodeEdges || selectedComm === null) {
        cy.edges('[!isLayerEdge]').removeStyle('display');
        return;
      }
      const commNodeIds = new Set();
      cy.nodes('[!isLayer]').forEach(n => {
        if (mergedMapForPanel[n.id()] === selectedComm) commNodeIds.add(n.id());
      });
      cy.edges('[!isLayerEdge]').forEach(e => {
        const srcIn = commNodeIds.has(e.source().id());
        const tgtIn = commNodeIds.has(e.target().id());
        const isIntra = srcIn && tgtIn;
        const isCross = (srcIn && !tgtIn) || (!srcIn && tgtIn); // exactly one endpoint in group
        const matchesFilter = (showIntraGroupEdges && isIntra) || (showCrossGroupEdges && isCross);
        const connectedToHighlight = highlightEdgesActive && selectedNodeInPanel &&
          (e.source().id() === selectedNodeInPanel || e.target().id() === selectedNodeInPanel);
        e.style('display', (matchesFilter || connectedToHighlight) ? 'element' : 'none');
      });
    });
  }, [showIntraGroupEdges, showCrossGroupEdges, selectedComm, showNodeEdges, mergedMapForPanel, highlightEdgesActive, selectedNodeInPanel]);

  // ── Layer panel: node-level edge highlight (published / read-only / arrange view) ───
  // Dims all nodes except the selected one and its direct neighbours;
  // highlights connected edges in the layer's colour.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || (versionStatus === 'draft' && workflowStage !== 'arrange')) return;
    cy.batch(() => {
      if (!layerPanelHighlightEdges || !layerPanelSelectedNode) {
        // Dim class nodes that are NOT in the selected layer; restore all when no layer is selected.
        // This complements the compound-node dim effect — Cytoscape child opacity is independent of
        // the parent compound's opacity, so we must set it explicitly here.
        if (layerPanelSelectedLayer) {
          cy.nodes("[!isLayer]").forEach(n => {
            const inSelected = nodeParentMapRef.current[n.id()] === layerPanelSelectedLayer;
            n.style("opacity", inSelected ? 1 : 0.1);
          });
        } else {
          cy.nodes("[!isLayer]").style("opacity", 1);
        }
        cy.edges("[!isLayerEdge]").style({ opacity: 0.25, width: 0.7, "line-color": "#94a3b8", "target-arrow-color": "#94a3b8" });
        return;
      }
      const lyr = layerNodes.find(l => l.id === layerPanelSelectedLayer);
      const edgeCol = lyr?.color ?? "#94a3b8";
      const visIds = new Set([layerPanelSelectedNode]);
      cy.edges("[!isLayerEdge]").forEach(e => {
        if (e.source().id() === layerPanelSelectedNode) visIds.add(e.target().id());
        else if (e.target().id() === layerPanelSelectedNode) visIds.add(e.source().id());
      });
      cy.edges("[!isLayerEdge]").forEach(e => {
        const conn = e.source().id() === layerPanelSelectedNode || e.target().id() === layerPanelSelectedNode;
        e.style(conn
          ? { display: "element", opacity: 0.85, width: 1.5, "line-color": edgeCol, "target-arrow-color": edgeCol }
          : { opacity: 0.12, width: 0.7, "line-color": "#cbd5e1", "target-arrow-color": "#cbd5e1" }
        );
      });
      cy.nodes("[!isLayer]").forEach(n => {
        n.style("opacity", visIds.has(n.id()) ? 1 : 0.07);
      });
    });
  }, [layerPanelHighlightEdges, layerPanelSelectedNode, layerPanelSelectedLayer, layerNodes, versionStatus, workflowStage]);

  // ── Layer panel: within-layer / cross-layer edge filter ───────────────────
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || (versionStatus === 'draft' && workflowStage !== 'arrange') || !layerPanelSelectedLayer) {
      // restore all edges when panel is closed or not in explorer mode
      if (cy) cy.edges("[!isLayerEdge]").removeStyle("display");
      return;
    }
    const neitherActive = !layerPanelIntraEdges && !layerPanelCrossEdges;
    if (neitherActive) {
      cy.edges("[!isLayerEdge]").removeStyle("display");
      return;
    }
    cy.batch(() => {
      const layerNodeIds = new Set();
      cy.nodes("[!isLayer]").forEach(n => {
        if (nodeParentMapRef.current[n.id()] === layerPanelSelectedLayer) layerNodeIds.add(n.id());
      });
      cy.edges("[!isLayerEdge]").forEach(e => {
        const srcIn = layerNodeIds.has(e.source().id());
        const tgtIn = layerNodeIds.has(e.target().id());
        const isIntra = srcIn && tgtIn;
        const isCross = (srcIn && !tgtIn) || (!srcIn && tgtIn);
        const pinned = layerPanelHighlightEdges && layerPanelSelectedNode &&
          (e.source().id() === layerPanelSelectedNode || e.target().id() === layerPanelSelectedNode);
        const show = (layerPanelIntraEdges && isIntra) || (layerPanelCrossEdges && isCross) || pinned;
        e.style("display", show ? "element" : "none");
      });
    });
  }, [layerPanelIntraEdges, layerPanelCrossEdges, layerPanelSelectedLayer, layerPanelHighlightEdges, layerPanelSelectedNode, versionStatus, workflowStage]);

  // ── Layer panel: dim non-selected layer compound nodes (explore-style focus) ──
  // When a layer is selected in the Explorer panel, dim all other layer nodes so
  // the selected one stands out — mirrors how selecting a community dims others.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || (versionStatus === 'draft' && workflowStage !== 'arrange')) return;
    cy.batch(() => {
      if (!layerPanelSelectedLayer) {
        cy.nodes("[?isLayer]").style({ opacity: 1, "border-width": 1 });
      } else {
        cy.nodes("[?isLayer]").forEach(n => {
          const isSelected = n.id() === layerPanelSelectedLayer;
          n.style({ opacity: isSelected ? 1 : 0.4, "border-width": isSelected ? 2.5 : 1 });
        });
      }
    });
    return () => {
      const cy2 = cyRef.current;
      if (cy2 && !cy2.destroyed()) cy2.nodes("[?isLayer]").style({ opacity: 1, "border-width": 1 });
    };
  }, [layerPanelSelectedLayer, versionStatus, workflowStage]);

  // ── Layer panel: compute Calls / Called-by for a selected node ────────────
  useEffect(() => {
    const cy = cyRef.current;
    if (!layerPanelSelectedNode || !cy) { setLayerNodeConnections(null); return; }

    const getShort = (id) => {
      const raw = rawNodes.find(n => (n.id ?? n.key ?? n.name ?? "") === id);
      const label = raw?.label ?? raw?.name ?? id;
      return label.split('.').pop() ?? label;
    };
    const getLayerInfo = (id) => {
      const layerId = nodeParentMapRef.current[id];
      const lyr = layerNodes.find(l => l.id === layerId);
      return { layerId: layerId ?? null, layerName: lyr?.name ?? null, layerColor: lyr?.color ?? "#94a3b8" };
    };

    const outMap = new Map();
    const inMap = new Map();
    cy.edges("[!isLayerEdge]").forEach(e => {
      const srcId = e.source().id();
      const tgtId = e.target().id();
      if (srcId === layerPanelSelectedNode && !outMap.has(tgtId)) {
        outMap.set(tgtId, { id: tgtId, shortName: getShort(tgtId), ...getLayerInfo(tgtId), isInLayer: nodeParentMapRef.current[tgtId] === layerPanelSelectedLayer });
      }
      if (tgtId === layerPanelSelectedNode && !inMap.has(srcId)) {
        inMap.set(srcId, { id: srcId, shortName: getShort(srcId), ...getLayerInfo(srcId), isInLayer: nodeParentMapRef.current[srcId] === layerPanelSelectedLayer });
      }
    });
    setLayerNodeConnections({ outgoing: [...outMap.values()], incoming: [...inMap.values()] });
  }, [layerPanelSelectedNode, layerPanelSelectedLayer, layerNodes, rawNodes]);

  // ── Layer panel: compute cross-layer connection map ───────────────────────
  useEffect(() => {
    const cy = cyRef.current;
    if (!layerPanelCrossEdges || !layerPanelSelectedLayer || !cy) {
      setCrossLayerData(null);
      setCrossLayerExpandedGrp(null);
      return;
    }
    const getShort = (id) => {
      const raw = rawNodes.find(n => (n.id ?? n.key ?? n.name ?? "") === id);
      const label = raw?.label ?? raw?.name ?? id;
      return label.split('.').pop() ?? label;
    };
    const layerNodeIds = new Set();
    cy.nodes("[!isLayer]").forEach(n => {
      if (nodeParentMapRef.current[n.id()] === layerPanelSelectedLayer) layerNodeIds.add(n.id());
    });

    const groupMap = new Map(); // layerId → { layerName, layerColor, conns[] }
    cy.edges("[!isLayerEdge]").forEach(e => {
      const srcId = e.source().id();
      const tgtId = e.target().id();
      const srcIn = layerNodeIds.has(srcId);
      const tgtIn = layerNodeIds.has(tgtId);
      if (srcIn === tgtIn) return; // skip intra-layer and fully-external
      const internalId = srcIn ? srcId : tgtId;
      const externalId = srcIn ? tgtId : srcId;
      const direction = srcIn ? 'out' : 'in';
      const extLayerId = nodeParentMapRef.current[externalId] ?? null;
      const extLyr = layerNodes.find(l => l.id === extLayerId);
      const extLayerName = extLyr?.name ?? (extLayerId ? `Layer ${extLayerId.substring(0, 6)}` : 'Unassigned');
      const extLayerColor = extLyr?.color ?? "#94a3b8";
      const key = extLayerId ?? '__unassigned__';
      if (!groupMap.has(key)) {
        groupMap.set(key, { layerName: extLayerName, layerColor: extLayerColor, conns: [] });
      }
      const list = groupMap.get(key).conns;
      const dupKey = `${internalId}__${externalId}`;
      if (!list.find(c => `${c.internalId}__${c.externalId}` === dupKey)) {
        list.push({ internalId, internalName: getShort(internalId), externalId, externalName: getShort(externalId), direction });
      }
    });
    setCrossLayerData(groupMap.size > 0 ? groupMap : null);
    setCrossLayerExpandedGrp(null);
  }, [layerPanelCrossEdges, layerPanelSelectedLayer, layerNodes, rawNodes]);

  // Community group hull overlay — appears on hover or when a group is being inspected.
  // Rendered as a DOM div (not a Cytoscape element) so it survives element-sync resets.
  useEffect(() => {
    const cy = cyRef.current;
    const targetComm = hoveredComm ?? selectedComm;
    // Skip hull for uncategorized — those nodes are scattered across the whole canvas
    // and a bounding-box hull would cover the entire viewport, which is more confusing than helpful.
    if (!cy || !cyReady || targetComm === null || targetComm === UNCATEGORIZED_COMM) { setCommHull(null); return; }

    const commColor = COMMUNITY_COLORS[targetComm % COMMUNITY_COLORS.length];
    const groupNodes = cy.nodes('[!isLayer]').filter(n => mergedMapForPanel[n.id()] === targetComm);
    if (groupNodes.length === 0) { setCommHull(null); return; }

    const computeBox = () => {
      const cy2 = cyRef.current;
      if (!cy2 || groupNodes.length === 0) return;
      const bb = groupNodes.boundingBox({ includeLabels: false });
      const zoom = cy2.zoom();
      const pan = cy2.pan();
      const PAD = 28;
      setCommHull({
        x: (bb.x1 - PAD) * zoom + pan.x,
        y: (bb.y1 - PAD) * zoom + pan.y,
        w: (bb.w + PAD * 2) * zoom,
        h: (bb.h + PAD * 2) * zoom,
        color: commColor,
        count: groupNodes.length,
        comm: targetComm,
      });
    };

    computeBox();
    cy.on('pan zoom', computeBox);
    return () => {
      if (!cy.destroyed()) cy.removeListener('pan zoom', computeBox);
      setCommHull(null);
    };
  }, [hoveredComm, selectedComm, cyReady, mergedMapForPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep refs in sync with state so Cytoscape handlers see current values
  useEffect(() => { connectModeRef.current = connectMode; }, [connectMode]);
  useEffect(() => { connectSourceRef.current = connectSource; }, [connectSource]);
  useEffect(() => { layerEdgesRef.current = layerEdges; }, [layerEdges]);
  useEffect(() => { scheduleSaveRef.current = scheduleSave; }, [scheduleSave]);
  useEffect(() => { activeLayoutRef.current = activeLayout; }, [activeLayout]);
  useEffect(() => { selectedCommRef.current = selectedComm; }, [selectedComm]);
  useEffect(() => { workflowStageRef.current = workflowStage; }, [workflowStage]);
  const versionStatusRef = useRef(versionStatus);
  useEffect(() => { versionStatusRef.current = versionStatus; }, [versionStatus]);

  // trigger autosave whenever React canvas state changes (layers, edges, collapse)
  useEffect(() => {
    if (isHydratingRef.current || !projectId || !definitionId || versionStatus !== 'draft') return;
    scheduleSave();
  }, [layerNodes, layerEdges, collapsedLayers, scheduleSave, projectId, definitionId, versionStatus]);

  // ── track rendered BB of selected layer (updates on pan/zoom) ────────────
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !selectedLayer) { setHandleBB(null); return; }

    const refresh = () => {
      const node = cy.$(`#${selectedLayer.layerId}`);
      if (node.length) setHandleBB(node.renderedBoundingBox({ includeLabels: false }));
      else setHandleBB(null);
    };

    cy.on("pan zoom position", refresh);
    refresh();
    return () => cy.off("pan zoom position", refresh);
  }, [selectedLayer]);

  // refresh handleBB after collapse/expand resizes the node imperatively
  useEffect(() => {
    if (!selectedLayer || !cyRef.current) return;
    const t = setTimeout(() => {
      const node = cyRef.current.$(`#${selectedLayer.layerId}`);
      if (node.length) setHandleBB(node.renderedBoundingBox({ includeLabels: false }));
    }, 60);
    return () => clearTimeout(t);
  }, [collapsedLayers, selectedLayer]);

  // ── track rendered BB of connect-mode source layer (updates on pan/zoom) ──
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !connectSource) { setConnectSourceBB(null); return; }
    const refresh = () => {
      const node = cy.$(`#${connectSource.layerId}`);
      if (!node.length) { setConnectSourceBB(null); return; }
      const bb = node.renderedBoundingBox({ includeLabels: false });
      setConnectSourceBB(bb);
      setConnectSource(prev => prev ? { ...prev, cx: (bb.x1 + bb.x2) / 2, cy: (bb.y1 + bb.y2) / 2 } : null);
    };
    cy.on("pan zoom position", refresh);
    refresh();
    return () => cy.off("pan zoom position", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectSource?.layerId]);

  // ── which layers are already connected to the current source ─────────────
  // only mark layers that this source already points TO (exact direction)
  const connectedToSource = useMemo(() => {
    if (!connectSource) return new Set();
    const ids = new Set();
    layerEdges.forEach(le => {
      if (le.source === connectSource.layerId) ids.add(le.target);
    });
    return ids;
  }, [connectSource, layerEdges]);

  // ── track rendered BBs of already-connected layers (for badge overlays) ──
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || connectedToSource.size === 0) { setConnectedLayerBBs({}); return; }
    const refresh = () => {
      const bbs = {};
      connectedToSource.forEach(id => {
        const node = cy.$(`#${id}`);
        if (node.length) bbs[id] = node.renderedBoundingBox({ includeLabels: false });
      });
      setConnectedLayerBBs(bbs);
    };
    cy.on("pan zoom position", refresh);
    refresh();
    return () => cy.off("pan zoom position", refresh);
  }, [connectedToSource]);

  // ── elements ──────────────────────────────────────────────────────────────

  const elements = useMemo(() => {
    // Build node→layer map (same priority as existing code: raw data parent wins)
    const nodeToLayer = {};
    rawNodes.forEach(node => {
      const id = node.id ?? node.key ?? node.name ?? "";
      const ref = nodeParentMapRef.current[id];
      if (ref) nodeToLayer[id] = ref;
      if (node.parent != null) nodeToLayer[id] = node.parent;
    });

    const layerEls = layerNodes.map(l => {
      const isCollapsed = collapsedLayers.has(l.id);
      const isSuggested = suggestedLayerIds.has(l.id);
      const isUncategorized = !!l.isUncategorized;
      const childCount = Object.values(nodeToLayer).filter(p => p === l.id).length;
      return {
        data: {
          id: l.id,
          // ⚠ prefix marks suggested layers as provisional; collapsed shows count
          label: isCollapsed
            ? `${l.name}  ·  ${childCount} nodes`
            : (isSuggested ? `⚠ ${l.name}` : l.name),
          isLayer: true,
          isCollapsed: isCollapsed || undefined,
          isSuggested: isSuggested || undefined,
          isUncategorized: isUncategorized || undefined,
          color: l.color,
        },
        style: isCollapsed
          ? { width: COLLAPSED_W, height: COLLAPSED_H }
          : (l.w && l.h ? { width: l.w, height: l.h } : {}),
      };
    });

    const nodeEls = rawNodes
      .filter(node => {
        const id = node.id ?? node.key ?? node.name ?? "";
        const parent = nodeToLayer[id];
        return !parent || !collapsedLayers.has(parent);
      })
      .map(node => {
        const id = node.id ?? node.key ?? node.name ?? "";
        const fullLabel = node.label ?? node.name ?? id ?? "";
        const shortLabel = (fullLabel.includes(".") ? fullLabel.split(".").pop() : fullLabel)
          .replace(/([a-z])([A-Z])/g, "$1\n$2");
        const sdRaw = Number(node?.sd ?? node?.smellDensity ?? node?.smell_density ?? 0);
        const sdValue = Number.isFinite(sdRaw) && sdRaw >= 0 ? sdRaw : 0;
        const locRaw = Number(node?.loc);
        const locValue = Number.isFinite(locRaw) && locRaw >= 0 ? locRaw : null;
        const baseColor = gradientColor(sdToGradientT(sdValue));
        const active = !activeNodeIds || !(activeNodeIds instanceof Set) || activeNodeIds.has(id);
        const activeMap = Object.keys(mergedCommunityMapRef.current).length > 0
          ? mergedCommunityMapRef.current
          : communityMapRef.current;
        const comm = activeMap[id];
        const commNodeColor = (comm !== undefined && comm !== UNCATEGORIZED_COMM)
          ? COMMUNITY_COLORS[comm % COMMUNITY_COLORS.length]
          : null; // uncategorized nodes keep their base color — no community overlay
        const data = { id, label: shortLabel, fullLabel, loc: locValue, sd: sdValue, baseColor, active, commId: comm ?? null, commNodeColor, commNodeBorder: commNodeColor };
        const parentId = nodeToLayer[id];
        if (parentId) data.parent = parentId;
        return { data, style: { "background-color": baseColor, opacity: active ? 1 : 0.15, width: 19, height: 19 } };
      });

    // Build real edges + collect virtual edges for collapsed layers
    const virtualEdges = new Map(); // "src|||tgt" → {id, source, target}
    const edgeEls = [];
    rawEdges.forEach((edge, i) => {
      const source = edge.from ?? edge.source ?? "";
      const target = edge.to ?? edge.target ?? "";
      const srcLayer = nodeToLayer[source];
      const tgtLayer = nodeToLayer[target];
      const srcCollapsed = srcLayer && collapsedLayers.has(srcLayer);
      const tgtCollapsed = tgtLayer && collapsedLayers.has(tgtLayer);

      if (srcCollapsed || tgtCollapsed) {
        const vs = srcCollapsed ? srcLayer : source;
        const vt = tgtCollapsed ? tgtLayer : target;
        if (vs === vt) return; // intra-layer or self-loop → drop
        const key = `${vs}|||${vt}`;
        if (!virtualEdges.has(key))
          virtualEdges.set(key, { id: `vcol-${vs}-${vt}`, source: vs, target: vt });
        return;
      }

      const active = !activeNodeIds || !(activeNodeIds instanceof Set) || (activeNodeIds.has(source) && activeNodeIds.has(target));
      const w = edge.weight ?? edge.data?.weight ?? null;
      edgeEls.push({ data: { id: edge.id ?? `e-${i}`, source, target, ...(w != null ? { weight: Number(w) } : {}) }, style: { opacity: active ? 0.25 : 0.04 } });
    });

    const virtualEdgeEls = [...virtualEdges.values()].map(ve => ({
      data: { id: ve.id, source: ve.source, target: ve.target, isVirtualCollapse: true },
    }));

    const layerEdgeEls = layerEdges.map(le => ({
      data: {
        id: le.id, source: le.source, target: le.target,
        isLayerEdge: true, color: le.color,
        rule: le.rule,
        enforcement: le.enforcement,
      },
    }));

    return [...layerEls, ...nodeEls, ...edgeEls, ...virtualEdgeEls, ...layerEdgeEls];
  }, [rawNodes, rawEdges, activeNodeIds, layerNodes, layerEdges, collapsedLayers, suggestedLayerIds, communityMapVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const stylesheet = useMemo(() => [
    { selector: "node[!isLayer]", style: { label: "data(label)", "text-valign": "bottom", "text-halign": "center", "font-size": 8, color: "#1e293b", "text-margin-y": 4, "border-width": 1, "border-color": "#00000022", "min-zoomed-font-size": 11, "text-wrap": "wrap", "text-max-width": "72px" } },
    { selector: "node[?isLayer]", style: { label: "data(label)", "background-color": "data(color)", "background-opacity": 0.07, "border-color": "data(color)", "border-width": 1, "text-valign": "top", "text-halign": "center", "font-size": 13, "font-weight": 600, color: "data(color)", "text-margin-y": -4, padding: "10px", shape: "roundrectangle", "text-background-color": "#ffffff", "text-background-opacity": 0.9, "text-background-padding": "3px", "text-background-shape": "roundrectangle", "overlay-opacity": 0 } },
    { selector: "node:selected", style: { "border-width": 2.5, "border-color": "#0ea5e9" } },
    { selector: "edge", style: { width: 0.7, "line-color": "#64748b", "target-arrow-color": "#0f172a", "target-arrow-shape": "triangle", "curve-style": "bezier", "arrow-scale": 0.75, opacity: 0.32, events: "no" } },
    { selector: "edge[!isLayerEdge]", style: { selectable: false } },
    // Layer-to-layer edges: clean colored arrows
    {
      selector: "edge[?isLayerEdge]", style: {
        "curve-style": "taxi",
        "taxi-direction": "auto",
        "taxi-turn": 40,
        width: 2.5,
        "line-color": "data(color)",
        "target-arrow-color": "data(color)",
        "target-arrow-shape": "triangle",
        "arrow-scale": 1.2,
        "line-style": "solid",
        opacity: 0.9,
        events: "yes",
        selectable: true,
      }
    },
    { selector: 'edge[enforcement="denied"]', style: { "line-style": "dashed", "line-dash-pattern": [8, 4] } },
    { selector: 'edge[enforcement="warning"]', style: { "line-color": "#d97706", "target-arrow-color": "#d97706" } },
    { selector: 'edge[enforcement="info"]', style: { "line-color": "#0ea5e9", "target-arrow-color": "#0ea5e9" } },
    { selector: "edge[?isLayerEdge]:selected", style: { width: 3, "arrow-scale": 1.6 } },
    { selector: "node[?isCollapsed]", style: { "background-opacity": 0.22, "border-style": "dashed", "border-width": 2.5, "text-valign": "center", "text-halign": "center", "text-margin-y": 0, "font-size": 12, "font-weight": 700, padding: "0px" } },
    { selector: "edge[?isVirtualCollapse]", style: { width: 1, "line-style": "dashed", "line-dash-pattern": [5, 4], "line-color": "#94a3b8", "target-arrow-color": "#94a3b8", "target-arrow-shape": "triangle", "arrow-scale": 0.5, opacity: 0.5, "curve-style": "bezier" } },
    // Suggested (unconfirmed) layers: amber italic label + dashed border — clearly provisional
    {
      selector: "node[?isSuggested]", style: {
        "border-style": "dashed", "border-width": 2.5,
        "background-opacity": 0.05, "border-opacity": 0.55,
        "font-style": "italic",
        color: "#92400e",
        "text-background-color": "#fffbeb", "text-background-opacity": 0.96,
        "text-background-padding": "4px",
      }
    },
    // Uncategorized layer override — slate-gray instead of amber; signals "needs review, not a real group"
    {
      selector: "node[?isSuggested][?isUncategorized]", style: {
        "border-color": "#94a3b8", "border-opacity": 0.6,
        "background-color": "#f1f5f9", "background-opacity": 0.07,
        color: "#64748b",
        "text-background-color": "#f8fafc", "text-background-opacity": 0.96,
      }
    },
    ...(activeLayout === "community-groups" && versionStatus === 'draft' ? [{
      selector: "node[!isLayer]",
      style: { "background-color": "data(commNodeColor)", "border-color": "data(commNodeBorder)" }
    }] : []),
    ...(showNodeEdges ? [] : [{ selector: "edge[!isLayerEdge]", style: { display: 'none' } }]),
  ], [activeLayout, showNodeEdges, versionStatus]);

  useEffect(() => {
    if (hasGraph) { setIsLoading(true); const t = setTimeout(() => setIsLoading(false), 400); return () => clearTimeout(t); }
    setIsLoading(false);
  }, [hasGraph]);

  // ── toolbar ───────────────────────────────────────────────────────────────

  const zoomIn = useCallback(() => { const cy = cyRef.current; if (!cy) return; cy.zoom({ level: Math.min(cy.zoom() * 1.25, cy.maxZoom()), renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } }); }, []);
  const zoomOut = useCallback(() => { const cy = cyRef.current; if (!cy) return; cy.zoom({ level: Math.max(cy.zoom() / 1.25, cy.minZoom()), renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } }); }, []);
  const fitView = useCallback(() => { const cy = cyRef.current; if (!cy) return; cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 300 }); }, []);
  const applyLayout = useCallback(name => {
    const cy = cyRef.current;
    if (!cy) return;
    setActiveLayout(name);
    setLayoutPickerOpen(false);

    if (name === "community-groups") {
      runCommunityLayout(cy, mergedCommunityMapRef.current);
      return;
    }

    if (name === "package-groups") {
      runPackageGroupLayout(cy);
      return;
    }

    const layers = cy.nodes('[?isLayer]');

    // No layers → plain layout on the whole graph
    if (layers.length === 0) {
      cy.layout({ name, animate: true, padding: 40 }).run();
      return;
    }

    // 1. Snapshot each compound: center + children positions relative to that center
    const snap = {};
    layers.forEach(layer => {
      const bb = layer.boundingBox({ includeLabels: false });
      const cx = (bb.x1 + bb.x2) / 2;
      const cy_ = (bb.y1 + bb.y2) / 2;
      const childRels = {};
      layer.children().forEach(child => {
        const pos = child.position();
        childRels[child.id()] = { dx: pos.x - cx, dy: pos.y - cy_ };
      });
      snap[layer.id()] = { cx, cy: cy_, w: bb.w, h: bb.h, childRels };
    });

    // 2. Add an invisible proxy node for each layer, sized to match the compound
    const proxyMap = []; // [{ pid, layerId }]
    layers.forEach(layer => {
      const s = snap[layer.id()];
      const pid = `__lp_${layer.id()}`;
      cy.add({
        group: 'nodes',
        data: { id: pid },
        position: { x: s.cx, y: s.cy },
        style: { width: s.w, height: s.h, opacity: 0, 'text-opacity': 0, 'background-opacity': 0, 'border-opacity': 0, events: 'no' },
      });
      proxyMap.push({ pid, layerId: layer.id() });
    });

    // 3. Proxy edges for layer-to-layer connections so force-based layouts respect them
    const proxyEdgeIds = [];
    layerEdgesRef.current.forEach(le => {
      const src = cy.$(`#__lp_${le.source}`);
      const tgt = cy.$(`#__lp_${le.target}`);
      if (src.length && tgt.length) {
        const eid = `__lpe_${le.id}`;
        cy.add({ group: 'edges', data: { id: eid, source: `__lp_${le.source}`, target: `__lp_${le.target}` } });
        proxyEdgeIds.push(eid);
      }
    });

    // 4. Run layout only on proxies + free nodes (synchronous so we can read results)
    const proxyNodes = cy.nodes().filter(n => n.id().startsWith('__lp_'));
    const freeNodes = cy.nodes().filter(n => !n.data('isLayer') && !n.parent().length && !n.id().startsWith('__lp_'));
    proxyNodes.add(freeNodes).layout({ name, animate: false, padding: 40 }).run();

    // 5. Move each compound's children by (proxy_new_pos - proxy_old_pos)
    cy.batch(() => {
      proxyMap.forEach(({ pid, layerId }) => {
        const newPos = cy.$(`#${pid}`).position();
        const s = snap[layerId];
        const dx = newPos.x - s.cx;
        const dy = newPos.y - s.cy;
        const layer = cy.$(`#${layerId}`);

        if (layer.children().length === 0) {
          layer.position({ x: newPos.x, y: newPos.y }); // empty compound: set directly
        } else {
          layer.children().forEach(child => {
            const rel = s.childRels[child.id()];
            if (rel) child.position({ x: s.cx + dx + rel.dx, y: s.cy + dy + rel.dy });
          });
        }
      });
    });

    // 6. Tear down proxies
    proxyEdgeIds.forEach(eid => cy.remove(cy.$(`#${eid}`)));
    proxyMap.forEach(({ pid }) => cy.remove(cy.$(`#${pid}`)));

    cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 400 });
  }, []);
  const exportPng = useCallback(() => { const cy = cyRef.current; if (!cy) return; const a = document.createElement("a"); a.href = cy.png({ full: true, scale: 2, bg: "#ffffff" }); a.download = "architecture-compliance.png"; a.click(); }, []);

  // ── draw layer ────────────────────────────────────────────────────────────

  const toggleDrawMode = useCallback(() => {
    setDrawMode(m => !m);
    setPendingRect(null); setDrawRect(null); setIsDrawing(false); setSelectedLayer(null);
    // exit connect mode when draw mode activates
    setConnectMode(false); setConnectSource(null); setConnectMouse(null); setSelectedLayerEdge(null);
  }, []);

  const toggleConnectMode = useCallback(() => {
    setConnectMode(m => !m);
    setConnectSource(null); setConnectMouse(null); setConnectSourceBB(null); setSelectedLayerEdge(null);
    // exit draw mode when connect mode activates
    setDrawMode(false); setPendingRect(null); setDrawRect(null); setIsDrawing(false); setSelectedLayer(null);
  }, []);

  const navigateToNode = useCallback((nodeId) => {
    const cy = cyRef.current;
    if (!cy) return;
    const n = cy.$(`#${CSS.escape(nodeId)}`);
    if (!n.length) return;

    // Build the neighborhood subgraph: the node + its direct edges + connected nodes
    const connectedEdges = n.connectedEdges('[!isLayerEdge]');
    const neighbors = connectedEdges.connectedNodes().not(n);
    const subgraph = n.union(connectedEdges).union(neighbors);

    // Fit to the full neighborhood so edges + neighbors are in view, or just
    // center on the node if it has no connections.
    if (subgraph.length > 1) {
      cy.animate({ fit: { eles: subgraph, padding: 100 } }, { duration: 450, easing: 'ease-in-out' });
    } else {
      cy.animate({ zoom: Math.max(cy.zoom(), 2.2), center: { eles: n } }, { duration: 400, easing: 'ease-in-out' });
    }

    // ── Apply highlighting DIRECTLY in Cytoscape (imperative, immediate) ──────
    // This guarantees correct visual state regardless of React effect ordering or
    // competing effects that might run after us.
    const connectedEdgeIds = new Set();
    connectedEdges.forEach(e => connectedEdgeIds.add(e.id()));
    const visibleNodeIds = new Set([nodeId]);
    neighbors.forEach(nb => visibleNodeIds.add(nb.id()));

    cy.batch(() => {
      // Make sure all non-layer edges are visible first (undo any display:none
      // that might have been applied by the intra/cross-group edge filters).
      cy.edges('[!isLayerEdge]').removeStyle('display');

      // Colour connected edges, dim unconnected ones
      cy.edges('[!isLayerEdge]').forEach(e => {
        if (connectedEdgeIds.has(e.id())) {
          e.style({ display: 'element', opacity: 0.85, width: 1.5, 'line-color': '#0ea5e9', 'target-arrow-color': '#0ea5e9' });
        } else {
          e.style({ opacity: 0.12, width: 0.7, 'line-color': '#cbd5e1', 'target-arrow-color': '#cbd5e1' });
        }
      });

      // Highlight selected node + neighbours; dim everything else
      cy.nodes('[!isLayer]').forEach(nd => {
        if (visibleNodeIds.has(nd.id())) {
          nd.style({ opacity: 1 });
        } else {
          nd.style({ opacity: 0.07 });
        }
      });

      // Selected node: glow treatment
      n.style({
        width: 34, height: 34,
        'border-width': 4, 'border-color': '#0ea5e9',
        'overlay-color': '#0ea5e9', 'overlay-opacity': 0.30, 'overlay-padding': 10,
        'z-index': 999, opacity: 1,
      });
    });
    // ─────────────────────────────────────────────────────────────────────────

    // Update React state so that panels, labels, and the edge-highlight effect
    // stay in sync with what we just drew imperatively.
    setShowNodeEdges(true);
    setShowIntraGroupEdges(false);
    setShowCrossGroupEdges(false);
    setSelectedNodeInPanel(nodeId);
    setHighlightEdgesActive(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateToLayer = useCallback((layerId) => {
    const cy = cyRef.current;
    if (!cy) return;
    const node = cy.$(`#${CSS.escape(layerId)}`);
    if (!node.length) return;
    cy.animate({ fit: { eles: node, padding: 80 } }, { duration: 400, easing: 'ease-in-out' });
    const color = node.data('color') || '#94a3b8';
    setSelectedLayer({ layerId, color });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteLayerEdge = useCallback(() => {
    if (!selectedLayerEdge) return;
    const cy = cyRef.current;
    if (cy) cy.remove(cy.$(`#${selectedLayerEdge.edgeId}`));
    setLayerEdges(prev => prev.filter(le => le.id !== selectedLayerEdge.edgeId));
    setSelectedLayerEdge(null);
  }, [selectedLayerEdge]);

  const changeLayerEdgeEnforcement = useCallback((newEnforcement) => {
    if (!selectedLayerEdge) return;
    const newColor = ruleColor(newEnforcement);
    const cy = cyRef.current;
    if (cy) {
      const el = cy.$(`#${selectedLayerEdge.edgeId}`);
      if (el.length) el.data({ enforcement: newEnforcement, color: newColor });
    }
    setLayerEdges(prev => prev.map(le =>
      le.id === selectedLayerEdge.edgeId
        ? { ...le, enforcement: newEnforcement, color: newColor }
        : le
    ));
    setSelectedLayerEdge(null);
    scheduleSaveRef.current?.();
  }, [selectedLayerEdge]);

  // Mouse move on container: update rubber-band target when in connect mode
  const handleContainerMouseMove = useCallback(e => {
    if (connectModeRef.current && connectSourceRef.current && containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setConnectMouse({ x: e.clientX - r.left, y: e.clientY - r.top });
    }
  }, []);

  const handleDrawStart = useCallback(e => {
    if (!drawMode || !containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setIsDrawing(true); setDrawStart({ x: e.clientX - r.left, y: e.clientY - r.top });
    setDrawRect(null); setPendingRect(null);
  }, [drawMode]);

  const handleDrawMove = useCallback(e => {
    if (!isDrawing || !drawStart || !containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    setDrawRect(normalizeRect(drawStart, { x: e.clientX - r.left, y: e.clientY - r.top }));
  }, [isDrawing, drawStart]);

  const handleDrawEnd = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false); setDrawStart(null);
    if (drawRect && drawRect.width > 20 && drawRect.height > 20) { setPendingRect(drawRect); setPendingName(""); }
    setDrawRect(null);
  }, [isDrawing, drawRect]);

  const confirmLayer = useCallback(() => {
    const cy = cyRef.current;
    if (!cy || !pendingRect || !pendingName.trim()) return;

    const layerId = `layer-${Date.now()}`;
    const color = LAYER_COLORS[layerNodes.length % LAYER_COLORS.length];
    const screenCx = pendingRect.x + pendingRect.width / 2;
    const screenCy = pendingRect.y + pendingRect.height / 2;
    const graphPos = { x: (screenCx - cy.pan().x) / cy.zoom(), y: (screenCy - cy.pan().y) / cy.zoom() };

    // capture only unassigned nodes inside the drawn rect — skip nodes already in a layer
    cy.nodes("[!isLayer]").forEach(node => {
      if (node.parent().length) return; // already belongs to a layer
      const rp = node.renderedPosition();
      if (rp.x >= pendingRect.x && rp.x <= pendingRect.x + pendingRect.width &&
        rp.y >= pendingRect.y && rp.y <= pendingRect.y + pendingRect.height) {
        nodeParentMapRef.current[node.id()] = layerId;
      }
    });

    // Convert drawn rect dimensions from screen to graph space so we can
    // set an explicit width/height on the childless compound node.
    const gw = pendingRect.width / cy.zoom();
    const gh = pendingRect.height / cy.zoom();

    cy.add({ group: "nodes", data: { id: layerId, label: pendingName.trim(), isLayer: true, color }, position: graphPos });

    // Apply explicit size immediately so the node is visible even without children
    const newNode = cy.$(`#${layerId}`);
    newNode.style({ width: gw, height: gh });

    Object.keys(nodeParentMapRef.current).forEach(nid => {
      if (nodeParentMapRef.current[nid] === layerId) {
        const n = cy.$(`#${nid}`); if (n.length) n.move({ parent: layerId });
      }
    });

    // Spread children inside the compound so they don't pile up
    const compound = cy.$(`#${layerId}`);
    const children = compound.children();
    if (children.length > 0) {
      const NS_INNER = 82;
      const perRow = Math.ceil(Math.sqrt(children.length));
      const bb = compound.boundingBox({ includeLabels: false });
      const startX = bb.x1 + 20;
      const startY = bb.y1 + 20;
      cy.batch(() => {
        children.forEach((child, i) => {
          child.position({
            x: startX + (i % perRow) * NS_INNER,
            y: startY + Math.floor(i / perRow) * NS_INNER,
          });
        });
      });
    }

    setLayerNodes(prev => [...prev, { id: layerId, name: pendingName.trim(), color, w: gw, h: gh }]);
    setPendingRect(null); setPendingName("");
  }, [pendingRect, pendingName, layerNodes.length]);

  const cancelPending = useCallback(() => { setPendingRect(null); setPendingName(""); }, []);

  // ── layer resize (graph-space, document-level listeners) ─────────────────
  // We register mousemove/mouseup on document immediately (synchronously) so
  // events are captured before React has a chance to re-render and insert the
  // overlay div.  This avoids the timing gap that caused resize to silently fail.

  const startLayerResize = useCallback((handle, e) => {
    e.stopPropagation();
    e.preventDefault();
    const cy = cyRef.current;
    if (!cy || !selectedLayer || !containerRef.current) return;

    const node = cy.$(`#${selectedLayer.layerId}`);
    if (!node.length) return;

    // ungrabify prevents user drag without blocking compound auto-reflow from children
    node.ungrabify();

    const graphBB = node.boundingBox({ includeLabels: false });
    const childData = {};
    node.children().forEach(c => { childData[c.id()] = { ...c.position() }; });

    const r = containerRef.current.getBoundingClientRect();
    const rd = {
      layerId: selectedLayer.layerId,
      handle,
      startGraphBB: { x1: graphBB.x1, y1: graphBB.y1, x2: graphBB.x2, y2: graphBB.y2, w: graphBB.w, h: graphBB.h },
      startChildData: childData,
      startMouse: { x: e.clientX - r.left, y: e.clientY - r.top },
    };
    setResizingLayer(rd); // drives cursor overlay only

    const onMove = (ev) => {
      const cr = containerRef.current?.getBoundingClientRect();
      const cy2 = cyRef.current;
      if (!cr || !cy2) return;

      const zoom = cy2.zoom();
      const dx = (ev.clientX - cr.left - rd.startMouse.x) / zoom;
      const dy = (ev.clientY - cr.top - rd.startMouse.y) / zoom;
      const { x1, y1, x2, y2, w, h } = rd.startGraphBB;
      const MIN = 60;

      let nx1 = x1, ny1 = y1, nx2 = x2, ny2 = y2;
      if (rd.handle.includes("e")) nx2 = Math.max(x1 + MIN, x2 + dx);
      if (rd.handle.includes("s")) ny2 = Math.max(y1 + MIN, y2 + dy);
      if (rd.handle.includes("w")) nx1 = Math.min(x2 - MIN, x1 + dx);
      if (rd.handle.includes("n")) ny1 = Math.min(y2 - MIN, y1 + dy);

      const nw = nx2 - nx1, nh = ny2 - ny1;
      const n = cy2.$(`#${rd.layerId}`);

      cy2.batch(() => {
        if (n.children().length === 0) {
          // For childless compound nodes, set width/height directly
          n.style({ width: nw, height: nh });
          n.position({ x: (nx1 + nx2) / 2, y: (ny1 + ny2) / 2 });
        } else {
          n.children().forEach(child => {
            const orig = rd.startChildData[child.id()];
            if (!orig) return;
            const relX = w > 1 ? (orig.x - x1) / w : 0.5;
            const relY = h > 1 ? (orig.y - y1) / h : 0.5;
            child.position({ x: nx1 + relX * nw, y: ny1 + relY * nh });
          });
        }
      });

      // persist the new size so it survives re-renders
      setLayerNodes(prev => prev.map(l =>
        l.id === rd.layerId
          ? { ...l, w: nw, h: nh }
          : l
      ));
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      cyRef.current?.$(`#${rd.layerId}`).grabify();
      setResizingLayer(null);
    };

    // synchronous — no React re-render required before these fire
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [selectedLayer]);

  // ── rename / delete ───────────────────────────────────────────────────────

  const deleteSelectedLayer = useCallback(() => {
    if (!selectedLayer) return;
    const cy = cyRef.current;
    if (cy) {
      const compound = cy.$(`#${selectedLayer.layerId}`);
      compound.children().move({ parent: null });
      cy.remove(compound);
    }
    Object.keys(nodeParentMapRef.current).forEach(nid => {
      if (nodeParentMapRef.current[nid] === selectedLayer.layerId) delete nodeParentMapRef.current[nid];
    });
    setLayerNodes(prev => prev.filter(l => l.id !== selectedLayer.layerId));
    setCollapsedLayers(prev => { const s = new Set(prev); s.delete(selectedLayer.layerId); return s; });
    delete collapsedNodePositions.current[selectedLayer.layerId];
    setLayerEdges(prev => prev.filter(
      le => le.source !== selectedLayer.layerId && le.target !== selectedLayer.layerId
    ));
    setSelectedLayer(null);
  }, [selectedLayer]);

  const toggleCollapseLayer = useCallback(() => {
    if (!selectedLayer) return;
    const { layerId } = selectedLayer;
    const cy = cyRef.current;
    if (!cy) return;
    const isCollapsed = collapsedLayers.has(layerId);
    const layerCy = cy.$(`#${layerId}`);

    if (!isCollapsed) {
      // Save positions as offsets from the layer centre so expand works at any location
      const bb = layerCy.boundingBox({ includeLabels: false });
      const cx = (bb.x1 + bb.x2) / 2;
      const cy_c = (bb.y1 + bb.y2) / 2;
      const positions = {};
      layerCy.children().forEach(n => {
        const p = n.position();
        positions[n.id()] = { x: p.x - cx, y: p.y - cy_c };
      });
      collapsedNodePositions.current[layerId] = positions;
      setAddNodesMode(false);
      setRemoveNodesMode(false);
      setPopupEditing(false);
      setCollapsedLayers(prev => new Set([...prev, layerId]));
      // Imperatively resize after React re-renders (children removed from elements)
      requestAnimationFrame(() => {
        layerCy.style({ width: COLLAPSED_W, height: COLLAPSED_H });
      });
    } else {
      const layer = layerNodes.find(l => l.id === layerId);
      // Capture position NOW — collapsed compound has no children so position() returns the
      // explicit centre the user dragged it to.  After setCollapsedLayers triggers a re-render
      // and CytoscapeComponent re-adds children (all landing at 0,0 by default), the compound's
      // centroid shifts to 0,0 and reading position() inside the timeout gives the wrong anchor.
      const expandAnchor = layerCy.length ? { ...layerCy.position() } : { x: 0, y: 0 };
      setCollapsedLayers(prev => { const s = new Set(prev); s.delete(layerId); return s; });
      setTimeout(() => {
        const n = cyRef.current?.$(`#${layerId}`);
        if (n?.length) {
          if (layer?.w && layer?.h) n.style({ width: layer.w, height: layer.h });
          else n.removeStyle("width height");
        }
        const saved = collapsedNodePositions.current[layerId];
        if (saved && cyRef.current) {
          cyRef.current.batch(() => {
            Object.entries(saved).forEach(([nid, rel]) => {
              const node = cyRef.current.$(`#${CSS.escape(nid)}`);
              if (node.length) node.position({ x: expandAnchor.x + rel.x, y: expandAnchor.y + rel.y });
            });
          });
        }
      }, 80);
    }
  }, [selectedLayer, collapsedLayers, layerNodes]);

  // ── suggest layers from community groups ───────────────────────────────────

  const _spreadChildren = useCallback((cy, layerId) => {
    const compound = cy.$(`#${CSS.escape(layerId)}`);
    const children = compound.children();
    if (children.length === 0) return;
    const NS = 82, perRow = Math.ceil(Math.sqrt(children.length));
    const bb = compound.boundingBox({ includeLabels: false });
    cy.batch(() => {
      children.forEach((child, ci) => {
        child.position({
          x: bb.x1 + 20 + (ci % perRow) * NS,
          y: bb.y1 + 20 + Math.floor(ci / perRow) * NS,
        });
      });
    });
  }, []);

  const _removeSuggestedLayerFromCy = useCallback((cy, layerId) => {
    const compound = cy.$(`#${CSS.escape(layerId)}`);
    if (!compound.length) return;
    compound.children().forEach(c => {
      c.move({ parent: null });
      delete nodeParentMapRef.current[c.id()];
    });
    cy.remove(compound);
  }, []);

  // Build a graphology graph from the current Cytoscape state and run Louvain community detection.
  // Primary: uses the GraphML edge `weight` attribute (dependency density) when present.
  // Fallback: counts duplicate directed edges between the same pair (A→B + B→A = weight 2).
  const computeAndStoreCommunities = useCallback((cy) => {
    const G = new Graph({ type: 'undirected', multi: false, allowSelfLoops: false });
    cy.nodes('[!isLayer]').forEach(n => { try { G.addNode(n.id()); } catch { } });

    // Accumulate weight per undirected pair.
    // If the edge carries a GraphML weight, use it; otherwise contribute 1 (edge-count fallback).
    const pairWeight = {};
    cy.edges('[!isLayerEdge]').forEach(e => {
      const s = e.source().id(), t = e.target().id();
      if (s === t) return;
      const key = s < t ? `${s}\0${t}` : `${t}\0${s}`;
      const w = e.data('weight');
      pairWeight[key] = (pairWeight[key] || 0) + (w != null ? Number(w) : 1);
    });

    Object.entries(pairWeight).forEach(([key, weight]) => {
      const [s, t] = key.split('\0');
      if (G.hasNode(s) && G.hasNode(t)) {
        try { G.addUndirectedEdge(s, t, { weight }); } catch { }
      }
    });

    if (G.order === 0) return;

    communityGraphRef.current = G;
    communityMapRef.current = louvain(G, { weightAttribute: 'weight' });
    setCommunityMapVersion(v => v + 1);
  }, []);

  // Apply the current panelMinNodes threshold: re-color nodes and re-run community layout.
  // Sub-threshold nodes that couldn't be merged (isolated communities with no cross-edges)
  // are force-assigned to the largest qualifying cluster so nothing is left floating outside.
  const handleGenerateClusters = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;

    // ── Clear any pending suggested layers so their nodes are free again ──
    // This keeps the panel node-counts (which count ALL nodes via rawNodes) consistent
    // with what createLayerFromCommunity / suggestLayersFromCommunities can actually grab.
    // Confirmed (permanent) layers are unaffected — only unconfirmed suggestions are dissolved.
    const pendingIds = suggestedLayerIdsRef.current;
    if (pendingIds.size > 0) {
      pendingIds.forEach(id => _removeSuggestedLayerFromCy(cy, id));
      setLayerNodes(prev => prev.filter(l => !pendingIds.has(l.id)));
      setSuggestedLayerIds(new Set());
    }

    const threshold = panelMinNodes;
    // Compute once — used for both colors and layout.
    const freshMap = mergeCommunities(communityMapRef.current, communityGraphRef.current, threshold);

    // Set ref synchronously so elements useMemo reads the correct map
    // when setCommunityMapVersion triggers the re-render below.
    mergedCommunityMapRef.current = freshMap;
    setAppliedMinNodes(threshold);      // updates panel list on next render
    setCommunityMapVersion(v => v + 1); // triggers elements useMemo → node colors update

    if (activeLayoutRef.current !== 'community-groups') return;

    // Build group sizes to find the largest qualifying cluster
    const groups = {};
    for (const [nodeId, comm] of Object.entries(freshMap)) {
      if (!groups[comm]) groups[comm] = [];
      groups[comm].push(nodeId);
    }

    const largestComm = Object.entries(groups)
      .filter(([, ns]) => ns.length >= threshold)
      .sort((a, b) => b[1].length - a[1].length)[0]?.[0];

    if (largestComm !== undefined) {
      // Force sub-threshold isolated nodes into the largest cluster for layout only
      const layoutMap = { ...freshMap };
      for (const [, nodes] of Object.entries(groups)) {
        if (nodes.length < threshold) {
          nodes.forEach(id => { layoutMap[id] = Number(largestComm); });
        }
      }
      runCommunityLayout(cy, layoutMap);
    } else {
      runCommunityLayout(cy, freshMap);
    }

    // Detect confirmed layers whose nodes now span multiple communities at the new threshold.
    // Each such layer gets a split suggestion so the user can redistribute or keep as-is.
    const splits = [];
    cy.nodes("[?isLayer]").forEach(compound => {
      if (compound.data("isSuggested")) return; // suggested layers haven't been committed yet — skip
      const subGroups = {};
      compound.children().forEach(n => {
        const comm = freshMap[n.id()];
        if (comm === undefined) return;
        if (!subGroups[comm]) subGroups[comm] = [];
        subGroups[comm].push(n.id());
      });
      const entries = Object.entries(subGroups);
      if (entries.length > 1) {
        splits.push({
          layerId: compound.id(),
          layerName: compound.data("label") || compound.id(),
          layerColor: compound.data("color") || "#64748b",
          subGroups: entries.map(([commId, nodes]) => ({ commId: Number(commId), nodes })),
        });
      }
    });
    setLayerSplitSuggestions(splits);
  }, [panelMinNodes, _removeSuggestedLayerFromCy]);

  // Generate suggested layers from community groups (filtered by minNodes threshold).
  // Any existing suggestions are cleared first; only unassigned nodes are eligible.
  const suggestLayersFromCommunities = useCallback((minNodes) => {
    const cy = cyRef.current;
    if (!cy) return 0;

    // Clear stale suggestions using the ref (avoids stale-closure on state)
    const staleIds = suggestedLayerIdsRef.current;
    staleIds.forEach(id => _removeSuggestedLayerFromCy(cy, id));
    setLayerNodes(prev => prev.filter(l => !staleIds.has(l.id)));

    // Merge small communities into their most-connected neighbour before grouping.
    // communityMapRef stays untouched — Community Groups panel is unaffected.
    const mergedMap = mergeCommunities(communityMapRef.current, communityGraphRef.current, minNodes);

    // Group unassigned nodes by merged community (including UNCATEGORIZED_COMM)
    const groups = {};
    cy.nodes("[!isLayer]").forEach(n => {
      if (n.parent().length) return;
      const comm = mergedMap[n.id()];
      if (comm === undefined) return;
      if (!groups[comm]) groups[comm] = [];
      groups[comm].push(n);
    });

    const qualifying = Object.entries(groups)
      .filter(([key, ns]) => Number(key) !== UNCATEGORIZED_COMM && ns.length >= minNodes)
      .sort((a, b) => b[1].length - a[1].length);

    const uncategorizedNodes = groups[UNCATEGORIZED_COMM] ?? [];

    if (qualifying.length === 0 && uncategorizedNodes.length === 0) {
      setSuggestedLayerIds(new Set());
      return 0;
    }

    const newIds = new Set();
    const newEntries = [];
    const now = Date.now();

    // ── Create one suggested layer per qualifying community ───────────────────
    qualifying.forEach(([comm, nodes], i) => {
      const layerId = `suggestion-comm-${comm}-${now}`;
      const color = COMMUNITY_COLORS[Number(comm) % COMMUNITY_COLORS.length];
      const name = `Suggested Group ${i + 1}`;

      cy.add({ group: "nodes", data: { id: layerId, label: name, isLayer: true, isSuggested: true, color } });
      nodes.forEach(n => { n.move({ parent: layerId }); nodeParentMapRef.current[n.id()] = layerId; });
      _spreadChildren(cy, layerId);

      newIds.add(layerId);
      newEntries.push({ id: layerId, name, color });
    });

    // ── Create the Uncategorized layer for orphan nodes ───────────────────────
    // These are nodes whose small community had no edge path to any large group.
    // Surfacing them explicitly lets the user review and manually reassign them
    // rather than silently mixing them into an unrelated architectural layer.
    if (uncategorizedNodes.length > 0) {
      const layerId = `suggestion-uncategorized-${now}`;
      const color = '#94a3b8'; // slate-gray — visually distinct from real groups
      const name = 'Uncategorized';

      cy.add({
        group: "nodes", data: {
          id: layerId, label: name,
          isLayer: true, isSuggested: true, isUncategorized: true, color,
        }
      });
      uncategorizedNodes.forEach(n => { n.move({ parent: layerId }); nodeParentMapRef.current[n.id()] = layerId; });
      _spreadChildren(cy, layerId);

      newIds.add(layerId);
      newEntries.push({ id: layerId, name, color, isUncategorized: true });
    }

    setLayerNodes(prev => [...prev, ...newEntries]);
    setSuggestedLayerIds(newIds);
    if (newEntries.length > 0) setSelectedLayer({ layerId: newEntries[0].id, color: newEntries[0].color, name: newEntries[0].name });
    cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 400 });
    return qualifying.length;
  }, [_spreadChildren, _removeSuggestedLayerFromCy]);

  // Create a single suggested layer from one community group.
  // map defaults to the raw Louvain map but callers can pass the merged map.
  // customName overrides the default "Community X" label.
  const createLayerFromCommunity = useCallback((commNum, map = null, customName = null) => {
    const cy = cyRef.current;
    if (!cy) return;
    const resolvedMap = map ?? communityMapRef.current;

    const nodes = [];
    cy.nodes("[!isLayer]").forEach(n => {
      if (n.parent().length) return;
      if (resolvedMap[n.id()] === commNum) nodes.push(n);
    });
    if (nodes.length === 0) return;

    const layerId = `suggestion-comm-${commNum}-${Date.now()}`;
    const color = COMMUNITY_COLORS[commNum % COMMUNITY_COLORS.length];
    const name = customName?.trim() || `Community ${commNum}`;

    cy.add({ group: "nodes", data: { id: layerId, label: name, isLayer: true, isSuggested: true, color } });
    nodes.forEach(n => { n.move({ parent: layerId }); nodeParentMapRef.current[n.id()] = layerId; });
    _spreadChildren(cy, layerId);

    setLayerNodes(prev => [...prev, { id: layerId, name, color }]);
    setSuggestedLayerIds(prev => new Set([...prev, layerId]));
    setSelectedLayer({ layerId, color, name });

    const cy2 = cyRef.current;
    if (cy2) cy2.animate({ fit: { eles: cy2.$(`#${CSS.escape(layerId)}`), padding: 80 } }, { duration: 400 });

    return layerId; // caller can immediately confirm the layer
  }, [_spreadChildren]);

  // Confirm a suggestion → remove provisional marker; layer becomes permanent on next save.
  const confirmSuggestedLayer = useCallback((layerId) => {
    const cy = cyRef.current;
    if (cy) {
      const n = cy.$(`#${CSS.escape(layerId)}`);
      if (n.length) { n.removeData("isSuggested"); n.data("isSuggested", undefined); }
    }
    setSuggestedLayerIds(prev => { const s = new Set(prev); s.delete(layerId); return s; });
    scheduleSaveRef.current?.();
  }, []);

  // Reject a suggestion → remove compound, release children, discard.
  const rejectSuggestedLayer = useCallback((layerId) => {
    const cy = cyRef.current;
    if (cy) _removeSuggestedLayerFromCy(cy, layerId);
    setLayerNodes(prev => prev.filter(l => l.id !== layerId));
    setSuggestedLayerIds(prev => { const s = new Set(prev); s.delete(layerId); return s; });
    if (selectedLayer?.layerId === layerId) setSelectedLayer(null);
  }, [_removeSuggestedLayerFromCy, selectedLayer]);

  // Redistribute a split layer: dissolve the compound and create a new suggested layer for each
  // sub-group whose nodes share the same community at the current threshold.
  const redistributeLayer = useCallback((layerId, subGroups) => {
    const cy = cyRef.current;
    if (!cy) return;

    const compound = cy.$(`#${CSS.escape(layerId)}`);
    if (!compound.length) return;

    // Release all children from the compound
    compound.children().forEach(n => {
      n.move({ parent: null });
      delete nodeParentMapRef.current[n.id()];
    });
    cy.remove(compound);
    setLayerNodes(prev => prev.filter(l => l.id !== layerId));
    setSuggestedLayerIds(prev => { const s = new Set(prev); s.delete(layerId); return s; });
    if (selectedLayer?.layerId === layerId) setSelectedLayer(null);

    // Create a new suggested-layer compound for each sub-group
    const now = Date.now();
    const newIds = new Set();
    const newEntries = [];

    subGroups.forEach(({ commId, nodes }, i) => {
      const newLayerId = `suggestion-comm-${commId}-${now}-${i}`;
      const color = COMMUNITY_COLORS[commId % COMMUNITY_COLORS.length];
      const name = `Community ${commId}`;

      cy.add({ group: "nodes", data: { id: newLayerId, label: name, isLayer: true, isSuggested: true, color } });
      nodes.forEach(nodeId => {
        const n = cy.$(`#${CSS.escape(nodeId)}`);
        if (n.length) { n.move({ parent: newLayerId }); nodeParentMapRef.current[nodeId] = newLayerId; }
      });
      _spreadChildren(cy, newLayerId);

      newIds.add(newLayerId);
      newEntries.push({ id: newLayerId, name, color });
    });

    setLayerNodes(prev => [...prev, ...newEntries]);
    setSuggestedLayerIds(prev => new Set([...prev, ...newIds]));
    setLayerSplitSuggestions(prev => prev.filter(s => s.layerId !== layerId));

    cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 400 });
  }, [_spreadChildren, selectedLayer]);

  // ─────────────────────────────────────────────────────────────────────────────

  const confirmRename = useCallback(() => {
    if (!selectedLayer || !popupName.trim()) return;
    const cy = cyRef.current;
    if (cy) cy.$(`#${selectedLayer.layerId}`).data("label", popupName.trim());
    setLayerNodes(prev => prev.map(l => l.id === selectedLayer.layerId ? { ...l, name: popupName.trim() } : l));
    setPopupEditing(false);
  }, [selectedLayer, popupName]);

  const confirmAddNodes = useCallback(() => {
    if (!selectedLayer || !addNodesPattern.trim()) return;
    const cy = cyRef.current;
    if (!cy) return;
    let regex;
    try { regex = globToRegex(addNodesPattern.trim()); } catch { return; }
    const layerId = selectedLayer.layerId;
    cy.nodes("[!isLayer]").forEach(node => {
      if (node.parent().length) return;
      if (regex.test(node.id())) {
        node.move({ parent: layerId });
        nodeParentMapRef.current[node.id()] = layerId;
      }
    });
    // Re-spread all children so nothing piles up
    const compound = cy.$(`#${layerId}`);
    const children = compound.children();
    if (children.length > 0) {
      const NS_INNER = 82;
      const perRow = Math.ceil(Math.sqrt(children.length));
      const bb = compound.boundingBox({ includeLabels: false });
      cy.batch(() => {
        children.forEach((child, i) => {
          child.position({
            x: bb.x1 + 20 + (i % perRow) * NS_INNER,
            y: bb.y1 + 20 + Math.floor(i / perRow) * NS_INNER,
          });
        });
      });
    }
    setAddNodesMode(false);
    setAddNodesPattern("");
  }, [selectedLayer, addNodesPattern]);

  const confirmRemoveNodes = useCallback(() => {
    if (!selectedLayer || !removeNodesPattern.trim()) return;
    const cy = cyRef.current;
    if (!cy) return;
    let regex;
    try { regex = globToRegex(removeNodesPattern.trim()); } catch { return; }
    cy.$(`#${selectedLayer.layerId}`).children().forEach(node => {
      if (regex.test(node.id())) {
        node.move({ parent: null });
        delete nodeParentMapRef.current[node.id()];
      }
    });
    setRemoveNodesMode(false);
    setRemoveNodesPattern("");
  }, [selectedLayer, removeNodesPattern]);

  const moveNodeOut = useCallback(() => {
    if (!nodePopup) return;
    const cy = cyRef.current; if (!cy) return;
    cy.$(`#${nodePopup.nodeId}`).move({ parent: null });
    delete nodeParentMapRef.current[nodePopup.nodeId];
    setNodePopup(null);
  }, [nodePopup]);

  const confirmConnection = useCallback((ruleData) => {
    if (!pendingConnection) return;
    const { sourceLayerId, targetLayerId } = pendingConnection;
    const enforcement = ruleData.enforcement ?? ruleData.rule ?? "denied";
    const color = ruleData.color || ruleColor(enforcement);
    const edgeId = `layer-edge-${Date.now()}`;
    setLayerEdges(prev => [...prev, {
      id: edgeId,
      source: sourceLayerId,
      target: targetLayerId,
      color,
      rule: enforcement,
      enforcement,
      ruleType: ruleData.ruleType ?? "dependency",
      name: ruleData.name ?? "",
      description: ruleData.description ?? "",
      isActive: true,
      createdById: "",
      createdAt: new Date().toISOString(),
    }]);
    setPendingConnection(null);
  }, [pendingConnection]);

  // ── cytoscape wiring ──────────────────────────────────────────────────────

  const handleCyRef = useCallback(cy => {
    cyRef.current = cy;
    if (!cy.__layoutRun) {
      cy.__layoutRun = true;
      setCyReady(true); // signal hydration useEffect; layout runs there or falls back
    }
    if (typeof onNetworkReady === "function") onNetworkReady(cy);
    if (cy.__alHandlers) return;
    cy.__alHandlers = true;

    // Attach wheel listener directly to Cytoscape's own canvas container so
    // the event is captured before Cytoscape can stop propagation.
    const cyCanvas = cy.container();
    if (cyCanvas) {
      cyCanvas.addEventListener('wheel', (e) => {
        e.preventDefault();
        const factor = e.deltaY < 0 ? 1.05 : 1 / 1.05;
        const newZoom = Math.min(Math.max(cy.zoom() * factor, 0.05), 3);
        const { left, top } = cyCanvas.getBoundingClientRect();
        cy.zoom({ level: newZoom, renderedPosition: { x: e.clientX - left, y: e.clientY - top } });
      }, { passive: false });
    }

    cy.on("zoom", () => setZoomLevel(Math.round(cy.zoom() * 100)));

    // track which node the user directly grabbed (not just moved with a parent compound)
    cy.on("grab", "node[!isLayer]", evt => {
      grabbedNodeIdRef.current = evt.target.id();
    });

    // drag node into / out of layer — blocked on published/archived versions
    cy.on("free", "node[!isLayer]", evt => {
      if (versionStatusRef.current !== 'draft') return; // read-only: discard node moves
      const node = evt.target;
      // ignore nodes that moved only because their parent compound was dragged
      if (node.id() !== grabbedNodeIdRef.current) return;
      grabbedNodeIdRef.current = null;

      const pos = node.position();
      const layers = cy.nodes("[?isLayer]");
      let target = null;
      layers.forEach(layer => {
        const bb = layer.boundingBox({});
        if (pos.x >= bb.x1 && pos.x <= bb.x2 && pos.y >= bb.y1 && pos.y <= bb.y2) target = layer;
      });
      const currentParent = node.parent().first();
      if (target) {
        const tid = target.id();
        if (!currentParent.length || currentParent.id() !== tid) {
          node.move({ parent: tid });
          nodeParentMapRef.current[node.id()] = tid;
        }
      } else if (currentParent.length && currentParent.data("isLayer")) {
        node.move({ parent: null });
        delete nodeParentMapRef.current[node.id()];
      }
      // node drag updates nodeParentMapRef directly (mutable ref, React never notified)
      // so we must schedule a save explicitly here
      if (scheduleSaveRef.current) scheduleSaveRef.current();
    });

    // layer drag — position stored in Cytoscape only, not in React state
    cy.on("dragfreeon", "node[?isLayer]", evt => {
      if (versionStatusRef.current !== 'draft') {
        // Published/archived: positions are not saved; re-ungrabify so the node
        // cannot be dragged again (handles nodes added during async hydration).
        evt.target.ungrabify();
        return;
      }
      if (scheduleSaveRef.current) scheduleSaveRef.current();
    });

    // tap child node → show "move out" popup if inside a layer (draft only);
    // in published/archived mode, tapping a layer child just zooms to the node.
    // in community-groups layout, tap free node → open group panel + select node (explore-style flow)
    cy.on("tap", "node[!isLayer]", evt => {
      const node = evt.target;
      const parent = node.parent().first();
      if (parent.length && parent.data("isLayer")) {
        if (versionStatusRef.current !== 'draft') {
          // Read-only: open the layer in the Explorer panel, select the node, then zoom+flash (explore-stage behaviour)
          const nodeId = node.id();
          const layerId = nodeParentMapRef.current[nodeId];
          if (layerId) setLayerPanelSelectedLayer(layerId);
          setLayerPanelSelectedNode(nodeId);
          setLayerPanelHighlightEdges(true);
          setLayerPanelIntraEdges(true);
          setLayerPanelCrossEdges(false);
          setCrossLayerData(null);
          cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: node } }, { duration: 350 });
          setTimeout(() => {
            if (!node.removed()) {
              node.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 });
              setTimeout(() => { if (!node.removed()) node.removeStyle('border-width border-color border-opacity'); }, 1400);
            }
          }, 380);
          return;
        }
        const pos = node.renderedPosition();
        setNodePopup({ nodeId: node.id(), label: node.data("label") || node.id(), pos });
        setSelectedLayer(null);
      } else {
        setNodePopup(null);
        if (activeLayoutRef.current === 'community-groups') {
          const activeMap = Object.keys(mergedCommunityMapRef.current).length > 0
            ? mergedCommunityMapRef.current
            : communityMapRef.current;
          const commNum = activeMap[node.id()];
          if (commNum !== undefined) {
            const nodeId = node.id();
            selectFromCanvasRef.current = nodeId;
            setSelectedComm(commNum);
            // Also set directly so same-community taps work (selectedComm won't change → effect won't fire)
            setSelectedNodeInPanel(nodeId);
            setHighlightEdgesActive(true);
            setShowIntraGroupEdges(true);
            setShowCrossGroupEdges(false);
            setShowNodeEdges(false);
            const cy2 = cyRef.current;
            const isSameComm = selectedCommRef.current === commNum;
            if (cy2) {
              if (isSameComm) {
                // Already viewing this community — pan to the node, then flash
                cy2.animate({ zoom: Math.min(Math.max(cy2.zoom(), 1.2), 2.5), center: { eles: node } }, { duration: 350 });
                setTimeout(() => {
                  if (!node.removed()) {
                    node.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 });
                    setTimeout(() => { if (!node.removed()) node.removeStyle('border-width border-color border-opacity'); }, 1400);
                  }
                }, 380);
              } else {
                // Different community — auto-fit effect handles centering; flash after it completes
                setTimeout(() => {
                  if (!node.removed()) {
                    node.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 });
                    setTimeout(() => { if (!node.removed()) node.removeStyle('border-width border-color border-opacity'); }, 1400);
                  }
                }, 400);
              }
            }
          }
        }
      }
    });

    // tap layer → connect mode OR select + show handles
    cy.on("tap", "node[?isLayer]", evt => {
      const node = evt.target;
      if (connectModeRef.current) {
        // block connecting to/from any unconfirmed suggested layer
        if (suggestedLayerIdsRef.current.has(node.id())) {
          const bb = node.renderedBoundingBox({ includeLabels: false });
          setConnectAlert({ x: (bb.x1 + bb.x2) / 2, y: bb.y1, msg: "Confirm this layer before drawing rules" });
          setTimeout(() => setConnectAlert(null), 2400);
          return;
        }
        const source = connectSourceRef.current;
        if (!source) {
          // pick source layer
          const bb = node.renderedBoundingBox({ includeLabels: false });
          setConnectSource({ layerId: node.id(), color: node.data("color"), cx: (bb.x1 + bb.x2) / 2, cy: (bb.y1 + bb.y2) / 2 });
          setConnectMouse(null);
        } else if (source.layerId !== node.id()) {
          // guard: block the exact same direction, but allow the reverse
          const alreadyConnected = layerEdgesRef.current.some(le =>
            le.source === source.layerId && le.target === node.id()
          );
          if (alreadyConnected) {
            const bb = node.renderedBoundingBox({ includeLabels: false });
            setConnectAlert({ x: (bb.x1 + bb.x2) / 2, y: bb.y1 });
            setTimeout(() => setConnectAlert(null), 2200);
            return;
          }
          // prompt user to define the rule before creating the edge
          const srcName = cy.$(`#${source.layerId}`).data("label") || source.layerId;
          const tgtName = node.data("label") || node.id();
          setPendingConnection({
            sourceLayerId: source.layerId,
            targetLayerId: node.id(),
            sourceName: srcName,
            targetName: tgtName,
            sourceColor: source.color,
            targetColor: node.data("color"),
          });
          setRuleFormEnforcement('denied');
          setRuleFormName(`${srcName} ${RULE_TYPES['denied'].autoVerb} ${tgtName}`);
          setConnectSource(null);
          setConnectMouse(null);
          setConnectSourceBB(null);
        }
        return;
      }
      if (versionStatusRef.current !== 'draft') {
        // Published/archived: open the layer in the Explorer panel instead of edit UI
        setLayerPanelSelectedLayer(node.id());
        setLayerPanelSelectedNode(null);
        setLayerPanelHighlightEdges(false);
        setLayerPanelIntraEdges(false);
        setLayerPanelCrossEdges(false);
        return;
      }
      setSelectedLayer({ layerId: node.id(), color: node.data("color"), name: node.data("label") });
      setPopupEditing(false);
      setNodePopup(null);
      setSelectedLayerEdge(null);
    });

    // tap layer edge → show edit/delete popup
    cy.on("tap", "edge[?isLayerEdge]", evt => {
      const edge = evt.target;
      const bb = edge.renderedBoundingBox();
      setSelectedLayerEdge({
        edgeId: edge.id(),
        color: edge.data("color"),
        rule: edge.data("rule"),
        enforcement: edge.data("enforcement"),
        pos: { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 },
      });
      setSelectedLayer(null);
      setNodePopup(null);
    });

    // tap background → deselect everything
    cy.on("tap", evt => {
      if (evt.target === cy) {
        setSelectedLayer(null); setPopupEditing(false); setNodePopup(null);
        setSelectedLayerEdge(null);
        setAddNodesMode(false); setAddNodesPattern("");
        setRemoveNodesMode(false); setRemoveNodesPattern("");
        // cancel connect source selection (but stay in connect mode)
        setConnectSource(null); setConnectMouse(null); setConnectSourceBB(null);
      }
    });

    cy.on("pan zoom", () => setNodePopup(null));

    const destroyTooltip = () => {
      if (tooltipRef.current) {
        tooltipRef.current.instance.destroy();
        tooltipRef.current.el.remove();
        tooltipRef.current = null;
      }
    };

    cy.on("mouseover", "node[!isLayer]", evt => {
      destroyTooltip();
      const node = evt.target;
      const d = node.data();

      const shortName = d.label || d.id || "";
      const fullLabel = d.fullLabel || "";
      const parts = fullLabel.split(".");
      const pkg = parts.length > 1 ? parts.slice(0, -1).join(".") : "";

      const sdRaw = Number(d.sd ?? d.smellDensity ?? d.smell_density ?? 0);
      const sdValue = Number.isFinite(sdRaw) && sdRaw >= 0 ? sdRaw : 0;
      const sdColor = gradientColor(sdToGradientT(sdValue));

      const locRaw = Number(d.loc);
      const locValue = Number.isFinite(locRaw) && locRaw >= 0 ? locRaw : null;

      const sdLabel = sdValue < 20 ? "Healthy" : sdValue < 35 ? "Moderate" : sdValue < 50 ? "High" : "Critical";

      // Layer / community membership — read live from Cytoscape so it reflects any moves the user made
      const parentNode = node.parent();
      const inLayer = parentNode.length > 0 && parentNode.data('isLayer');
      const layerLabel = inLayer ? (parentNode.data('label') || 'Unnamed Layer') : null;
      const layerColor = inLayer ? (parentNode.data('color') || '#64748b') : null;
      const isSuggested = inLayer && !!parentNode.data('isSuggested');
      const commNum = mergedCommunityMapRef.current[node.id()];
      const isUncategorizedNode = commNum === UNCATEGORIZED_COMM;
      const commColor = (commNum !== undefined && !isUncategorizedNode)
        ? COMMUNITY_COLORS[commNum % COMMUNITY_COLORS.length]
        : null;

      const membershipRow = inLayer
        ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #f1f5f9;display:flex;align-items:center;gap:5px;flex-wrap:wrap">
             <span style="width:8px;height:8px;border-radius:2px;background:${layerColor};flex-shrink:0;display:inline-block"></span>
             <span style="font-size:10px;color:#64748b;font-weight:500">${isSuggested ? 'Suggested layer' : 'Assigned to layer'}:</span>
             <span style="font-size:10px;color:#0f172a;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px">${layerLabel}</span>
           </div>`
        : isUncategorizedNode
          ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #fde68a;display:flex;align-items:center;gap:5px">
             <span style="font-size:11px;flex-shrink:0">⚠️</span>
             <span style="font-size:10px;color:#92400e;font-weight:600;font-style:italic">Uncategorized</span>
             <span style="font-size:10px;color:#b45309">— no group path</span>
           </div>`
          : commColor
            ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid #f1f5f9;display:flex;align-items:center;gap:5px">
             <span style="width:8px;height:8px;border-radius:50%;background:${commColor};flex-shrink:0;display:inline-block"></span>
             <span style="font-size:10px;color:#64748b;font-weight:500">Community group:</span>
             <span style="font-size:10px;color:#0f172a;font-weight:700">Group ${commNum}</span>
           </div>`
            : '';

      const el = document.createElement("div");
      el.style.cssText = [
        "background:#fff",
        "border:1px solid #e2e8f0",
        `border-left:3px solid ${sdColor}`,
        "border-radius:8px",
        "padding:10px 13px 9px",
        "min-width:190px",
        "max-width:290px",
        "pointer-events:none",
        "box-shadow:0 8px 28px rgba(0,0,0,0.11),0 2px 6px rgba(0,0,0,0.06)",
        "z-index:9999",
        "font-family:inherit",
        "transition:opacity 0.1s",
      ].join(";");

      el.innerHTML = `
        <div style="font-weight:700;font-size:13px;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:260px;margin-bottom:${pkg ? "2px" : "8px"}">${shortName}</div>
        ${pkg ? `<div style="font-size:10px;color:#94a3b8;margin-bottom:8px;line-height:1.4;word-break:break-all">${pkg}</div>` : ""}
        <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
          ${locValue !== null ? `<div style="display:inline-flex;align-items:center;gap:4px;background:#f1f5f9;border-radius:5px;padding:3px 8px;font-size:11px;color:#475569;font-weight:500;white-space:nowrap"><span style="font-size:10px;opacity:0.7">&#9998;</span>${locValue}&nbsp;lines</div>` : ""}
          <div style="display:inline-flex;align-items:center;gap:5px;background:${sdColor}15;border:1px solid ${sdColor}35;border-radius:5px;padding:3px 9px;white-space:nowrap">
            <span style="width:7px;height:7px;border-radius:50%;background:${sdColor};flex-shrink:0;display:inline-block"></span>
            <span style="font-size:11px;font-weight:600;color:${sdColor}">${sdValue.toFixed(1)}</span>
            <span style="font-size:10px;color:${sdColor};opacity:0.75">${sdLabel}</span>
          </div>
        </div>
        ${membershipRow}`;
      document.body.appendChild(el);

      const instance = createPopper(node.popperRef(), el, {
        placement: "top",
        modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
      });

      const update = () => instance.update();
      cy.on("pan zoom", update);
      node.on("position", update);

      tooltipRef.current = {
        instance, el,
        cleanup: () => { cy.off("pan zoom", update); node.off("position", update); },
      };
    });

    cy.on("mouseout", "node", destroyTooltip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cy = cyRef.current; if (!cy || !centerNodeId) return;
    const node = cy.$(`#${CSS.escape(centerNodeId)}`);
    if (node.length) { cy.animate({ center: { eles: node }, zoom: 1.4 }, { duration: 450, easing: "ease-in-out" }); cy.$("node").unselect(); node.select(); }
  }, [centerNodeId]);

  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    cy.$("node").unselect();
    cy.animate({ fit: { eles: cy.elements(), padding: 40 } }, { duration: 300 });
  }, [resetViewSignal]);

  // Child componentDidUpdate has reconciled the React-owned elements by this
  // point. Restore coordinates before paint, without creating duplicate nodes.
  useLayoutEffect(() => {
    if (!pendingCanvasRestoreRef.current) return;
    const restore = pendingCanvasRestoreRef.current;
    pendingCanvasRestoreRef.current = null;
    restore();
    setZoomLevel(Math.round(cyRef.current.zoom() * 100));
  }, [layerNodes, layerEdges, collapsedLayers]);

  // hydrate saved canvas once Cytoscape is mounted and we have a definition context
  useEffect(() => {
    if (!cyReady || !cyRef.current || hasHydratedRef.current) return;

    // Compute communities synchronously, then immediately merge with the default threshold
    // so the initial layout clusters match what the Suggestions panel shows.
    // Storing the result in mergedCommunityMapRef makes the layout-switcher consistent too.
    const computeAndLayout = (cy) => {
      computeAndStoreCommunities(cy);
      const merged = mergeCommunities(communityMapRef.current, communityGraphRef.current, 10);
      mergedCommunityMapRef.current = merged;
      runCommunityLayout(cy, merged);
    };

    if (!projectId || !definitionId) {
      computeAndLayout(cyRef.current);
      cyRef.current.zoom(1); cyRef.current.center(); setZoomLevel(100);
      return;
    }
    hasHydratedRef.current = true;
    isHydratingRef.current = true;

    const loader = versionNumber
      ? loadVersion(projectId, definitionId, versionNumber)
      : loadLatestVersion(projectId, definitionId);

    loader
      .then(versionData => {
        computeAndStoreCommunities(cyRef.current);
        const merged = mergeCommunities(communityMapRef.current, communityGraphRef.current, 10);
        mergedCommunityMapRef.current = merged;
        if (versionData?.canvas) {
          // Hydrate whenever the snapshot carries any content. A forked draft can have
          // layers/rules but no saved node positions; hydrateCanvasFromSnapshot draws
          // layers from their own x/y and tolerates empty positions, so gating on
          // positions alone would silently drop a copied layer set.
          const c = versionData.canvas;
          const hasContent =
            (c.layers?.length ?? 0) > 0 ||
            (c.nodePositions?.length ?? 0) > 0 ||
            (c.rules?.length ?? 0) > 0;
          if (hasContent) {
            // This draft already has an architecture (a fork of a published version, or a
            // reopened draft). Open it in Arrange & Define rather than the Suggest onboarding,
            // and stop the initial community compute above from re-locking to Suggest.
            if (versionStatus === 'draft') {
              skipSuggestLockRef.current = true;
              setArrangeUnlocked(true);
              // This isn't a first-time, blank-slate definition — suppress the Suggest/Arrange
              // coaching banners so editing a fork doesn't reopen onboarding popups.
              setDismissedBanners(new Set(['suggest', 'arrange']));
            }
            pendingCanvasRestoreRef.current = hydrateCanvasFromSnapshot(
              cyRef.current, versionData.canvas,
              setLayerNodes, setLayerEdges, setCollapsedLayers,
              nodeParentMapRef.current,
              collapsedNodePositions.current,
              COLLAPSED_W, COLLAPSED_H,
            );
          } else {
            runCommunityLayout(cyRef.current, merged);
            cyRef.current.zoom(1); cyRef.current.center(); setZoomLevel(100);
          }
          if (typeof onVersionLoaded === 'function') onVersionLoaded(versionData);
        } else {
          runCommunityLayout(cyRef.current, merged);
          cyRef.current.zoom(1); cyRef.current.center(); setZoomLevel(100);
          if (typeof onVersionLoaded === 'function') onVersionLoaded(null);
        }
      })
      .catch(() => {
        computeAndLayout(cyRef.current);
        cyRef.current.zoom(1); cyRef.current.center(); setZoomLevel(100);
        if (typeof onVersionLoaded === 'function') onVersionLoaded(null);
      })
      .finally(() => { isHydratingRef.current = false; });
  }, [cyReady, projectId, definitionId, versionNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasGraph) return <div />;

  const isResizing = !!resizingLayer;

  // true when a docked 280px right panel is visible, so Cytoscape reserves that space
  const showRightPanel =
    (activeLayout === "community-groups" && communityMapVersion > 0 && showSuggestionsPanel)
    || ((versionStatus !== 'draft' || workflowStage === 'arrange') && layerNodes.length > 0);

  return (
    <div className="card shadow-sm p-3" style={{ position: "relative" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{title}</h5>
        {headerActions && <div className="d-flex align-items-center gap-2">{headerActions}</div>}
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
          <LoadingSpinner title="Loading graph..." />
        </div>
      ) : (
        <div ref={containerRef} style={{ position: "relative", height: 900, overflow: "hidden" }} onMouseMove={handleContainerMouseMove}>

          {/* ── Stage guidance toasts — centered vertical card with auto-dismiss timer ── */}
          <style>{`
            @keyframes dcode-banner-in { from { opacity:0; transform:translate(-50%,-50%) scale(0.94); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }
            @keyframes dcode-bar-shrink { from { transform:scaleX(1); } to { transform:scaleX(0); } }
          `}</style>
          {/* Guidance banner — hidden for published/archived and in wizard mode */}
          {versionStatus === 'draft' && !drawMode && !connectMode && !wizardStep && !dismissedBanners.has(workflowStage) && (() => {
            const SECS = 7;
            const cfg = {
              suggest: {
                icon: '💡', accent: '#f59e0b', bg: '#fffbeb', step: 'Suggest',
                title: 'Auto-generate layer proposals from community clusters',
                body: `Open the Suggestions panel on the right → inspect each detected group → name it → click "+ Layer" to add it. Click "Continue to Arrange & Define" at the bottom when you're satisfied.`,
              },
              arrange: {
                icon: '🏗', accent: '#0d9488', bg: '#f0fdfa', step: 'Arrange & Define',
                title: 'Set dependency rules between your components',
                body: 'Use ↔ Connect Mode to draw arrows between components. Click each arrow to set its rule — Denied blocks it in CI, Warning flags it for review, Info tracks it silently. When ready, click Publish.',
              },
            };
            const b = cfg[workflowStage];
            if (!b) return null;
            const dismiss = () => setDismissedBanners(prev => new Set([...prev, workflowStage]));
            return (
              <div key={workflowStage}
                onMouseEnter={() => setBannerHovered(true)}
                onMouseLeave={() => setBannerHovered(false)}
                style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  zIndex: 150, pointerEvents: 'auto', width: 340,
                  background: '#fff', borderRadius: 12,
                  borderTop: `4px solid ${b.accent}`,
                  boxShadow: bannerHovered
                    ? '0 16px 48px rgba(0,0,0,0.18),0 4px 14px rgba(0,0,0,0.10)'
                    : '0 8px 32px rgba(0,0,0,0.13),0 2px 8px rgba(0,0,0,0.07)',
                  overflow: 'hidden',
                  animation: 'dcode-banner-in 0.28s ease-out both',
                  transition: 'box-shadow 0.15s ease',
                }}>
                {/* Dismiss × */}
                <button
                  onClick={dismiss}
                  style={{
                    position: 'absolute', top: 10, right: 12,
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: '#94a3b8', lineHeight: 1, padding: 2,
                  }}
                >×</button>

                <div style={{ padding: '20px 20px 16px' }}>
                  {/* Icon badge — centered */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: b.bg, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 22,
                    }}>{b.icon}</div>
                  </div>

                  {/* Step label */}
                  <div style={{
                    textAlign: 'center', fontSize: 10, fontWeight: 700,
                    color: b.accent, textTransform: 'uppercase',
                    letterSpacing: '0.07em', marginBottom: 6,
                  }}>{b.step}</div>

                  {/* Title */}
                  <div style={{
                    textAlign: 'center', fontSize: 14, fontWeight: 700,
                    color: '#0f172a', lineHeight: 1.35, marginBottom: 10,
                  }}>{b.title}</div>

                  {/* Body */}
                  <div style={{
                    textAlign: 'center', fontSize: 12, color: '#64748b',
                    lineHeight: 1.6, marginBottom: 16,
                  }}>{b.body}</div>

                  {/* Got it button — full width */}
                  <button
                    onClick={dismiss}
                    style={{
                      width: '100%', padding: '8px 0', fontSize: 13,
                      fontWeight: 600, cursor: 'pointer',
                      border: `1.5px solid ${b.accent}`,
                      borderRadius: 8, background: b.bg, color: b.accent,
                      lineHeight: 1.4, transition: 'background 0.15s',
                    }}
                  >Got it</button>
                </div>

                {/* Auto-dismiss progress bar — freezes while hovered */}
                <div style={{ height: 3, background: '#f1f5f9' }}>
                  <div style={{
                    height: '100%', background: b.accent,
                    transformOrigin: 'left center',
                    animation: `dcode-bar-shrink ${SECS}s linear forwards`,
                    animationPlayState: bannerHovered ? 'paused' : 'running',
                  }} />
                </div>
              </div>
            );
          })()}

          <CytoscapeComponent
            elements={elements}
            stylesheet={stylesheet}
            style={{ width: showRightPanel ? "calc(100% - 280px)" : "100%", height: "100%" }}
            cy={handleCyRef}
            boxSelectionEnabled={!drawMode}
            autounselectify={false}
            userZoomingEnabled={false}
            minZoom={0.05}
            maxZoom={3}
          />

          {/* ── community group hull overlay — clipped to canvas bounds ── */}
          {commHull && (
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 55 }}>
              <div
                style={{
                  position: 'absolute',
                  left: commHull.x,
                  top: commHull.y,
                  width: commHull.w,
                  height: commHull.h,
                  background: `${commHull.color}0d`,
                  border: `2px dashed ${commHull.color}99`,
                  borderRadius: 14,
                  pointerEvents: 'none',
                  boxShadow: `inset 0 0 60px ${commHull.color}08`,
                }}
              >
                {/* label badge at top-left corner */}
                <div style={{
                  position: 'absolute',
                  top: -13,
                  left: 10,
                  background: '#fff',
                  border: `1.5px solid ${commHull.color}88`,
                  borderRadius: 20,
                  padding: '2px 9px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: commHull.color,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  letterSpacing: '0.01em',
                }}>
                  {commHull.comm === UNCATEGORIZED_COMM ? 'Uncategorized' : `Group ${commHull.comm}`} · {commHull.count} classes
                </div>
              </div>
            </div>
          )}

          {/* ── resize handles + control bar for selected layer — draft only ── */}
          {versionStatus === 'draft' && selectedLayer && handleBB && !drawMode && (
            <>
              {/* dashed selection outline — hidden when collapsed (cytoscape node already has dashed border) */}
              {!collapsedLayers.has(selectedLayer.layerId) && (
                <div style={{ position: "absolute", left: handleBB.x1, top: handleBB.y1, width: handleBB.w, height: handleBB.h, border: `2px dashed ${selectedLayer.color}`, borderRadius: 6, pointerEvents: "none", zIndex: 60 }} />
              )}

              {/* control bar above the layer */}
              <div
                style={{ position: "absolute", left: handleBB.x1, top: Math.max(4, handleBB.y1 - 36), display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1.5px solid ${selectedLayer.color}44`, borderRadius: 7, padding: "3px 6px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 200, pointerEvents: "all" }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
              >
                {/* All edit branches (rename / add / remove) are draft-only */}
                {versionStatus === 'draft' && popupEditing ? (
                  <>
                    <input
                      ref={popupInputRef}
                      value={popupName}
                      onChange={e => setPopupName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") confirmRename(); if (e.key === "Escape") setPopupEditing(false); e.stopPropagation(); }}
                      style={{ padding: "2px 6px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12, outline: "none", width: 110 }}
                    />
                    <button onClick={confirmRename} disabled={!popupName.trim()} style={{ padding: "2px 8px", fontSize: 11, border: "none", borderRadius: 4, background: popupName.trim() ? "#0d9488" : "#94a3b8", color: "#fff", cursor: popupName.trim() ? "pointer" : "not-allowed" }}>Save</button>
                    <button onClick={() => setPopupEditing(false)} style={{ padding: "2px 6px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 4, background: "#f8fafc", cursor: "pointer" }}>✕</button>
                  </>
                ) : versionStatus === 'draft' && removeNodesMode ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <input
                        ref={removeNodesInputRef}
                        value={removeNodesPattern}
                        onChange={e => setRemoveNodesPattern(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") confirmRemoveNodes(); if (e.key === "Escape") { setRemoveNodesMode(false); setRemoveNodesPattern(""); } e.stopPropagation(); }}
                        placeholder="e.g. *.dto.*"
                        style={{ padding: "2px 6px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12, outline: "none", width: 130 }}
                      />
                      <button
                        onClick={confirmRemoveNodes}
                        disabled={!removeNodesPattern.trim() || removeNodesMatchCount === 0}
                        style={{ padding: "2px 8px", fontSize: 11, border: "none", borderRadius: 4, background: (removeNodesPattern.trim() && removeNodesMatchCount > 0) ? "#dc2626" : "#94a3b8", color: "#fff", cursor: (removeNodesPattern.trim() && removeNodesMatchCount > 0) ? "pointer" : "not-allowed" }}
                      >Remove</button>
                      <button onClick={() => { setRemoveNodesMode(false); setRemoveNodesPattern(""); }} style={{ padding: "2px 6px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 4, background: "#f8fafc", cursor: "pointer" }}>✕</button>
                    </div>
                    <div style={{ fontSize: 10, color: removeNodesMatchCount > 0 ? "#dc2626" : "#94a3b8", paddingLeft: 2 }}>
                      {removeNodesPattern.trim() ? `${removeNodesMatchCount} node${removeNodesMatchCount !== 1 ? "s" : ""} will be removed` : "Type a pattern to filter nodes in this layer"}
                    </div>
                  </div>
                ) : versionStatus === 'draft' && addNodesMode ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <input
                        ref={addNodesInputRef}
                        value={addNodesPattern}
                        onChange={e => setAddNodesPattern(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") confirmAddNodes(); if (e.key === "Escape") { setAddNodesMode(false); setAddNodesPattern(""); } e.stopPropagation(); }}
                        placeholder="e.g. service.*"
                        style={{ padding: "2px 6px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12, outline: "none", width: 130 }}
                      />
                      <button
                        onClick={confirmAddNodes}
                        disabled={!addNodesPattern.trim() || addNodesMatchCount === 0}
                        style={{ padding: "2px 8px", fontSize: 11, border: "none", borderRadius: 4, background: (addNodesPattern.trim() && addNodesMatchCount > 0) ? "#16a34a" : "#94a3b8", color: "#fff", cursor: (addNodesPattern.trim() && addNodesMatchCount > 0) ? "pointer" : "not-allowed" }}
                      >Add</button>
                      <button onClick={() => { setAddNodesMode(false); setAddNodesPattern(""); }} style={{ padding: "2px 6px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 4, background: "#f8fafc", cursor: "pointer" }}>✕</button>
                    </div>
                    <div style={{ fontSize: 10, color: addNodesMatchCount > 0 ? "#16a34a" : "#94a3b8", paddingLeft: 2 }}>
                      {addNodesPattern.trim() ? `${addNodesMatchCount} node${addNodesMatchCount !== 1 ? "s" : ""} match` : "Type a pattern to filter nodes"}
                    </div>
                  </div>
                ) : (() => {
                  const isCollapsed = collapsedLayers.has(selectedLayer.layerId);
                  const isSuggestion = suggestedLayerIds.has(selectedLayer.layerId);

                  // ── Suggested layer: confirm / rename / reject ──────────
                  if (isSuggestion) return (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 600, color: selectedLayer.color, paddingRight: 2 }}>
                        {layerNodes.find(l => l.id === selectedLayer.layerId)?.name ?? ""}
                      </span>
                      <span style={{ fontSize: 10, color: "#94a3b8", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4, padding: "1px 6px", fontStyle: "italic", whiteSpace: "nowrap" }}>
                        suggested
                      </span>
                      <button
                        onClick={() => { setPopupName(layerNodes.find(l => l.id === selectedLayer.layerId)?.name ?? ""); setPopupEditing(true); }}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: `1px solid ${selectedLayer.color}44`, borderRadius: 4, background: `${selectedLayer.color}0d`, color: selectedLayer.color, cursor: "pointer" }}
                      ><Pencil size={10} /> Rename</button>
                      <button
                        onClick={() => confirmSuggestedLayer(selectedLayer.layerId)}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 8px", fontSize: 11, border: "1px solid #5eead4", borderRadius: 4, background: "#f0fdfa", color: "#0f766e", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                      >✓ Confirm as layer</button>
                      <button
                        onClick={() => rejectSuggestedLayer(selectedLayer.layerId)}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: "1px solid #fca5a5", borderRadius: 4, background: "#fef2f2", color: "#dc2626", cursor: "pointer" }}
                      ><X size={10} /> Reject</button>
                    </>
                  );

                  // ── Confirmed layer: normal controls (edit actions only in draft) ───────
                  return (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 600, color: selectedLayer.color, paddingRight: 4 }}>
                        {layerNodes.find(l => l.id === selectedLayer.layerId)?.name ?? ""}
                      </span>
                      {versionStatus === 'draft' && (
                        <>
                          <button
                            onClick={() => { setPopupName(layerNodes.find(l => l.id === selectedLayer.layerId)?.name ?? ""); setPopupEditing(true); }}
                            style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: `1px solid ${selectedLayer.color}44`, borderRadius: 4, background: `${selectedLayer.color}0d`, color: selectedLayer.color, cursor: "pointer" }}
                          ><Pencil size={10} /> Rename</button>
                          {!isCollapsed && (
                            <>
                              <button
                                onClick={() => { setAddNodesMode(true); setAddNodesPattern(""); }}
                                style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: "1px solid #bbf7d044", borderRadius: 4, background: "#f0fdf4", color: "#16a34a", cursor: "pointer" }}
                              >+ Add Nodes</button>
                              <button
                                onClick={() => { setRemoveNodesMode(true); setRemoveNodesPattern(""); }}
                                style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: "1px solid #fca5a544", borderRadius: 4, background: "#fff7ed", color: "#ea580c", cursor: "pointer" }}
                              >− Remove Nodes</button>
                            </>
                          )}
                        </>
                      )}
                      <button
                        onClick={toggleCollapseLayer}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: `1px solid ${selectedLayer.color}44`, borderRadius: 4, background: `${selectedLayer.color}0d`, color: selectedLayer.color, cursor: "pointer" }}
                      >{isCollapsed ? "⊞ Expand" : "⊟ Collapse"}</button>
                      {versionStatus === 'draft' && (
                        <button
                          onClick={deleteSelectedLayer}
                          style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", fontSize: 11, border: "1px solid #fca5a544", borderRadius: 4, background: "#fef2f2", color: "#ef4444", cursor: "pointer" }}
                        ><Trash size={10} /> Delete</button>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* 8 resize handles — hidden when collapsed or when version is read-only */}
              {versionStatus === 'draft' && !collapsedLayers.has(selectedLayer.layerId) && RESIZE_HANDLES.map(h => {
                const pos = h.getPos(handleBB);
                return (
                  <div
                    key={h.dir}
                    style={{ position: "absolute", left: pos.left, top: pos.top, width: 14, height: 14, background: "#fff", border: `2.5px solid ${selectedLayer.color}`, borderRadius: 3, cursor: h.cursor, zIndex: 210, pointerEvents: "all", boxShadow: "0 1px 6px rgba(0,0,0,0.25)" }}
                    onMouseDown={e => startLayerResize(h.dir, e)}
                    onClick={e => e.stopPropagation()}
                  />
                );
              })}
            </>
          )}

          {/* ── resize cursor overlay (events handled by document listeners) ── */}
          {isResizing && (
            <div style={{ position: "absolute", inset: 0, zIndex: 800, cursor: HANDLE_CURSORS[resizingLayer.handle], pointerEvents: "all" }} />
          )}

          {/* ── connect mode: source layer highlight ring ── */}
          {connectMode && connectSource && connectSourceBB && (
            <div style={{
              position: "absolute",
              left: connectSourceBB.x1 - 6,
              top: connectSourceBB.y1 - 6,
              width: connectSourceBB.w + 12,
              height: connectSourceBB.h + 12,
              border: `3px solid ${connectSource.color}`,
              borderRadius: 10,
              boxShadow: `0 0 0 4px ${connectSource.color}33, 0 0 18px 4px ${connectSource.color}44`,
              pointerEvents: "none",
              zIndex: 70,
              animation: "layer-pulse 1.2s ease-in-out infinite",
            }} />
          )}

          {/* ── connect mode: rubber-band ── */}
          {connectMode && connectSource && connectMouse && (() => {
            const c = connectSource.color;
            const uid = c.replace("#", "");
            const markId = `rba-${uid}`;
            return (
              <svg style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 160, overflow: "visible" }} width="100%" height="100%">
                <defs>
                  {/* Open chevron arrowhead — stroke only, no fill */}
                  <marker id={markId} markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto" markerUnits="strokeWidth">
                    <path d="M 0 0.5 L 5 4 L 0 7.5" fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </marker>
                </defs>

                {/* Colored line */}
                <line
                  x1={connectSource.cx} y1={connectSource.cy}
                  x2={connectMouse.x} y2={connectMouse.y}
                  stroke={c} strokeWidth="2" strokeLinecap="round"
                  markerEnd={`url(#${markId})`} opacity="0.85"
                />
                {/* White shimmer — glass glint */}
                <line
                  x1={connectSource.cx} y1={connectSource.cy}
                  x2={connectMouse.x} y2={connectMouse.y}
                  stroke="rgba(255,255,255,0.55)" strokeWidth="1" strokeLinecap="round"
                />

                {/* Source dot */}
                <circle cx={connectSource.cx} cy={connectSource.cy} r={5} fill={c} opacity="0.85" />
                <circle cx={connectSource.cx} cy={connectSource.cy} r={2} fill="white" opacity="0.9" />
              </svg>
            );
          })()}

          {/* ── draw overlay ── */}
          <div
            style={{ position: "absolute", inset: 0, zIndex: 100, cursor: drawMode ? "crosshair" : "default", pointerEvents: drawMode ? "all" : "none" }}
            onMouseDown={handleDrawStart}
            onMouseMove={handleDrawMove}
            onMouseUp={handleDrawEnd}
          >
            {isDrawing && drawRect && (
              <div style={{ position: "absolute", left: drawRect.x, top: drawRect.y, width: drawRect.width, height: drawRect.height, border: "2px dashed #3b82f6", borderRadius: 6, background: "rgba(59,130,246,0.06)", pointerEvents: "none" }} />
            )}
          </div>

          {/* keep the drawn rect visible as a dotted outline while naming */}
          {pendingRect && (
            <div style={{ position: "absolute", left: pendingRect.x, top: pendingRect.y, width: pendingRect.width, height: pendingRect.height, border: "2px dashed #3b82f6", borderRadius: 6, background: "rgba(59,130,246,0.04)", pointerEvents: "none", zIndex: 99 }} />
          )}

          {/* ── name input popup — flips above the rect when near the canvas bottom ── */}
          {pendingRect && (() => {
            const POPUP_H = 130;
            const POPUP_W = 210; // matches minWidth below
            const containerW = containerRef.current?.clientWidth ?? 0;
            const containerH = containerRef.current?.clientHeight ?? 900;
            // keep the popup clear of the right-side panel when it is shown
            const usableW = containerW - (showRightPanel ? 280 : 0);
            const popupTop = pendingRect.y + pendingRect.height + 8;
            const fitsBelow = popupTop + POPUP_H <= containerH;
            const top = fitsBelow ? popupTop : Math.max(4, pendingRect.y - POPUP_H - 4);
            const maxLeft = Math.max(4, usableW - POPUP_W - 4);
            const left = Math.min(Math.max(4, pendingRect.x), maxLeft);
            return (
              <div style={{ position: "absolute", left, top, zIndex: 300, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", padding: "10px 12px", minWidth: 210 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>Component name</div>
                <input ref={nameInputRef} value={pendingName} onChange={e => setPendingName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") confirmLayer(); if (e.key === "Escape") cancelPending(); }} placeholder="e.g. Service Component" style={{ width: "100%", padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 5, fontSize: 12, outline: "none", marginBottom: 8, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button onClick={cancelPending} style={{ padding: "4px 10px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 5, background: "#f8fafc", cursor: "pointer" }}>Cancel</button>
                  <button onClick={confirmLayer} disabled={!pendingName.trim()} style={{ padding: "4px 10px", fontSize: 11, border: "none", borderRadius: 5, background: pendingName.trim() ? "#3b82f6" : "#93c5fd", color: "#fff", cursor: pendingName.trim() ? "pointer" : "not-allowed" }}>Add</button>
                </div>
              </div>
            );
          })()}

          {/* ── draw mode hint ── */}
          {drawMode && (
            <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 200, background: "rgba(59,130,246,0.92)", color: "#fff", borderRadius: 6, padding: "4px 14px", fontSize: 12, fontWeight: 500, pointerEvents: "none", whiteSpace: "nowrap" }}>
              Drag to draw a component — nodes inside the rectangle will be assigned to the component automatically
            </div>
          )}

          {/* ── connect mode hint ── */}
          {connectMode && (
            <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 200, background: connectSource ? "rgba(13,148,136,0.95)" : "rgba(8,145,178,0.95)", color: "#fff", borderRadius: 6, padding: "5px 16px", fontSize: 12, fontWeight: 500, pointerEvents: "none", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 10px rgba(13,148,136,0.3)" }}>
              <Link45deg size={14} />
              {connectSource ? "Now click the destination component to connect" : "Click a component to start — then click another to connect"}
            </div>
          )}

          {/* ── already-connected badges (shown while picking a destination) ── */}
          {connectSource && Object.entries(connectedLayerBBs).map(([layerId, bb]) => (
            <div
              key={layerId}
              style={{
                position: "absolute",
                left: (bb.x1 + bb.x2) / 2 - 44,
                top: bb.y1 - 28,
                zIndex: 220,
                background: "#fef9c3",
                border: "1.5px solid #fbbf24",
                borderRadius: 999,
                padding: "2px 9px",
                fontSize: 11,
                fontWeight: 600,
                color: "#92400e",
                display: "flex",
                alignItems: "center",
                gap: 4,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                boxShadow: "0 1px 5px rgba(0,0,0,0.10)",
              }}
            >
              🔗 Linked
            </div>
          ))}

          {/* ── connect-mode alert toast (duplicate or unconfirmed layer) ── */}
          {connectAlert && (
            <div
              style={{
                position: "absolute",
                left: connectAlert.x - 90,
                top: connectAlert.y - 48,
                zIndex: 500,
                background: "#fffbeb",
                border: "1.5px solid #f59e0b",
                borderRadius: 8,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                color: "#92400e",
                display: "flex",
                alignItems: "center",
                gap: 7,
                pointerEvents: "none",
                boxShadow: "0 4px 14px rgba(245,158,11,0.25)",
                animation: "connect-alert-fade 2.4s ease forwards",
                whiteSpace: "nowrap",
              }}
            >
              ⚠️ {connectAlert.msg ?? "Already connected"}
            </div>
          )}

          {/* ── layer-edge popup (edit when draft, read-only when published/archived) ── */}
          {selectedLayerEdge && (
            <div
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              style={{ position: "absolute", left: selectedLayerEdge.pos.x - 60, top: selectedLayerEdge.pos.y - 52, zIndex: 400, background: "#fff", border: `2px solid ${selectedLayerEdge.color}44`, borderRadius: 9, boxShadow: "0 4px 18px rgba(0,0,0,0.14)", padding: "8px 12px", minWidth: 150, display: "flex", flexDirection: "column", gap: 6 }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: selectedLayerEdge.color }} />
                Component connection
                {(() => {
                  const rt = RULE_TYPES[selectedLayerEdge.enforcement] ?? RULE_TYPES.denied; return (
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
                      textTransform: "uppercase", letterSpacing: "0.04em",
                      background: rt.chipBg, color: rt.chipColor, border: `1px solid ${rt.chipBorder}60`,
                    }}>{rt.icon} {rt.label}</span>
                  );
                })()}
              </div>

              {versionStatus === 'draft' ? (
                /* ── Editable (draft only) ── */
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {/* Change-to buttons — all types except current */}
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", marginBottom: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>Change to</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {RULE_TYPE_KEYS.filter(k => k !== selectedLayerEdge.enforcement).map(k => {
                      const rt = RULE_TYPES[k];
                      return (
                        <button
                          key={k}
                          onClick={() => changeLayerEdgeEnforcement(k)}
                          style={{
                            display: "flex", alignItems: "center", gap: 4,
                            padding: "4px 8px", fontSize: 11, fontWeight: 600,
                            border: `1px solid ${rt.chipBorder}80`, borderRadius: 5,
                            background: rt.chipBg, color: rt.chipColor, cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >{rt.icon} {rt.label}</button>
                      );
                    })}
                  </div>
                  <div style={{ width: "100%", height: 1, background: "#f1f5f9", margin: "3px 0" }} />
                  <button
                    onClick={deleteLayerEdge}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 5, background: "#f8fafc", color: "#94a3b8", cursor: "pointer" }}
                  >
                    <Trash size={10} /> Remove connection
                  </button>
                  <button
                    onClick={() => setSelectedLayerEdge(null)}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 5, background: "#f8fafc", color: "#64748b", cursor: "pointer" }}
                  >
                    <X size={10} /> Cancel
                  </button>
                </div>
              ) : (
                /* ── Read-only (published / archived) ── */
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>{versionStatus === 'published' ? <Lock size={11} /> : <Archive size={11} />}</span>
                    {versionStatus === 'published' ? 'Published — read only' : 'Archived — read only'}
                  </div>
                  <button
                    onClick={() => setSelectedLayerEdge(null)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "5px 10px", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 5, background: "#f8fafc", color: "#64748b", cursor: "pointer" }}
                  >
                    <X size={10} /> Close
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── read-only banner ── */}
          {versionStatus && versionStatus !== 'draft' && (
            <div style={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              zIndex: 300, display: "flex", alignItems: "center", gap: 8,
              background: versionStatus === 'published' ? "#0f766e" : "#374151",
              color: "#fff", borderRadius: 8, padding: "6px 16px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.18)", fontSize: 12, fontWeight: 600,
              pointerEvents: "none", userSelect: "none", letterSpacing: "0.02em",
            }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>{versionStatus === 'published' ? <Lock size={15} /> : <Archive size={15} />}</span>
              {versionStatus === 'published' ? 'Published — Read Only' : 'Archived — Read Only'}
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8, marginLeft: 4 }}>
                {versionStatus === 'published' ? 'Fork a new version to edit' : ''}
              </span>
            </div>
          )}

          {/* ── saved toast notification ── */}
          {saveStatus === 'saved' && (
            <div style={{
              position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
              zIndex: 300, display: "flex", alignItems: "center", gap: 8,
              background: "#16a34a", color: "#fff", borderRadius: 8, padding: "8px 16px",
              boxShadow: "0 4px 16px rgba(22,163,74,0.35)", fontSize: 12, fontWeight: 600,
              pointerEvents: "none", userSelect: "none",
              animation: "fadeInDown 0.2s ease",
            }}>
              ✓ Canvas saved
            </div>
          )}
          {saveStatus === 'error' && (
            <div style={{
              position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
              zIndex: 300, display: "flex", alignItems: "center", gap: 8,
              background: "#dc2626", color: "#fff", borderRadius: 8, padding: "8px 16px",
              boxShadow: "0 4px 16px rgba(220,38,38,0.35)", fontSize: 12, fontWeight: 600,
              pointerEvents: "none", userSelect: "none",
              animation: "fadeInDown 0.2s ease",
            }}>
              ✕ Save failed — check connection
            </div>
          )}

          {/* ── floating toolbar ── */}
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "#ffffff", borderRadius: 10, boxShadow: "0 2px 12px rgba(0,0,0,0.12)", padding: "6px 4px", border: "1px solid #e2e8f0" }}>
            <ToolBtn title="Zoom in" onClick={zoomIn}><ZoomIn size={15} /></ToolBtn>
            <div style={{ fontSize: 9, color: "#94a3b8", lineHeight: 1, padding: "2px 0", userSelect: "none" }}>{zoomLevel}%</div>
            <ToolBtn title="Zoom out" onClick={zoomOut}><ZoomOut size={15} /></ToolBtn>
            <ToolBtn title="Fit to screen" onClick={fitView}><ArrowsFullscreen size={14} /></ToolBtn>
            <ToolBtn
              title={showNodeEdges ? "Hide edges" : "Show edges"}
              onClick={() => setShowNodeEdges(v => !v)}
              active={showNodeEdges}
            >{showNodeEdges ? <Eye size={14} /> : <EyeSlash size={14} />}</ToolBtn>
            <ToolBtn
              title={showNavigatePanel ? "Close navigate panel" : "Navigate — search classes & components"}
              onClick={() => setShowNavigatePanel(v => !v)}
              active={showNavigatePanel}
            ><Search size={14} /></ToolBtn>
            <ToolDivider />
            {wizardStep !== 'rules' && (
              <ToolBtn title="Change layout" onClick={() => setLayoutPickerOpen(o => !o)} active={layoutPickerOpen}><Diagram2 size={15} /></ToolBtn>
            )}
            {activeLayout === 'community-groups' && Object.keys(communityMapRef.current).length > 0
              && workflowStage !== 'arrange' && wizardStep !== 'rules' && (
                <>
                  <ToolDivider />
                  <ToolBtn
                    title={showSuggestionsPanel ? 'Hide suggestions panel' : 'Show suggestions panel'}
                    onClick={() => setShowSuggestionsPanel(v => !v)}
                    active={showSuggestionsPanel}
                  ><LayoutSidebarReverse size={14} /></ToolBtn>
                </>
              )}
            {versionStatus === 'draft' && (
              <>
                <ToolDivider />
                <ToolBtn title={drawMode ? "Exit draw mode" : "Draw component"} onClick={toggleDrawMode} active={drawMode}><Square size={14} /></ToolBtn>
              </>
            )}
            {/* Connect button: available in the Arrange & Define stage */}
            {versionStatus === 'draft' && workflowStage === 'arrange' && layerNodes.length >= 2 && (
              <>
                <ToolDivider />
                <ToolBtn
                  title={suggestedLayerIds.size > 0
                    ? `Confirm or reject all ${suggestedLayerIds.size} suggested component${suggestedLayerIds.size !== 1 ? 's' : ''} before defining rules`
                    : (connectMode ? "Exit connect mode" : "Connect components — draw dependency rules")}
                  onClick={toggleConnectMode}
                  active={connectMode}
                  disabled={suggestedLayerIds.size > 0}
                ><Link45deg size={15} /></ToolBtn>
              </>
            )}
            <ToolDivider />
            <ToolBtn title="Export as PNG" onClick={exportPng}><Download size={15} /></ToolBtn>
            {saveStatus === 'saving' && (
              <>
                <ToolDivider />
                <span style={{ fontSize: 9, color: '#94a3b8', padding: '2px 4px', textAlign: 'center', minWidth: 44 }}>Saving…</span>
              </>
            )}
          </div>


          {/* ── layout picker ── */}
          {layoutPickerOpen && wizardStep !== 'rules' && (
            <>
              <div style={{ position: "absolute", inset: 0, zIndex: 199 }} onClick={() => setLayoutPickerOpen(false)} />
              <div style={{ position: "absolute", left: 56, top: "50%", transform: "translateY(-50%)", zIndex: 201, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", border: "1px solid #e2e8f0", padding: "6px", minWidth: 148 }}>
                <div style={{ fontSize: 10, color: "#94a3b8", padding: "2px 8px 6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Layout</div>
                {LAYOUTS.map(l => (
                  <button key={l.name} onClick={() => applyLayout(l.name)} style={{ display: "block", width: "100%", padding: "6px 10px", textAlign: "left", background: activeLayout === l.name ? "#f0fdfa" : "transparent", border: "none", borderRadius: 5, fontSize: 12, color: activeLayout === l.name ? "#0d9488" : "#334155", cursor: "pointer", fontWeight: activeLayout === l.name ? 600 : 400 }}>
                    {l.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ── Navigate panel ── */}
          {showNavigatePanel && (() => {
            const isLayerTab = workflowStage === 'arrange';
            const secondTabKey = isLayerTab ? 'components' : 'groups';
            const secondTabLabel = isLayerTab ? 'Components' : 'Groups';
            const currentQuery = navigateTab === 'nodes' ? nodeQuery : groupQuery;
            const setCurrentQuery = navigateTab === 'nodes' ? setNodeQuery : setGroupQuery;
            return (
              <div
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', left: 66, top: 12, zIndex: 220,
                  background: '#fff', border: '1px solid #e2e8f0',
                  borderRadius: 12, boxShadow: '0 4px 24px rgba(15,23,42,0.13)',
                  width: 262, display: 'flex', flexDirection: 'column',
                  maxHeight: 460, overflow: 'hidden', pointerEvents: 'all',
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px 8px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
                  <Search size={12} style={{ color: '#94a3b8', marginRight: 6, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', flex: 1 }}>Navigate</span>
                  <button
                    onClick={() => setShowNavigatePanel(false)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px 4px', borderRadius: 4, fontSize: 14, lineHeight: 1, transition: 'color 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#475569'}
                    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                  ><X size={14} /></button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', padding: '8px 12px 0', gap: 4, flexShrink: 0 }}>
                  {[{ key: 'nodes', label: 'Classes' }, { key: secondTabKey, label: secondTabLabel }].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setNavigateTab(tab.key)}
                      style={{
                        padding: '5px 14px', fontSize: 11, fontWeight: 600,
                        border: '1px solid transparent', borderRadius: '6px 6px 0 0',
                        cursor: 'pointer', transition: 'all 0.12s',
                        background: navigateTab === tab.key ? '#0d9488' : 'transparent',
                        color: navigateTab === tab.key ? '#fff' : '#64748b',
                        borderBottomColor: navigateTab === tab.key ? '#0d9488' : '#e2e8f0',
                      }}
                      onMouseEnter={e => { if (navigateTab !== tab.key) e.currentTarget.style.background = '#f0fdfa'; }}
                      onMouseLeave={e => { if (navigateTab !== tab.key) e.currentTarget.style.background = 'transparent'; }}
                    >{tab.label}</button>
                  ))}
                  <div style={{ flex: 1, borderBottom: '1px solid #e2e8f0' }} />
                </div>

                {/* Search input */}
                <div style={{ padding: '10px 12px 6px', position: 'relative', flexShrink: 0 }}>
                  <Search size={12} style={{ position: 'absolute', left: 22, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                  <input
                    autoFocus
                    type="text"
                    value={currentQuery}
                    onChange={e => setCurrentQuery(e.target.value)}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Escape') setShowNavigatePanel(false); }}
                    placeholder={navigateTab === 'nodes' ? 'Search classes…' : isLayerTab ? 'Search components…' : 'Search groups…'}
                    style={{
                      width: '100%', padding: '7px 28px 7px 28px', fontSize: 12,
                      border: '1px solid #e2e8f0', borderRadius: 7,
                      outline: 'none', color: '#1e293b', boxSizing: 'border-box',
                      background: '#f8fafc', transition: 'border-color 0.15s',
                    }}
                    onFocus={e => e.target.style.borderColor = '#5eead4'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                  {currentQuery && (
                    <button
                      onClick={() => setCurrentQuery('')}
                      style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, lineHeight: 1, padding: 2 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#475569'}
                      onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                    ><X size={12} /></button>
                  )}
                </div>

                {/* Results */}
                <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 8 }}>

                  {/* ── Classes tab ── */}
                  {navigateTab === 'nodes' && (
                    filteredNavNodes.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#94a3b8', padding: '10px 14px', fontStyle: 'italic' }}>
                        {nodeQuery.trim() ? 'No classes match' : 'Type a class name to search'}
                      </div>
                    ) : filteredNavNodes.map(n => {
                      const label = n.label || n.data?.label || n.id || '';
                      const shortName = label.split('.').pop();
                      const pkg = label.includes('.') ? label.split('.').slice(0, -1).join('.') : '';
                      return (
                        <button
                          key={n.id || n.key}
                          onClick={() => navigateToNode(n.id || n.key)}
                          style={{
                            width: '100%', padding: '7px 14px', display: 'flex', flexDirection: 'column', gap: 1,
                            background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortName}</span>
                          {pkg && <span style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg}</span>}
                        </button>
                      );
                    })
                  )}

                  {/* ── Groups tab (explore / suggest) ── */}
                  {navigateTab === 'groups' && (
                    filteredNavGroups.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#94a3b8', padding: '10px 14px', fontStyle: 'italic' }}>
                        {mergedGroupEntries.length === 0 ? 'Generate clusters to see groups' : 'No groups match'}
                      </div>
                    ) : filteredNavGroups.map(({ commNum, count }) => (
                      <button
                        key={commNum}
                        onClick={() => setSelectedComm(commNum)}
                        style={{
                          width: '100%', padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 9,
                          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: COMMUNITY_COLORS[commNum % COMMUNITY_COLORS.length], flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', flex: 1 }}>Group {commNum}</span>
                        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>{count} classes</span>
                      </button>
                    ))
                  )}

                  {/* ── Layers tab (confirm / rules) ── */}
                  {navigateTab === 'components' && (
                    filteredNavLayers.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#94a3b8', padding: '10px 14px', fontStyle: 'italic' }}>
                        {layerNodes.length === 0 ? 'No components created yet' : 'No components match'}
                      </div>
                    ) : filteredNavLayers.map((layer, idx) => (
                      <button
                        key={layer.id}
                        onClick={() => navigateToLayer(layer.id)}
                        style={{
                          width: '100%', padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 9,
                          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdfa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: LAYER_COLORS[idx % LAYER_COLORS.length], flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{layer.name}</span>
                      </button>
                    ))
                  )}

                </div>
              </div>
            );
          })()}

          {/* ── Layers Explorer panel (published / archived / arrange stage) ── */}
          {(versionStatus !== 'draft' || workflowStage === 'arrange') && layerNodes.length > 0 && (
            <div
              style={{
                position: "absolute", right: 0, top: 0, bottom: 0, width: 280, zIndex: 200,
                background: "#fff",
                borderLeft: layerPanelSelectedLayer
                  ? `2px solid ${(layerNodes.find(l => l.id === layerPanelSelectedLayer)?.color ?? '#94a3b8')}44`
                  : "1px solid #e2e8f0",
                boxShadow: "-2px 0 12px rgba(0,0,0,0.06)",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
                transition: "border-color 0.2s",
              }}
            >

              {/* ── Header ── */}
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "11px 14px 10px",
                  borderBottom: "1px solid #f1f5f9",
                  flexShrink: 0,
                }}
              >
                {layerPanelSelectedLayer ? (
                  /* ── Detail header ── */
                  (() => {
                    const lyr = layerNodes.find(l => l.id === layerPanelSelectedLayer);
                    const lyrColor = lyr?.color ?? '#94a3b8';
                    const nodeCount = rawNodes.filter(n => {
                      const id = n.id ?? n.key ?? n.name ?? "";
                      return nodeParentMapRef.current[id] === layerPanelSelectedLayer;
                    }).length;
                    return (
                      <>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => {
                            setLayerPanelSelectedLayer(null);
                            setLayerPanelSelectedNode(null);
                            setLayerPanelHighlightEdges(false);
                            setLayerPanelIntraEdges(false);
                            setLayerPanelCrossEdges(false);
                            setLayerNodeConnections(null);
                            setCrossLayerData(null);
                          }}
                          title="Back to components"
                          style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b", padding: "6px 8px 6px 4px", fontSize: 16, lineHeight: 1, flexShrink: 0, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >←</button>
                        <span style={{ width: 9, height: 9, borderRadius: "50%", display: "inline-block", flexShrink: 0, background: lyrColor }} />
                        <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", flex: 1, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lyr?.name ?? "Layer"}
                        </span>
                        <span style={{ fontSize: 11, color: "#94a3b8" }}>{nodeCount} classes</span>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => {
                            setLayerPanelSelectedLayer(null);
                            setLayerPanelSelectedNode(null);
                            setLayerPanelHighlightEdges(false);
                            setLayerPanelIntraEdges(false);
                            setLayerPanelCrossEdges(false);
                            setLayerNodeConnections(null);
                            setCrossLayerData(null);
                          }}
                          title="Close"
                          style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                          onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                          onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                        >×</button>
                      </>
                    );
                  })()
                ) : (
                  /* ── List header ── */
                  <>
                    <span style={{ display: "inline-flex", alignItems: "center", color: "#334155" }}><Buildings size={13} /></span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#334155", flex: 1, letterSpacing: "-0.01em" }}>
                      Components
                    </span>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                      {layerNodes.length} layer{layerNodes.length !== 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>

              {/* ── Panel body ── */}
              {layerPanelSelectedLayer ? (
                /* ── Node detail view ── */
                (() => {
                  const lyr = layerNodes.find(l => l.id === layerPanelSelectedLayer);
                  const lyrColor = lyr?.color ?? '#94a3b8';
                  const nodesInLayer = rawNodes.filter(n => {
                    const id = n.id ?? n.key ?? n.name ?? "";
                    return nodeParentMapRef.current[id] === layerPanelSelectedLayer;
                  });
                  return (
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", padding: "7px 14px 3px", flexShrink: 0, lineHeight: 1.5 }}>
                        Click a class to locate it · sorted by smell density ↓
                      </div>
                      {/* Edge filter toggles */}
                      <div style={{ padding: "2px 14px 6px", flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => setLayerPanelIntraEdges(v => !v)}
                          title="Show edges where both endpoints are inside this layer"
                          style={{
                            display: "flex", alignItems: "center", gap: 4,
                            fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 5, cursor: "pointer",
                            border: layerPanelIntraEdges ? "none" : `1px solid ${lyrColor}55`,
                            background: layerPanelIntraEdges ? lyrColor : "transparent",
                            color: layerPanelIntraEdges ? "#fff" : lyrColor,
                            transition: "background 0.15s",
                          }}
                        >
                          {layerPanelIntraEdges ? <EyeSlash size={10} /> : <Eye size={10} />}
                          Within layer
                        </button>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => setLayerPanelCrossEdges(v => !v)}
                          title="Show edges where one endpoint is in this layer and the other is outside"
                          style={{
                            display: "flex", alignItems: "center", gap: 4,
                            fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 5, cursor: "pointer",
                            border: layerPanelCrossEdges ? "none" : "1px solid #94a3b855",
                            background: layerPanelCrossEdges ? "#64748b" : "transparent",
                            color: layerPanelCrossEdges ? "#fff" : "#64748b",
                            transition: "background 0.15s",
                          }}
                        >
                          {layerPanelCrossEdges ? <EyeSlash size={10} /> : <Eye size={10} />}
                          Cross-layer
                        </button>
                      </div>
                      {/* Node list */}
                      <div style={{ overflowY: "auto", flex: 1, padding: "4px 10px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
                        {nodesInLayer.length === 0 ? (
                          <div style={{ padding: "20px 0", textAlign: "center", fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                            No classes in this layer
                          </div>
                        ) : (
                          nodesInLayer
                            .slice()
                            .sort((a, b) => {
                              const sa = Number(a.sd ?? a.smellDensity ?? a.smell_density ?? 0);
                              const sb = Number(b.sd ?? b.smellDensity ?? b.smell_density ?? 0);
                              return sb - sa;
                            })
                            .map(n => {
                              const id = n.id ?? n.key ?? n.name ?? "";
                              const fullLabel = n.label ?? n.name ?? id;
                              const shortName = fullLabel.includes(".") ? fullLabel.split(".").pop() : fullLabel;
                              const sdRaw = Number(n.sd ?? n.smellDensity ?? n.smell_density ?? 0);
                              const sdValue = Number.isFinite(sdRaw) && sdRaw >= 0 ? sdRaw : 0;
                              const locRaw = Number(n.loc);
                              const locValue = Number.isFinite(locRaw) && locRaw >= 0 ? locRaw : null;
                              const sdColor = gradientColor(sdToGradientT(sdValue));
                              const isNodeSel = layerPanelSelectedNode === id;
                              const isEdgeHL = layerPanelHighlightEdges && isNodeSel;
                              return (
                                <div
                                  key={id}
                                  data-layer-panel-node={id}
                                  onClick={() => {
                                    if (isNodeSel) {
                                      setLayerPanelSelectedNode(null);
                                      setLayerPanelHighlightEdges(false);
                                      setLayerPanelIntraEdges(false);
                                      setLayerNodeConnections(null);
                                    } else {
                                      setLayerPanelSelectedNode(id);
                                      setLayerPanelHighlightEdges(true);
                                      setLayerPanelIntraEdges(true);
                                      setLayerPanelCrossEdges(false);
                                      setCrossLayerData(null);
                                      const cy = cyRef.current;
                                      if (!cy) return;
                                      const node = cy.$(`#${CSS.escape(id)}`);
                                      if (node.length) {
                                        const neighbourhood = node.closedNeighborhood();
                                        const bb = neighbourhood.boundingBox({ includeLabels: false });
                                        const container = cy.container();
                                        const PAD = 120;
                                        const naturalZoom = Math.min(
                                          (container.clientWidth - PAD * 2) / (bb.w || 1),
                                          (container.clientHeight - PAD * 2) / (bb.h || 1)
                                        );
                                        const targetZoom = Math.min(Math.max(naturalZoom, 0.65), 2.0);
                                        cy.animate(
                                          { zoom: targetZoom, center: { eles: node } },
                                          { duration: 420, easing: "ease-in-out" }
                                        );
                                      }
                                    }
                                  }}
                                  style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "5px 7px", borderRadius: 6, cursor: "pointer",
                                    background: isNodeSel ? `${lyrColor}12` : "transparent",
                                    border: isNodeSel ? `1px solid ${lyrColor}40` : "1px solid transparent",
                                    transition: "background 0.1s",
                                  }}
                                  onMouseEnter={e => { if (!isNodeSel) e.currentTarget.style.background = "#f8fafc"; }}
                                  onMouseLeave={e => { if (!isNodeSel) e.currentTarget.style.background = "transparent"; }}
                                >
                                  <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: isNodeSel ? lyrColor : "#d1d5db" }} />
                                  <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: isNodeSel ? 600 : 400, color: isNodeSel ? "#1e293b" : "#475569", lineHeight: 1.3, wordBreak: "break-word" }}>
                                    {shortName}
                                  </span>
                                  <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                                    {locValue !== null && (
                                      <span style={{ fontSize: 9, padding: "1px 4px", borderRadius: 3, background: "#f1f5f9", color: "#7c8fa6", fontWeight: 500 }}>
                                        {locValue}L
                                      </span>
                                    )}
                                    <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 8, background: `${sdColor}18`, color: sdColor, border: `1px solid ${sdColor}30` }}>
                                      {sdValue.toFixed(1)}
                                    </span>
                                    {isNodeSel && (
                                      <button
                                        onClick={e => { e.stopPropagation(); setLayerPanelHighlightEdges(v => !v); }}
                                        style={{
                                          fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 5, cursor: "pointer",
                                          border: isEdgeHL ? "none" : `1px solid ${lyrColor}66`,
                                          background: isEdgeHL ? lyrColor : "transparent",
                                          color: isEdgeHL ? "#fff" : lyrColor,
                                        }}
                                      >{isEdgeHL ? "hide" : "edges"}</button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : (
                /* ── Layer list view ── */
                <div style={{ overflowY: "auto", flex: "0 1 auto", minHeight: 0, padding: "4px 0" }}>
                  {layerNodes.map((lyr, idx) => {
                    const lyrColor = lyr.color ?? LAYER_COLORS[idx % LAYER_COLORS.length];
                    const nodeCount = rawNodes.filter(n => {
                      const id = n.id ?? n.key ?? n.name ?? "";
                      return nodeParentMapRef.current[id] === lyr.id;
                    }).length;
                    // find any rules that involve this layer
                    const rulesForLayer = layerEdges.filter(le => le.source === lyr.id || le.target === lyr.id);
                    return (
                      <button
                        key={lyr.id}
                        onClick={() => {
                          setLayerPanelSelectedLayer(lyr.id);
                          setLayerPanelSelectedNode(null);
                          setLayerPanelHighlightEdges(false);
                          setLayerPanelIntraEdges(false);
                          setLayerPanelCrossEdges(false);
                          setLayerNodeConnections(null);
                          setCrossLayerData(null);
                          // animate canvas to fit this layer
                          const cy = cyRef.current;
                          if (cy) {
                            const el = cy.$(`#${CSS.escape(lyr.id)}`);
                            if (el.length) cy.animate({ fit: { eles: el, padding: 60 } }, { duration: 380 });
                          }
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          width: "100%", padding: "9px 14px",
                          background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
                          borderLeft: `3px solid ${lyrColor}`,
                          transition: "background 0.12s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {/* Color dot */}
                        <span style={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, background: lyrColor }} />
                        {/* Layer name */}
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lyr.name}
                        </span>
                        {/* Rule count badges */}
                        {rulesForLayer.length > 0 && (
                          <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                            {['denied', 'warning', 'info'].map(type => {
                              const cnt = rulesForLayer.filter(le => le.enforcement === type).length;
                              if (!cnt) return null;
                              const rt = RULE_TYPES[type];
                              return (
                                <span key={type} style={{
                                  fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
                                  background: rt.chipBg, color: rt.chipColor, border: `1px solid ${rt.chipBorder}60`,
                                }}>{rt.icon} {cnt}</span>
                              );
                            })}
                          </div>
                        )}
                        {/* Node count */}
                        <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>{nodeCount}</span>
                        {/* Chevron */}
                        <span style={{ fontSize: 10, color: "#cbd5e1", flexShrink: 0 }}>›</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* ── Layer Connections — docked at the bottom of the Layers panel.
                    Hidden while drilled into a single layer so the (potentially
                    large) node detail list gets the full panel height. ── */}
              {workflowStage === 'arrange' && !layerPanelSelectedLayer && (
                <div style={{ borderTop: "1px solid #f1f5f9", padding: "10px 14px", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
                    Component Connections
                  </div>
                  {layerEdges.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 150, overflowY: "auto" }}>
                      {layerEdges.map(le => {
                        const srcName = layerNodes.find(l => l.id === le.source)?.name ?? le.source;
                        const tgtName = layerNodes.find(l => l.id === le.target)?.name ?? le.target;
                        return (
                          <div key={le.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, flexWrap: "wrap" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: le.color, display: "inline-block" }} />
                            <span style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 72 }} title={srcName}>{srcName}</span>
                            <span style={{ color: le.color, fontWeight: 700, fontSize: 14, flexShrink: 0 }}>→</span>
                            <span style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 72 }} title={tgtName}>{tgtName}</span>
                            {(() => {
                              const rt = RULE_TYPES[le.enforcement] ?? RULE_TYPES.denied; return (
                                <span style={{
                                  flexShrink: 0, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  background: rt.chipBg, color: rt.chipColor, border: `1px solid ${rt.chipBorder}60`,
                                }}>{rt.icon} {rt.label}</span>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5, paddingBottom: 4 }}>
                      No connections yet — use Connect Mode to draw arrows between components.
                    </div>
                  )}
                  {versionStatus === 'draft' && onPublish && (
                    <button
                      onClick={onPublish}
                      style={{
                        marginTop: 10, width: "100%", display: "flex", alignItems: "center",
                        justifyContent: "center", gap: 6,
                        background: "#0d9488", color: "#fff", border: "none", borderRadius: 7,
                        padding: "8px 0", fontSize: 12, fontWeight: 600, cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#0f766e"}
                      onMouseLeave={e => e.currentTarget.style.background = "#0d9488"}
                    >
                      ✓ Publish version
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Layer Explorer: Node Connections panel ── */}
          {(versionStatus !== 'draft' || workflowStage === 'arrange') && layerPanelSelectedNode && layerNodeConnections &&
            (layerNodeConnections.outgoing.length > 0 || layerNodeConnections.incoming.length > 0) && (() => {
              const lyr = layerNodes.find(l => l.id === layerPanelSelectedLayer);
              const connColor = lyr?.color ?? "#94a3b8";
              const selRaw = rawNodes.find(n => (n.id ?? n.key ?? n.name ?? "") === layerPanelSelectedNode);
              const selShort = (selRaw?.label ?? selRaw?.name ?? layerPanelSelectedNode).split('.').pop();
              const panelPos = versionStatus === 'draft'
                ? { top: 12, right: 288 }
                : { bottom: 12, right: 288 };
              const flashNode = (id) => {
                const cy = cyRef.current; if (!cy) return;
                const n = cy.$(`#${CSS.escape(id)}`);
                if (n.length) {
                  cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 2.0), 3.5), center: { eles: n } }, { duration: 350 });
                  setTimeout(() => {
                    if (!n.removed()) {
                      // z-index raises the node above edges and other nodes in Cytoscape's render order
                      n.style({ 'border-width': 6, 'border-color': '#f59e0b', 'border-opacity': 1, opacity: 1, 'z-index': 9999 });
                      setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity opacity z-index'); }, 1600);
                    }
                  }, 380);
                }
              };
              return (
                <div style={{ position: "absolute", inset: 0, zIndex: 201, pointerEvents: "none", overflow: "hidden" }}>
                  <div style={{
                    position: "absolute", pointerEvents: "all",
                    ...panelPos,
                    width: 232, maxHeight: 500,
                    background: "#fff",
                    border: `1.5px solid ${connColor}44`,
                    borderRadius: 14,
                    boxShadow: "0 4px 24px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.04)",
                    display: "flex", flexDirection: "column",
                    overflow: "hidden",
                  }}>
                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 14px 10px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: connColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {selShort}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>
                        Connections
                      </span>
                      <button
                        onClick={() => { setLayerPanelSelectedNode(null); setLayerPanelHighlightEdges(false); setLayerNodeConnections(null); }}
                        title="Close"
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                      >×</button>
                    </div>
                    {/* Body */}
                    <div style={{ overflowY: "auto", flex: 1, padding: "8px 12px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {layerNodeConnections.outgoing.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                            Calls → <span style={{ fontWeight: 400 }}>({layerNodeConnections.outgoing.length})</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {layerNodeConnections.outgoing.map(conn => (
                              <div
                                key={conn.id}
                                onClick={() => flashNode(conn.id)}
                                style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", padding: "3px 4px", borderRadius: 4, position: "relative" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: conn.isInLayer ? connColor : (conn.layerColor ?? "#94a3b8") }} />
                                <span style={{ flex: 1, fontSize: 12, color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word" }}>{conn.shortName}</span>
                                {!conn.isInLayer && conn.layerName && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#f1f5f9", color: "#64748b", whiteSpace: "nowrap", flexShrink: 0 }}>
                                    {conn.layerName}
                                  </span>
                                )}
                                <button
                                  title="Jump to node"
                                  onClick={e => { e.stopPropagation(); flashNode(conn.id); setLayerPanelSelectedNode(conn.id); setLayerPanelHighlightEdges(true); }}
                                  style={{ opacity: 0.55, border: "none", background: "none", cursor: "pointer", padding: "2px 4px", borderRadius: 3, fontSize: 11, color: "#0d9488", fontWeight: 700, flexShrink: 0, lineHeight: 1 }}
                                >⊙</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {layerNodeConnections.incoming.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                            ← Called by <span style={{ fontWeight: 400 }}>({layerNodeConnections.incoming.length})</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {layerNodeConnections.incoming.map(conn => (
                              <div
                                key={conn.id}
                                onClick={() => flashNode(conn.id)}
                                style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", padding: "3px 4px", borderRadius: 4, position: "relative" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: conn.isInLayer ? connColor : (conn.layerColor ?? "#94a3b8") }} />
                                <span style={{ flex: 1, fontSize: 12, color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word" }}>{conn.shortName}</span>
                                {!conn.isInLayer && conn.layerName && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#f1f5f9", color: "#64748b", whiteSpace: "nowrap", flexShrink: 0 }}>
                                    {conn.layerName}
                                  </span>
                                )}
                                <button
                                  title="Jump to node"
                                  onClick={e => { e.stopPropagation(); flashNode(conn.id); setLayerPanelSelectedNode(conn.id); setLayerPanelHighlightEdges(true); }}
                                  style={{ opacity: 0.55, border: "none", background: "none", cursor: "pointer", padding: "2px 4px", borderRadius: 3, fontSize: 11, color: "#0d9488", fontWeight: 700, flexShrink: 0, lineHeight: 1 }}
                                >⊙</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── Layer Explorer: Cross-layer connections panel ── */}
          {(versionStatus !== 'draft' || workflowStage === 'arrange') && layerPanelCrossEdges && crossLayerData && layerPanelSelectedLayer && (() => {
            const lyr = layerNodes.find(l => l.id === layerPanelSelectedLayer);
            const lyrColor = lyr?.color ?? "#94a3b8";
            const hasConnPanel = layerPanelSelectedNode && layerNodeConnections &&
              (layerNodeConnections.outgoing.length > 0 || layerNodeConnections.incoming.length > 0);
            const panelWidth = 264;
            const panelPos = versionStatus === 'draft'
              ? { top: 12, right: 288 + (hasConnPanel ? 240 : 0) }
              : { bottom: 12, right: 288 + (hasConnPanel ? 240 : 0) };
            const sortedGroups = [...crossLayerData.entries()].sort((a, b) => b[1].conns.length - a[1].conns.length);
            const flashNode = (id) => {
              const cy = cyRef.current; if (!cy) return;
              const n = cy.$(`#${CSS.escape(id)}`);
              if (n.length) {
                cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 2.0), 3.5), center: { eles: n } }, { duration: 350 });
                setTimeout(() => {
                  if (!n.removed()) {
                    // z-index raises the node above edges and other nodes in Cytoscape's render order
                    n.style({ 'border-width': 6, 'border-color': '#f59e0b', 'border-opacity': 1, opacity: 1, 'z-index': 9999 });
                    setTimeout(() => {
                      if (!n.removed()) n.removeStyle('border-width border-color border-opacity opacity z-index');
                    }, 1600);
                  }
                }, 380);
              }
            };
            return (
              <div style={{ position: "absolute", inset: 0, zIndex: 201, pointerEvents: "none", overflow: "hidden" }}>
                <div style={{
                  position: "absolute", pointerEvents: "all",
                  ...panelPos,
                  width: panelWidth, maxHeight: 520,
                  background: "#fff",
                  border: `1.5px solid ${lyrColor}44`,
                  borderRadius: 14,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.04)",
                  display: "flex", flexDirection: "column",
                  overflow: "hidden",
                }}>
                  {/* Header */}
                  <div style={{ padding: "11px 14px 9px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: lyrColor, flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: 12, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {lyr?.name ?? "Layer"}
                      </span>
                      <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500, flexShrink: 0 }}>cross-layer</span>
                      <button
                        onClick={() => { setLayerPanelCrossEdges(false); setCrossLayerData(null); }}
                        title="Close"
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                      >×</button>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
                      {sortedGroups.length} connected layer{sortedGroups.length !== 1 ? "s" : ""} · {[...crossLayerData.values()].reduce((s, v) => s + v.conns.length, 0)} edges
                    </div>
                  </div>
                  {/* Scrollable layer list */}
                  <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
                    {sortedGroups.map(([extLayerId, { layerName, layerColor, conns }]) => {
                      const isExpanded = crossLayerExpandedGrp === extLayerId;
                      const outConns = conns.filter(c => c.direction === 'out');
                      const inConns = conns.filter(c => c.direction === 'in');
                      return (
                        <div key={extLayerId}>
                          {/* Layer row header */}
                          <div
                            onClick={() => setCrossLayerExpandedGrp(isExpanded ? null : extLayerId)}
                            style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 14px", cursor: "pointer", userSelect: "none" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: layerColor, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{layerName}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 10, background: `${layerColor}22`, color: layerColor, flexShrink: 0 }}>
                              {conns.length}
                            </span>
                            <span style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                          </div>
                          {/* Expanded: show individual node pairs */}
                          {isExpanded && (
                            <div style={{ padding: "2px 12px 8px 28px", display: "flex", flexDirection: "column", gap: 2 }}>
                              {outConns.length > 0 && (
                                <>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 4, marginBottom: 2 }}>
                                    Calls → <span style={{ fontWeight: 400 }}>({outConns.length})</span>
                                  </div>
                                  {outConns.map((c, i) => (
                                    <div
                                      key={i}
                                      onClick={e => { e.stopPropagation(); flashNode(c.externalId); }}
                                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 4px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}
                                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    >
                                      <span style={{ color: "#475569", flex: "0 0 auto", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.internalName}</span>
                                      <span style={{ color: "#0d9488", fontWeight: 700, flexShrink: 0 }}>→</span>
                                      <span style={{ color: layerColor, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.externalName}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                              {inConns.length > 0 && (
                                <>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: outConns.length > 0 ? 6 : 4, marginBottom: 2 }}>
                                    ← Called by <span style={{ fontWeight: 400 }}>({inConns.length})</span>
                                  </div>
                                  {inConns.map((c, i) => (
                                    <div
                                      key={i}
                                      onClick={e => { e.stopPropagation(); flashNode(c.externalId); }}
                                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 4px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}
                                      onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    >
                                      <span style={{ color: layerColor, fontWeight: 600, flex: "0 0 auto", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.externalName}</span>
                                      <span style={{ color: "#0d9488", fontWeight: 700, flexShrink: 0 }}>→</span>
                                      <span style={{ color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.internalName}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Suggested Groups panel — draft only; published/archived versions never show suggestions ── */}
          {versionStatus === 'draft' && activeLayout === "community-groups" && Object.keys(communityMapRef.current).length > 0 && showSuggestionsPanel && (
            <>
              <div
                style={{
                  position: "absolute", right: 0, top: 0, bottom: 0, width: 280, zIndex: 200,
                  background: "#fff",
                  borderLeft: selectedComm !== null
                    ? `2px solid ${selectedComm === UNCATEGORIZED_COMM ? '#94a3b8' : COMMUNITY_COLORS[selectedComm % COMMUNITY_COLORS.length]}44`
                    : "1px solid #e2e8f0",
                  boxShadow: "-2px 0 12px rgba(0,0,0,0.06)",
                  display: "flex", flexDirection: "column",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                }}
              >

                {/* ── Header ── */}
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "11px 14px 10px",
                    borderBottom: "1px solid #f1f5f9",
                    flexShrink: 0,
                  }}
                >
                  {selectedComm !== null ? (
                    <>
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => { setSelectedComm(null); setSelectedNodeInPanel(null); setHighlightEdgesActive(false); }}
                        title="Back to suggestions"
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#64748b", padding: "6px 8px 6px 4px", fontSize: 16, lineHeight: 1, flexShrink: 0, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >←</button>
                      <span style={{
                        width: 9, height: 9, borderRadius: "50%", display: "inline-block", flexShrink: 0,
                        background: selectedComm === UNCATEGORIZED_COMM
                          ? "#94a3b8"
                          : COMMUNITY_COLORS[selectedComm % COMMUNITY_COLORS.length],
                      }} />
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", flex: 1, letterSpacing: "-0.01em" }}>
                        {selectedComm === UNCATEGORIZED_COMM ? "Uncategorized" : `Group ${selectedComm}`}
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {selectedComm === UNCATEGORIZED_COMM
                          ? uncategorizedCount
                          : (mergedGroupEntries.find(([c]) => Number(c) === selectedComm)?.[1]?.length ?? 0)
                        } classes
                      </span>
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => { setSelectedComm(null); setSelectedNodeInPanel(null); setHighlightEdgesActive(false); setShowIntraGroupEdges(false); setShowCrossGroupEdges(false); }}
                        title="Close"
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                      >×</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#334155", flex: 1, letterSpacing: "-0.01em" }}>
                        Suggestions
                      </span>
                      <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400, marginRight: 6, display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap" }}>
                        {mergedGroupEntries.length} group{mergedGroupEntries.length !== 1 ? "s" : ""}
                        {uncategorizedCount > 0 && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 3,
                            background: "#fffbeb", border: "1px solid #fcd34d",
                            borderRadius: 20, padding: "1px 7px",
                            fontSize: 10, fontWeight: 700, color: "#92400e",
                            whiteSpace: "nowrap",
                          }}>
                            ⚠ {uncategorizedCount} unclassified
                          </span>
                        )}
                      </span>
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => setShowSuggestionsPanel(false)}
                        title="Close suggestions panel"
                        style={{
                          border: "none", cursor: "pointer", padding: "3px 4px", borderRadius: 5,
                          fontSize: 13, lineHeight: 1, display: "flex", alignItems: "center",
                          background: "transparent", color: "#cbd5e1",
                          transition: "color 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#64748b'}
                        onMouseLeave={e => e.currentTarget.style.color = '#cbd5e1'}
                      ><X size={14} /></button>
                    </>
                  )}
                </div>

                {/* ── Panel body: group list OR node detail ── */}
                {selectedComm !== null ? (
                  /* ── Node detail view ── */
                  (() => {
                    // Safe color for the inspected group — slate-gray for Uncategorized
                    const inspectColor = selectedComm === UNCATEGORIZED_COMM
                      ? "#94a3b8"
                      : COMMUNITY_COLORS[selectedComm % COMMUNITY_COLORS.length];
                    // Node list — either the uncategorized set or the regular group
                    const inspectNodes = selectedComm === UNCATEGORIZED_COMM
                      ? rawNodes.filter(n => {
                        const id = n.id ?? n.key ?? n.name ?? "";
                        return mergedMapForPanel[id] === UNCATEGORIZED_COMM;
                      })
                      : (mergedGroupEntries.find(([c]) => Number(c) === selectedComm)?.[1] ?? []);
                    return (
                      <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                        <div style={{ fontSize: 10, color: "#94a3b8", padding: "7px 14px 3px", flexShrink: 0, lineHeight: 1.5 }}>
                          Click a class to locate it · sorted by smell density ↓
                        </div>
                        {/* Edge filter toggles — always visible inside group panel */}
                        <div style={{ padding: "2px 14px 6px", flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
                          {/* Within-group edges */}
                          <button
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => { setShowNodeEdges(false); setShowIntraGroupEdges(v => !v); }}
                            title="Show edges where both endpoints are inside this group"
                            style={{
                              display: "flex", alignItems: "center", gap: 4,
                              fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 5, cursor: "pointer",
                              border: showIntraGroupEdges ? "none" : `1px solid ${inspectColor}55`,
                              background: showIntraGroupEdges ? inspectColor : "transparent",
                              color: showIntraGroupEdges ? "#fff" : inspectColor,
                              transition: "background 0.15s",
                            }}
                          >
                            {showIntraGroupEdges ? <EyeSlash size={10} /> : <Eye size={10} />}
                            Within group
                          </button>
                          {/* Cross-group edges */}
                          <button
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => { setShowNodeEdges(false); setShowCrossGroupEdges(v => !v); }}
                            title="Show edges where one endpoint is in this group and the other is in a different group"
                            style={{
                              display: "flex", alignItems: "center", gap: 4,
                              fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 5, cursor: "pointer",
                              border: showCrossGroupEdges ? "none" : "1px solid #94a3b855",
                              background: showCrossGroupEdges ? "#64748b" : "transparent",
                              color: showCrossGroupEdges ? "#fff" : "#64748b",
                              transition: "background 0.15s",
                            }}
                          >
                            {showCrossGroupEdges ? <EyeSlash size={10} /> : <Eye size={10} />}
                            Cross-group
                          </button>
                        </div>
                        <div style={{ overflowY: "auto", flex: 1, padding: "4px 10px 10px", display: "flex", flexDirection: "column", gap: 3 }}>
                          {inspectNodes
                            .slice()
                            .sort((a, b) => {
                              const sa = Number(a.sd ?? a.smellDensity ?? a.smell_density ?? 0);
                              const sb = Number(b.sd ?? b.smellDensity ?? b.smell_density ?? 0);
                              return sb - sa;
                            })
                            .map(n => {
                              const id = n.id ?? n.key ?? n.name ?? "";
                              const fullLabel = n.label ?? n.name ?? id;
                              const shortName = fullLabel.includes(".") ? fullLabel.split(".").pop() : fullLabel;
                              const sdRaw = Number(n.sd ?? n.smellDensity ?? n.smell_density ?? 0);
                              const sdValue = Number.isFinite(sdRaw) && sdRaw >= 0 ? sdRaw : 0;
                              const locRaw = Number(n.loc);
                              const locValue = Number.isFinite(locRaw) && locRaw >= 0 ? locRaw : null;
                              const sdColor = gradientColor(sdToGradientT(sdValue));
                              const commColor = inspectColor; // already safe for UNCATEGORIZED_COMM
                              const isNodeSel = selectedNodeInPanel === id;
                              const isEdgeHL = highlightEdgesActive && isNodeSel;
                              return (
                                <React.Fragment key={id}>
                                  <div
                                    data-panel-node={id}
                                    onClick={() => {
                                      if (isNodeSel) { setSelectedNodeInPanel(null); setHighlightEdgesActive(false); setShowIntraGroupEdges(false); }
                                      else {
                                        setSelectedNodeInPanel(id); setHighlightEdgesActive(true);
                                        setShowNodeEdges(false); setShowIntraGroupEdges(true); setShowCrossGroupEdges(false);
                                        const cy = cyRef.current;
                                        if (!cy) return;
                                        const node = cy.$(`#${CSS.escape(id)}`);
                                        if (node.length) {
                                          const neighbourhood = node.closedNeighborhood();
                                          const bb = neighbourhood.boundingBox({ includeLabels: false });
                                          const container = cy.container();
                                          const PAD = 120;
                                          const naturalZoom = Math.min(
                                            (container.clientWidth - PAD * 2) / (bb.w || 1),
                                            (container.clientHeight - PAD * 2) / (bb.h || 1)
                                          );
                                          // dynamic: use the natural fit zoom if it's readable, but
                                          // enforce a floor (0.65) so spread-out graphs still centre visibly,
                                          // and a ceiling (2.0) so isolated nodes don't over-zoom
                                          const targetZoom = Math.min(Math.max(naturalZoom, 0.65), 2.0);
                                          cy.animate(
                                            { zoom: targetZoom, center: { eles: node } },
                                            { duration: 420, easing: "ease-in-out" }
                                          );
                                        }
                                      }
                                    }}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 6,
                                      padding: "5px 7px", borderRadius: 6, cursor: "pointer",
                                      background: isNodeSel ? `${commColor}12` : "transparent",
                                      border: isNodeSel ? `1px solid ${commColor}40` : "1px solid transparent",
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={e => { if (!isNodeSel) e.currentTarget.style.background = "#f8fafc"; }}
                                    onMouseLeave={e => { if (!isNodeSel) e.currentTarget.style.background = "transparent"; }}
                                  >
                                    <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: isNodeSel ? commColor : "#d1d5db" }} />
                                    <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: isNodeSel ? 600 : 400, color: isNodeSel ? "#1e293b" : "#475569", lineHeight: 1.3, wordBreak: "break-word" }}>
                                      {shortName}
                                    </span>
                                    <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                                      {locValue !== null && (
                                        <span style={{ fontSize: 9, padding: "1px 4px", borderRadius: 3, background: "#f1f5f9", color: "#7c8fa6", fontWeight: 500 }}>
                                          {locValue}L
                                        </span>
                                      )}
                                      <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 8, background: `${sdColor}18`, color: sdColor, border: `1px solid ${sdColor}30` }}>
                                        {sdValue.toFixed(1)}
                                      </span>
                                      {isNodeSel && (
                                        <button
                                          onClick={e => { e.stopPropagation(); setHighlightEdgesActive(v => !v); }}
                                          style={{
                                            fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 5, cursor: "pointer",
                                            border: isEdgeHL ? "none" : `1px solid ${commColor}66`,
                                            background: isEdgeHL ? commColor : "transparent",
                                            color: isEdgeHL ? "#fff" : commColor,
                                          }}
                                        >{isEdgeHL ? "hide" : "edges"}</button>
                                      )}
                                    </div>
                                  </div>

                                </React.Fragment>
                              );
                            })}
                        </div>

                        {/* Convert-to-layer action from detail view — not applicable for Uncategorized */}
                        {versionStatus === 'draft' && selectedComm !== UNCATEGORIZED_COMM && !convertedCommNums.has(selectedComm) && (
                          <div style={{ borderTop: "1px solid #f1f5f9", padding: "8px 12px", flexShrink: 0 }}>
                            <button
                              onClick={() => { setCreateLayerPrompt({ commNum: selectedComm, map: mergedMapForPanel, inputValue: '', closeInspect: true }); }}
                              style={{ width: "100%", padding: "7px 0", background: "#f0fdfa", color: "#0f766e", border: "1px solid #5eead4", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                            >
                              + Use Group {selectedComm} as layer
                            </button>
                          </div>
                        )}
                      </div>
                    ); // closes return(
                  })() /* closes IIFE */
                ) : (
                  /* ── Group list view ── */
                  <>
                    {/* ── Layer conflict notices ── */}
                    {layerSplitSuggestions.length > 0 && (
                      <div style={{ borderBottom: "1px solid #fde68a", background: "#fffbeb", padding: "6px 10px", flexShrink: 0 }}>
                        <div
                          onClick={() => setConflictsCollapsed(v => !v)}
                          style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: conflictsCollapsed ? 0 : 5 }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", flex: 1 }}>
                            Layer conflicts
                          </span>
                          <span style={{ fontSize: 10, color: "#92400e", marginRight: 5 }}>
                            {layerSplitSuggestions.length}
                          </span>
                          <span style={{ fontSize: 10, color: "#b45309", lineHeight: 1 }}>
                            {conflictsCollapsed ? "▸" : "▾"}
                          </span>
                        </div>
                        {!conflictsCollapsed && layerSplitSuggestions.map(split => (
                          <div key={split.layerId} style={{ marginBottom: 6, padding: "7px 8px", background: "#fff", borderRadius: 6, border: "1px solid #fde68a" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: split.layerColor, flexShrink: 0 }} />
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {split.layerName}
                              </span>
                              <span style={{ fontSize: 10, color: "#92400e", flexShrink: 0 }}>
                                {split.subGroups.length} sub-groups
                              </span>
                            </div>
                            <div style={{ fontSize: 10, color: "#78716c", marginBottom: 6, lineHeight: 1.5 }}>
                              Nodes now belong to {split.subGroups.length} communities at min&nbsp;{appliedMinNodes}.
                              Move them into separate suggested components?
                            </div>
                            <div style={{ display: "flex", gap: 5 }}>
                              <button
                                onMouseDown={e => e.stopPropagation()}
                                onClick={() => redistributeLayer(split.layerId, split.subGroups)}
                                style={{ flex: 1, padding: "4px 0", background: "#0d9488", color: "#fff", border: "none", borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "background 0.15s" }}
                                onMouseEnter={e => e.currentTarget.style.background = '#0f766e'}
                                onMouseLeave={e => e.currentTarget.style.background = '#0d9488'}
                              >Redistribute</button>
                              <button
                                onMouseDown={e => e.stopPropagation()}
                                onClick={() => setLayerSplitSuggestions(prev => prev.filter(s => s.layerId !== split.layerId))}
                                style={{ padding: "4px 10px", background: "transparent", color: "#94a3b8", border: "1px solid #e2e8f0", borderRadius: 5, fontSize: 10, cursor: "pointer" }}
                              >Keep</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {mergedGroupEntries.length === 0 ? (
                      <div style={{ padding: "28px 16px", fontSize: 12, color: "#94a3b8", textAlign: "center", lineHeight: 1.7 }}>
                        No groups meet the minimum size.
                        <br />
                        <span style={{ fontSize: 11 }}>Lower the threshold with ⚙ above.</span>
                      </div>
                    ) : (
                      <div style={{ overflowY: "auto", flex: "0 1 auto", maxHeight: "calc(100% - 240px)", padding: "4px 0" }}>
                        {/* ── Uncategorized layer card — shown above the group list when present ── */}
                        {(() => {
                          const uncatLayer = layerNodes.find(l => l.isUncategorized && suggestedLayerIds.has(l.id));
                          if (!uncatLayer) return null;
                          const childCount = Object.entries(nodeParentMapRef.current)
                            .filter(([, pid]) => pid === uncatLayer.id).length;
                          return (
                            <div style={{ margin: "0 0 4px", borderBottom: "1px dashed #e2e8f0" }}>
                              {/* Header row */}
                              <div
                                style={{
                                  display: "flex", alignItems: "center",
                                  padding: "8px 14px 4px",
                                  background: "#fafbfd",
                                  borderLeft: "3px solid #94a3b8",
                                }}
                              >
                                <span style={{ fontSize: 13, marginRight: 7, lineHeight: 1 }}>⚠️</span>
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#64748b", fontStyle: "italic" }}>
                                  Uncategorized
                                </span>
                                <span style={{ fontSize: 11, color: "#b4bfcc", marginRight: 8, flexShrink: 0 }}>
                                  {childCount} class{childCount !== 1 ? "es" : ""}
                                </span>
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button
                                    onClick={e => { e.stopPropagation(); confirmSuggestedLayer(uncatLayer.id); }}
                                    title="Keep as a permanent component"
                                    style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 5, cursor: "pointer", border: "1px solid #5eead4", background: "#f0fdfa", color: "#0f766e", whiteSpace: "nowrap" }}
                                  >✓ Keep</button>
                                  <button
                                    onClick={e => { e.stopPropagation(); rejectSuggestedLayer(uncatLayer.id); }}
                                    title="Discard — nodes return to the canvas"
                                    style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 5, cursor: "pointer", border: "1px solid #fca5a5", background: "#fef2f2", color: "#dc2626", whiteSpace: "nowrap" }}
                                  >✕ Discard</button>
                                </div>
                              </div>
                              {/* Explanatory note */}
                              <div style={{ padding: "3px 14px 7px", fontSize: 10, color: "#94a3b8", lineHeight: 1.6, borderLeft: "3px solid #94a3b8", background: "#fafbfd" }}>
                                These classes had no edge path to any qualifying group.
                                Drag them into an appropriate layer or discard to leave them unassigned.
                              </div>
                            </div>
                          );
                        })()}

                        {/* ── Pinned "Uncategorized" row — always shown when orphan nodes exist ── */}
                        {uncategorizedCount > 0 && communityMapVersion > 0 && (() => {
                          const isInspecting = selectedComm === UNCATEGORIZED_COMM;
                          const flyToUncategorized = () => {
                            const cy = cyRef.current;
                            if (!cy) return;
                            const eles = cy.nodes("[!isLayer]").filter(n => mergedMapForPanel[n.id()] === UNCATEGORIZED_COMM);
                            if (eles.length) cy.animate({ fit: { eles, padding: 80 } }, { duration: 450, easing: "ease-in-out" });
                          };
                          return (
                            <div
                              key="uncategorized-row"
                              onClick={() => { flyToUncategorized(); setSelectedComm(isInspecting ? null : UNCATEGORIZED_COMM); }}
                              style={{
                                display: "flex", alignItems: "center",
                                padding: "6px 10px", cursor: "pointer", userSelect: "none",
                                background: isInspecting ? "#fff7ed" : "#fffbeb",
                                borderLeft: "3px solid #f59e0b",
                                borderBottom: "1px solid #fde68a",
                                marginBottom: 2,
                                gap: 6,
                                transition: "background 0.12s",
                              }}
                              onMouseEnter={e => { if (!isInspecting) e.currentTarget.style.background = "#fef3c7"; }}
                              onMouseLeave={e => { if (!isInspecting) e.currentTarget.style.background = "#fffbeb"; }}
                            >
                              {/* Warning icon */}
                              <span style={{ fontSize: 13, flexShrink: 0, lineHeight: 1 }}>⚠️</span>

                              {/* Label + subtitle */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: 11, fontWeight: 700,
                                  color: isInspecting ? "#92400e" : "#78350f",
                                  lineHeight: 1.2,
                                }}>
                                  Uncategorized
                                </div>
                                <div style={{
                                  fontSize: 10, fontWeight: 500,
                                  color: "#92400e",
                                  lineHeight: 1.3,
                                }}>
                                  Needs manual review
                                </div>
                              </div>

                              {/* Count badge */}
                              <span style={{
                                display: "inline-flex", alignItems: "center",
                                background: "#fef3c7", border: "1.5px solid #f59e0b",
                                borderRadius: 20, padding: "1px 7px",
                                fontSize: 11, fontWeight: 700, color: "#78350f",
                                flexShrink: 0, whiteSpace: "nowrap",
                              }}>
                                {uncategorizedCount}
                              </span>

                              {/* Inspect button */}
                              <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setSelectedComm(isInspecting ? null : UNCATEGORIZED_COMM);
                                    if (!isInspecting) flyToUncategorized();
                                  }}
                                  style={{
                                    fontSize: 10, fontWeight: 600, padding: "3px 8px",
                                    borderRadius: 5, cursor: "pointer",
                                    border: "1.5px solid #f59e0b",
                                    background: isInspecting ? "#f59e0b" : "#fff",
                                    color: isInspecting ? "#fff" : "#78350f",
                                    whiteSpace: "nowrap",
                                    transition: "background 0.1s, color 0.1s",
                                  }}
                                >
                                  {isInspecting ? "✓ Viewing" : "Inspect"}
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {mergedGroupEntries.map(([comm, nodes]) => {
                          const cNum = Number(comm);
                          const cColor = COMMUNITY_COLORS[cNum % COMMUNITY_COLORS.length];
                          const isInspecting = selectedComm === cNum;
                          const isHov = hoveredComm === cNum;
                          const isConverted = convertedCommNums.has(cNum);
                          const isSuggestedPending = suggestedCommNums.has(cNum); // promoted but not yet confirmed
                          const flyToComm = () => {
                            const cy = cyRef.current;
                            if (!cy) return;
                            const commEles = cy.nodes("[!isLayer]").filter(n => mergedMapForPanel[n.id()] === cNum);
                            if (commEles.length) cy.animate({ fit: { eles: commEles, padding: 80 } }, { duration: 450, easing: "ease-in-out" });
                          };
                          return (
                            <div
                              key={comm}
                              onClick={flyToComm}
                              onMouseEnter={() => setHoveredComm(cNum)}
                              onMouseLeave={() => setHoveredComm(null)}
                              style={{
                                display: "flex", alignItems: "center",
                                padding: "6px 10px", cursor: "pointer", userSelect: "none",
                                background: isSuggestedPending ? "#fffbeb" : isInspecting ? `${cColor}0e` : isHov ? "#f8fafc" : "transparent",
                                borderLeft: isSuggestedPending ? "3px solid #f59e0b" : isInspecting ? `3px solid ${cColor}` : "3px solid transparent",
                                opacity: isConverted && !isSuggestedPending ? 0.55 : 1,
                                transition: "background 0.1s, border-color 0.1s, opacity 0.2s",
                              }}
                            >
                              {/* Color dot */}
                              <span style={{
                                width: 7, height: 7, borderRadius: "50%", flexShrink: 0, marginRight: 8,
                                background: isSuggestedPending ? "#f59e0b" : isConverted ? "#10b981" : cColor,
                                boxShadow: isInspecting ? `0 0 0 3px ${cColor}25` : "none",
                              }} />

                              {/* Group name */}
                              <span style={{
                                flex: 1, fontSize: 13, letterSpacing: "-0.01em",
                                fontWeight: isSuggestedPending ? 600 : isInspecting ? 600 : 400,
                                color: isSuggestedPending ? "#92400e" : isConverted ? "#94a3b8" : isInspecting ? "#1e293b" : "#475569",
                              }}>
                                Group {comm}
                              </span>

                              {/* Node count */}
                              <span style={{ fontSize: 12, color: "#b4bfcc", marginRight: 6, flexShrink: 0 }}>
                                {nodes.length}
                              </span>

                              {/* Action area */}
                              {isSuggestedPending ? (
                                /* Pending suggestion — always show Remove */
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    const lid = layerNodes.find(l => {
                                      const m = l.id.match(/^suggestion-comm-(\d+)-\d+$/);
                                      return m && Number(m[1]) === cNum;
                                    })?.id;
                                    if (lid) rejectSuggestedLayer(lid);
                                  }}
                                  title="Remove this suggestion"
                                  style={{
                                    fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 5, cursor: "pointer",
                                    border: "1px solid #fca5a5", background: "#fef2f2", color: "#dc2626",
                                    whiteSpace: "nowrap", flexShrink: 0,
                                  }}
                                >
                                  ✕ Remove
                                </button>
                              ) : isConverted ? (
                                /* Confirmed — static checkmark */
                                <span style={{ fontSize: 10, color: "#10b981", fontWeight: 600, flexShrink: 0 }}>✓</span>
                              ) : (isHov || isInspecting) ? (
                                /* Hover / active: reveal Inspect + Layer actions */
                                <div
                                  style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <button
                                    onClick={e => { e.stopPropagation(); setSelectedComm(isInspecting ? null : cNum); if (!isInspecting) flyToComm(); }}
                                    style={{
                                      fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 5, cursor: "pointer",
                                      border: isInspecting ? "none" : "1px solid #e2e8f0",
                                      background: isInspecting ? `${cColor}18` : "#f1f5f9",
                                      color: isInspecting ? cColor : "#64748b",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {isInspecting ? "Viewing" : "Inspect"}
                                  </button>
                                  {versionStatus === 'draft' && (
                                    <button
                                      onClick={e => { e.stopPropagation(); setCreateLayerPrompt({ commNum: cNum, map: mergedMapForPanel, inputValue: '' }); }}
                                      title="Create a named layer from this group"
                                      style={{
                                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5,
                                        border: "1px solid #5eead4", background: "#f0fdfa", color: "#0f766e",
                                        cursor: "pointer", whiteSpace: "nowrap",
                                      }}
                                    >
                                      + Layer
                                    </button>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Min-size stepper + Generate clusters ── */}
                    <div style={{ borderTop: "1px solid #f1f5f9", padding: "7px 14px", display: "flex", flexDirection: "column", gap: 7, flexShrink: 0, background: "#fafbfc" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 10, color: "#94a3b8", flex: 1, letterSpacing: "0.02em", fontWeight: 500 }}>Min size</span>
                        <div style={{ display: "flex", alignItems: "center", background: "#f1f5f9", borderRadius: 6, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                          <button
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => {
                              setSelectedComm(null);
                              const v = Math.max(1, panelMinNodes - 1);
                              setPanelMinNodes(v);
                              setMinSizeInput(String(v));
                            }}
                            style={{ border: "none", background: "transparent", padding: "3px 9px", fontSize: 14, color: "#64748b", cursor: "pointer", lineHeight: 1, fontWeight: 500 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >−</button>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={minSizeInput}
                            onChange={e => {
                              const raw = e.target.value.replace(/[^0-9]/g, '');
                              setMinSizeInput(raw);
                              const n = parseInt(raw, 10);
                              if (!isNaN(n) && n >= 1) { setSelectedComm(null); setPanelMinNodes(n); }
                            }}
                            onBlur={() => {
                              const n = parseInt(minSizeInput, 10);
                              const clamped = isNaN(n) || n < 1 ? 1 : n;
                              setPanelMinNodes(clamped);
                              setMinSizeInput(String(clamped));
                            }}
                            onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') e.target.blur(); }}
                            onMouseDown={e => e.stopPropagation()}
                            style={{ fontSize: 11, fontWeight: 700, color: "#334155", padding: "0 4px", width: 36, textAlign: "center", borderTop: "none", borderBottom: "none", borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0", outline: "none", background: "transparent" }}
                          />
                          <button
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => {
                              setSelectedComm(null);
                              const v = panelMinNodes + 1;
                              setPanelMinNodes(v);
                              setMinSizeInput(String(v));
                            }}
                            style={{ border: "none", background: "transparent", padding: "3px 9px", fontSize: 14, color: "#64748b", cursor: "pointer", lineHeight: 1, fontWeight: 500 }}
                            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >+</button>
                        </div>
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>classes</span>
                      </div>
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={handleGenerateClusters}
                        style={{
                          width: "100%", padding: "6px 0",
                          background: "#0d9488", color: "#fff",
                          border: "none", borderRadius: 6,
                          fontSize: 11, fontWeight: 600, cursor: "pointer",
                          letterSpacing: "0.01em",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#0f766e'}
                        onMouseLeave={e => e.currentTarget.style.background = '#0d9488'}
                      >Generate clusters</button>
                    </div>

                    {/* ── Footer: stage-aware actions + always-visible Continue CTA ── */}
                    {versionStatus === 'draft' && communityMapVersion > 0 && (() => {
                      const confirmedCount = layerNodes.filter(l => !suggestedLayerIds.has(l.id)).length;
                      const pendingCount = suggestedLayerIds.size;
                      const isReadyToMove = confirmedCount > 0;

                      return (
                        <div style={{ borderTop: "1px solid #f1f5f9", padding: "12px 14px", flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>

                          {/* ── Sub-state: no layers yet → Suggest all shortcut; else progress summary ── */}
                          {layerNodes.length === 0 && pendingCount === 0 ? (
                            mergedGroupEntries.length > 0 && (
                              <button
                                onClick={() => suggestLayersFromCommunities(panelMinNodes)}
                                style={{
                                  width: "100%", padding: "8px 0",
                                  background: "#0d9488", color: "#fff",
                                  border: "none", borderRadius: 7,
                                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                                }}
                              >Suggest all as components</button>
                            )
                          ) : (
                            <>
                              {/* ── Progress summary ── */}
                              <div style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                padding: "8px 10px",
                                background: confirmedCount > 0 ? "#f0fdf4" : "#f8fafc",
                                border: `1px solid ${confirmedCount > 0 ? "#bbf7d0" : "#e2e8f0"}`,
                                borderRadius: 8,
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                  <span style={{ fontSize: 15 }}>{confirmedCount > 0 ? "🏗" : "⏳"}</span>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: confirmedCount > 0 ? "#065f46" : "#475569" }}>
                                      {confirmedCount > 0
                                        ? `${confirmedCount} component${confirmedCount !== 1 ? "s" : ""} confirmed`
                                        : "No components confirmed yet"}
                                    </div>
                                    {pendingCount > 0 && (
                                      <div style={{ fontSize: 10, color: "#92400e", marginTop: 1 }}>
                                        {pendingCount} suggestion{pendingCount !== 1 ? "s" : ""} still pending
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {confirmedCount > 0 && (
                                  <span style={{
                                    background: "#10b981", color: "#fff", borderRadius: "50%",
                                    width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                                  }}>✓</span>
                                )}
                              </div>

                              {/* ── Pending suggestions: bulk actions ── */}
                              {pendingCount > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  <div style={{ fontSize: 10, color: "#64748b", fontWeight: 500, textAlign: "center" }}>
                                    Resolve remaining suggestions
                                  </div>
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                      onClick={() => [...suggestedLayerIds].forEach(id => confirmSuggestedLayer(id))}
                                      style={{ flex: 1, padding: "6px 0", background: "#f0fdfa", color: "#0f766e", border: "1px solid #5eead4", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                    >✓ Confirm all</button>
                                    <button
                                      onClick={() => [...suggestedLayerIds].forEach(id => rejectSuggestedLayer(id))}
                                      style={{ flex: 1, padding: "6px 0", background: "#fef2f2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: "pointer" }}
                                    >✕ Discard all</button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* ── Divider ── */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                            <span style={{ fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap" }}>
                              or skip for now
                            </span>
                            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                          </div>

                          {/* ── Primary CTA — always visible once clusters have been generated ── */}
                          <button
                            onClick={() => {
                              if (pendingCount > 0) [...suggestedLayerIds].forEach(id => rejectSuggestedLayer(id));
                              setSuggestStageLocked(false);
                              setArrangeUnlocked(true);
                            }}
                            style={{
                              width: "100%", padding: "10px 0",
                              background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
                              color: "#fff", border: "none", borderRadius: 8,
                              fontSize: 13, fontWeight: 700, cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                              boxShadow: "0 3px 10px rgba(13,148,136,0.4)",
                              letterSpacing: "0.01em",
                              transition: "background 0.2s, box-shadow 0.2s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = "0.92"; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                          >
                            <span>
                              {pendingCount > 0
                                ? `Discard ${pendingCount} pending & continue`
                                : "Continue to Arrange & Define"}
                            </span>
                            <span style={{ fontSize: 16, lineHeight: 1 }}>→</span>
                          </button>

                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* ── Connections side-panel — floats to the LEFT of the group panel ── */}
              {selectedNodeInPanel && nodeConnections && (nodeConnections.outgoing.length > 0 || nodeConnections.incoming.length > 0) && (() => {
                const connColor = (selectedComm === UNCATEGORIZED_COMM || selectedComm === null)
                  ? "#94a3b8"
                  : COMMUNITY_COLORS[selectedComm % COMMUNITY_COLORS.length];
                const selEntry = (selectedComm === UNCATEGORIZED_COMM
                  ? rawNodes.filter(n => mergedMapForPanel[n.id ?? n.key ?? n.name ?? ""] === UNCATEGORIZED_COMM)
                  : (mergedGroupEntries.find(([c]) => Number(c) === selectedComm)?.[1] ?? [])
                ).find(n => (n.id ?? n.key ?? n.name ?? "") === selectedNodeInPanel);
                const selShort = selEntry
                  ? (selEntry.label ?? selEntry.name ?? selectedNodeInPanel).split('.').pop()
                  : (selectedNodeInPanel?.split('.')?.pop() ?? selectedNodeInPanel);
                const panelPos = { top: 12, right: 288 };
                return (
                  <div
                    style={{
                      position: "absolute", pointerEvents: "all",
                      ...panelPos,
                      width: 224, maxHeight: 500,
                      background: "#fff",
                      border: `1.5px solid ${connColor}44`,
                      borderRadius: 14,
                      boxShadow: "0 4px 24px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.04)",
                      display: "flex", flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "11px 14px 10px",
                      borderBottom: "1px solid #f1f5f9",
                      flexShrink: 0,
                    }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: connColor, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {selShort}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>
                        Connections
                      </span>
                      <button
                        onClick={() => { setSelectedNodeInPanel(null); setHighlightEdgesActive(false); setShowIntraGroupEdges(false); }}
                        title="Close"
                        style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                      >×</button>
                    </div>
                    {/* Scrollable body */}
                    <div style={{ overflowY: "auto", flex: 1, padding: "8px 12px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {nodeConnections.outgoing.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                            Calls → <span style={{ fontWeight: 400 }}>({nodeConnections.outgoing.length})</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {nodeConnections.outgoing.map(conn => (
                              <div
                                key={conn.id}
                                className="conn-row"
                                onClick={e => {
                                  e.stopPropagation();
                                  const cy = cyRef.current; if (!cy) return;
                                  const n = cy.$(`#${CSS.escape(conn.id)}`);
                                  if (n.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                }}
                                style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", padding: "3px 4px", borderRadius: 4, position: "relative" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: conn.isInGroup ? connColor : "#94a3b8" }} />
                                <span style={{ flex: 1, fontSize: 12, color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word" }}>{conn.shortName}</span>
                                {!conn.isInGroup && conn.groupNum !== undefined && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#f1f5f9", color: "#64748b", whiteSpace: "nowrap" }}>
                                    Grp {conn.groupNum}
                                  </span>
                                )}
                                <button
                                  className="inspect-btn"
                                  title="Inspect this node"
                                  onClick={e => {
                                    e.stopPropagation();
                                    const cy = cyRef.current;
                                    const n = cy?.$(`#${CSS.escape(conn.id)}`);
                                    if (n?.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                    setSelectedNodeInPanel(conn.id);
                                    setHighlightEdgesActive(true);
                                  }}
                                  style={{ opacity: 0.55, border: "none", background: "none", cursor: "pointer", padding: "2px 4px", borderRadius: 3, fontSize: 11, color: "#0d9488", fontWeight: 700, flexShrink: 0, lineHeight: 1 }}
                                >⊙</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {nodeConnections.incoming.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                            ← Called by <span style={{ fontWeight: 400 }}>({nodeConnections.incoming.length})</span>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {nodeConnections.incoming.map(conn => (
                              <div
                                key={conn.id}
                                onClick={e => {
                                  e.stopPropagation();
                                  const cy = cyRef.current; if (!cy) return;
                                  const n = cy.$(`#${CSS.escape(conn.id)}`);
                                  if (n.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                }}
                                style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", padding: "3px 4px", borderRadius: 4, position: "relative" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: conn.isInGroup ? connColor : "#94a3b8" }} />
                                <span style={{ flex: 1, fontSize: 12, color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word" }}>{conn.shortName}</span>
                                {!conn.isInGroup && conn.groupNum !== undefined && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#f1f5f9", color: "#64748b", whiteSpace: "nowrap" }}>
                                    Grp {conn.groupNum}
                                  </span>
                                )}
                                <button
                                  className="inspect-btn"
                                  title="Inspect this node"
                                  onClick={e => {
                                    e.stopPropagation();
                                    const cy = cyRef.current;
                                    const n = cy?.$(`#${CSS.escape(conn.id)}`);
                                    if (n?.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                    setSelectedNodeInPanel(conn.id);
                                    setHighlightEdgesActive(true);
                                  }}
                                  style={{ opacity: 0.55, border: "none", background: "none", cursor: "pointer", padding: "2px 4px", borderRadius: 3, fontSize: 11, color: "#0d9488", fontWeight: 700, flexShrink: 0, lineHeight: 1 }}
                                >⊙</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}

          {/* ── Cross-group connections panel ── */}
          {showCrossGroupEdges && crossGroupData && selectedComm !== null && (() => {
            const grpColor = selectedComm === UNCATEGORIZED_COMM
              ? "#94a3b8"
              : COMMUNITY_COLORS[selectedComm % COMMUNITY_COLORS.length];
            const hasConnPanel = selectedNodeInPanel && nodeConnections &&
              (nodeConnections.outgoing.length > 0 || nodeConnections.incoming.length > 0);
            const panelWidth = 264;
            const panelPos = { bottom: 12, right: 288 + (hasConnPanel ? 232 : 0) };
            const sortedGroups = [...crossGroupData.entries()].sort((a, b) => b[1].length - a[1].length);
            return (
              <div
                style={{
                  position: "absolute", pointerEvents: "all",
                  ...panelPos,
                  width: panelWidth, maxHeight: 520,
                  background: "#fff",
                  border: `1.5px solid ${grpColor}44`,
                  borderRadius: 14,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.09), 0 1px 3px rgba(0,0,0,0.04)",
                  display: "flex", flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <div style={{ padding: "11px 14px 9px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: grpColor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1e293b", flex: 1 }}>
                      {selectedComm === UNCATEGORIZED_COMM ? "Uncategorized" : `Group ${selectedComm}`}
                    </span>
                    <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>cross-group</span>
                    <button
                      onClick={() => { setShowCrossGroupEdges(false); }}
                      title="Close"
                      style={{ border: "none", background: "none", cursor: "pointer", color: "#fca5a5", padding: "2px 4px", fontSize: 15, lineHeight: 1, flexShrink: 0, borderRadius: 4 }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fef2f2"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = "#fca5a5"; e.currentTarget.style.background = "none"; }}
                    >×</button>
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
                    {sortedGroups.length} connected group{sortedGroups.length !== 1 ? "s" : ""} · {[...crossGroupData.values()].reduce((s, v) => s + v.length, 0)} edges
                  </div>
                </div>
                {/* Scrollable group list */}
                <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
                  {sortedGroups.map(([extGrp, conns]) => {
                    const extIsUncat = Number(extGrp) === UNCATEGORIZED_COMM;
                    const extColor = extIsUncat ? "#94a3b8" : COMMUNITY_COLORS[extGrp % COMMUNITY_COLORS.length];
                    const extLabel = extIsUncat ? "Uncategorized" : `Group ${extGrp}`;
                    const isExpanded = crossGroupExpandedGrp === extGrp;
                    const outConns = conns.filter(c => c.direction === 'out');
                    const inConns = conns.filter(c => c.direction === 'in');
                    return (
                      <div key={extGrp}>
                        {/* Group row */}
                        <div
                          onClick={() => setCrossGroupExpandedGrp(isExpanded ? null : extGrp)}
                          style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 14px", cursor: "pointer", userSelect: "none" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: extColor, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", flex: 1 }}>{extLabel}</span>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 10, background: `${extColor}22`, color: extColor }}>
                            {conns.length}
                          </span>
                          <span style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1 }}>{isExpanded ? "▲" : "▼"}</span>
                        </div>
                        {/* Expanded connection pairs */}
                        {isExpanded && (
                          <div style={{ padding: "2px 12px 8px 28px", display: "flex", flexDirection: "column", gap: 2 }}>
                            {outConns.length > 0 && (
                              <>
                                <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 4, marginBottom: 2 }}>
                                  Calls → <span style={{ fontWeight: 400 }}>({outConns.length})</span>
                                </div>
                                {outConns.map((c, i) => (
                                  <div
                                    key={i}
                                    onClick={e => {
                                      e.stopPropagation();
                                      const cy = cyRef.current;
                                      const n = cy?.$(`#${CSS.escape(c.externalId)}`);
                                      if (n?.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                    }}
                                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 4px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                  >
                                    <span style={{ color: "#475569", flex: "0 0 auto", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.internalName}</span>
                                    <span style={{ color: "#0d9488", fontWeight: 700, flexShrink: 0 }}>→</span>
                                    <span style={{ color: extColor, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.externalName}</span>
                                  </div>
                                ))}
                              </>
                            )}
                            {inConns.length > 0 && (
                              <>
                                <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: outConns.length > 0 ? 6 : 4, marginBottom: 2 }}>
                                  ← Called by <span style={{ fontWeight: 400 }}>({inConns.length})</span>
                                </div>
                                {inConns.map((c, i) => (
                                  <div
                                    key={i}
                                    onClick={e => {
                                      e.stopPropagation();
                                      const cy = cyRef.current;
                                      const n = cy?.$(`#${CSS.escape(c.externalId)}`);
                                      if (n?.length) { cy.animate({ zoom: Math.min(Math.max(cy.zoom(), 1.2), 2.5), center: { eles: n } }, { duration: 350 }); setTimeout(() => { if (!n.removed()) { n.style({ 'border-width': 5, 'border-color': '#f59e0b', 'border-opacity': 1 }); setTimeout(() => { if (!n.removed()) n.removeStyle('border-width border-color border-opacity'); }, 1400); } }, 380); }
                                    }}
                                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 4px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                  >
                                    <span style={{ color: extColor, fontWeight: 600, flex: "0 0 auto", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.externalName}</span>
                                    <span style={{ color: "#0d9488", fontWeight: 700, flexShrink: 0 }}>→</span>
                                    <span style={{ color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.internalName}</span>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── node-inside-layer popup — draft only (read-only versions zoom on tap, no popup) ── */}
          {versionStatus === 'draft' && nodePopup && (
            <div
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              style={{ position: "absolute", left: nodePopup.pos.x + 14, top: nodePopup.pos.y - 16, zIndex: 400, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "8px 10px", minWidth: 170 }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>
                {nodePopup.label}
              </div>
              <button
                onClick={moveNodeOut}
                style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "5px 8px", fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 5, background: "#f8fafc", color: "#334155", cursor: "pointer", whiteSpace: "nowrap" }}
              >
                ↗ Move out of component
              </button>
            </div>
          )}


          {/* ── Name-before-create layer prompt ── */}
          {createLayerPrompt && (
            <div
              style={{ position: "absolute", inset: 0, zIndex: 700, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={() => setCreateLayerPrompt(null)}
              onMouseDown={e => e.stopPropagation()}
            >
              <div
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", padding: "24px 28px", minWidth: 320, maxWidth: 420 }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>
                  Name this component
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 16, lineHeight: 1.5 }}>
                  Give the component a meaningful name so teammates understand its responsibility — e.g. <em>"API Gateway"</em>, <em>"Data Access"</em>, <em>"Auth Service"</em>.
                </div>

                <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>
                  Component name
                </label>
                <input
                  autoFocus
                  type="text"
                  value={createLayerPrompt.inputValue}
                  onChange={e => setCreateLayerPrompt(p => ({ ...p, inputValue: e.target.value }))}
                  onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter' && createLayerPrompt.inputValue.trim()) {
                      const lid = createLayerFromCommunity(createLayerPrompt.commNum, createLayerPrompt.map, createLayerPrompt.inputValue);
                      if (lid) confirmSuggestedLayer(lid);
                      if (createLayerPrompt.closeInspect) setSelectedComm(null);
                      setCreateLayerPrompt(null);
                    }
                    if (e.key === 'Escape') setCreateLayerPrompt(null);
                  }}
                  placeholder="e.g. API Gateway, Data Access, Auth Service…"
                  style={{
                    width: "100%", padding: "9px 12px", fontSize: 13,
                    border: "1.5px solid #e2e8f0", borderRadius: 8,
                    outline: "none", color: "#1e293b", boxSizing: "border-box",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#0d9488'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                />

                <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setCreateLayerPrompt(null)}
                    style={{ padding: "8px 18px", fontSize: 13, fontWeight: 500, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", color: "#475569", cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={!createLayerPrompt.inputValue.trim()}
                    onClick={() => {
                      const lid = createLayerFromCommunity(createLayerPrompt.commNum, createLayerPrompt.map, createLayerPrompt.inputValue);
                      if (lid) confirmSuggestedLayer(lid);
                      if (createLayerPrompt.closeInspect) setSelectedComm(null);
                      setCreateLayerPrompt(null);
                    }}
                    style={{
                      padding: "8px 18px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 8, cursor: createLayerPrompt.inputValue.trim() ? "pointer" : "not-allowed",
                      background: createLayerPrompt.inputValue.trim() ? "#0d9488" : "#94a3b8", color: "#fff",
                      transition: "background 0.15s",
                    }}
                  >
                    Create component →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── connection rule modal ── */}
          {pendingConnection && (
            <div style={{ position: "absolute", inset: 0, zIndex: 700, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", padding: "24px 28px", minWidth: 340, maxWidth: 440 }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
                  Define dependency rule
                </div>

                {/* Direction header */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 13, padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
                  <span style={{ fontWeight: 700, color: pendingConnection.sourceColor }}>{pendingConnection.sourceName}</span>
                  <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 15 }}>→</span>
                  <span style={{ fontWeight: 700, color: pendingConnection.targetColor }}>{pendingConnection.targetName}</span>
                </div>

                {/* Rule name */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>
                    Rule name <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional)</span>
                  </label>
                  <input
                    autoFocus
                    type="text"
                    value={ruleFormName}
                    onChange={e => setRuleFormName(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    placeholder={`e.g. ${pendingConnection.sourceName} may use ${pendingConnection.targetName}`}
                    style={{ width: "100%", padding: "7px 10px", fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 7, outline: "none", color: "#1e293b", boxSizing: "border-box" }}
                  />
                </div>

                {/* Enforcement type — one card per RULE_TYPE_KEYS entry, single row */}
                <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>
                  Enforcement type
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                  {RULE_TYPE_KEYS.map(k => {
                    const rt = RULE_TYPES[k];
                    const selected = ruleFormEnforcement === k;
                    return (
                      <button
                        key={k}
                        onClick={() => {
                          setRuleFormEnforcement(k);
                          setRuleFormName(prev => {
                            const isAuto = RULE_TYPE_KEYS.some(
                              rk => prev === `${pendingConnection.sourceName} ${RULE_TYPES[rk].autoVerb} ${pendingConnection.targetName}`
                            );
                            return isAuto
                              ? `${pendingConnection.sourceName} ${RULE_TYPES[k].autoVerb} ${pendingConnection.targetName}`
                              : prev;
                          });
                        }}
                        style={{
                          padding: "10px 11px", fontSize: 12, fontWeight: 600,
                          border: selected ? `2px solid ${rt.color}` : `1.5px solid ${rt.chipBorder}60`,
                          borderRadius: 9, background: selected ? rt.chipBg : "#fafbfc",
                          color: rt.chipColor, cursor: "pointer",
                          textAlign: "left", display: "flex", flexDirection: "column", gap: 3,
                          boxShadow: selected ? `0 0 0 3px ${rt.color}18` : "none",
                          transition: "all 0.12s",
                          position: "relative",
                        }}
                      >
                        {selected && (
                          <span style={{
                            position: "absolute", top: 6, right: 7,
                            width: 14, height: 14, borderRadius: "50%",
                            background: rt.color, color: "#fff",
                            fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                          }}>✓</span>
                        )}
                        <div style={{ fontSize: 13 }}>{rt.icon} {rt.label}</div>
                        <div style={{ fontSize: 10, fontWeight: 400, color: rt.chipColor, opacity: 0.75, lineHeight: 1.4 }}>{rt.desc}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Hint for selected type */}
                <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 12, padding: "6px 10px", background: "#f8fafc", borderRadius: 6, lineHeight: 1.5 }}>
                  {RULE_TYPES[ruleFormEnforcement]?.hint}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button
                    onClick={() => { setPendingConnection(null); setRuleFormName(''); setRuleFormEnforcement('denied'); }}
                    style={{ flex: 1, padding: "8px 0", fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 7, background: "#f8fafc", color: "#64748b", cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      confirmConnection({ enforcement: ruleFormEnforcement, name: ruleFormName.trim() });
                      setRuleFormName(''); setRuleFormEnforcement('denied');
                    }}
                    style={{
                      flex: 2, padding: "8px 0", fontSize: 12, fontWeight: 700,
                      border: "none", borderRadius: 7, cursor: "pointer",
                      background: RULE_TYPES[ruleFormEnforcement]?.color ?? '#0d9488',
                      color: "#fff", transition: "opacity 0.12s",
                    }}
                  >
                    {RULE_TYPES[ruleFormEnforcement]?.icon} Create {RULE_TYPES[ruleFormEnforcement]?.label} Rule
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* tooltip rendered via cytoscape-popper into document.body — no JSX needed */}
        </div>
      )}

      {/* ── pulse keyframe for connect-mode source highlight ── */}
      <style>{`
        @keyframes layer-pulse {
          0%,100% { box-shadow: 0 0 0 4px rgba(124,58,237,0.20), 0 0 14px 2px rgba(124,58,237,0.25); }
          50%      { box-shadow: 0 0 0 8px rgba(124,58,237,0.08), 0 0 22px 6px rgba(124,58,237,0.40); }
        }
        @keyframes connect-alert-fade {
          0%   { opacity: 0; transform: translateY(6px); }
          12%  { opacity: 1; transform: translateY(0);   }
          75%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes fadeInUp {
          0%   { opacity: 0; transform: translateX(-50%) translateY(10px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0);    }
        }
        @keyframes fadeInDown {
          0%   { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0);     }
        }
      `}</style>
    </div>
  );
}
